import type {
  LoginResponse,
  CycleParametersUpdatePayload,
  CycleCoherence,
  CycleCoherenceCheckPayload,
  InstructorCycleParametersResponse,
  CycleSuggestionResponse,
  CycleSuggestionMutationResponse,
  CycleWeeksResponse,
  CycleReportResponse,
  MyCycleParametersResponse,
  InstructorReadinessProjectionResponse,
  InstructorFarewellResponse,
  InstructorLateArrivalResponse,
  LateArrivalChoicePayload,
  LateArrivalChoiceResponse,
  InstructorMemoryComprehensionResponse,
  InstructorStatisticalGuideResponse,
  InstructorPrevalenceMatrixResponse,
  InstitutionPortfolioResponse,
  InstructorReliabilityHeaderResponse,
  ReliabilityHeaderDays,
  FarewellDisagreementPayload,
  FarewellDisagreementResponse,
  TedPortfolio,
  ConductsResponse,
  ConductResponse,
  ConductCreatePayload,
  ExamResultsResponse,
  ExamResultResponse,
  ExamResultCreatePayload,
  ExamResultFields,
  InstructorTimelineResponse,
  CommitmentsResponse,
  InstructorCommitmentsResponse,
  CommitmentResponse,
  CommitmentCreatePayload,
  CommitmentClosePayload,
  CommitmentStudentPayload,
  DayLine,
  PortfolioSignals,
  AgendaResponse,
  CalendarFeedResponse,
  MeetingsResponse,
  MeetingResponse,
  MeetingTranscriptResponse,
  MeetingSchedulePayload,
  MeetingBriefingResponse,
  MentorGuideStatusResponse,
  MentorGuideAskResponse,
  BriefingSectionKey,
  MaterialLibraryResponse,
  MaterialSharesResponse,
  MaterialShareResponse,
  MaterialShareCreatePayload,
  MeetingMinutesResponse,
  MeetingMinutesUpdatePayload,
  MeetingStudentMessagesResponse,
  MainExam,
  MainExamUpdatePayload,
  WhatsappOptIn,
  WhatsappOptInUpdatePayload,
  VerifyOtpResponse,
  RefreshResponse,
  MeResponse,
  DashboardMetrics,
  DashboardKpis,
  ExamTrackScores,
  StudyStats,
  RecentActivity,
  ProgressBenchmark,
  Quiz,
  QuizQuestion,
  QuizResult,
  CalendarEvent,
  CreateEventData,
  Goal,
  GoalProgress,
  IntegrationPlatform,
  MedwayTrail,
  MedwayTrailDetail,
  MedwayQuestion,
  MedwayTrailStats,
  MedcofEstudo,
  MedcofEstudosStats,
  MedcofRevisao,
  MedcofRevisoesStats,
  MedcofRecommendedRevision,
  MedcofRecommendedStats,
  MedcofSimulado,
  MedcofSimuladosStats,
  OnboardingResponse,
  SaveOnboardingRequest,
  CompleteOnboardingResponse,
  QuizAttemptResult,
  QuizQuestionResult,
  MetricsAnalysis,
  StatisticalGuide,
  TopicRevision,
  TrackMetricsResponse,
  TrackDetailResponse,
  TracksResponse,
  ExamMetricsResponse,
  ExamComparisonResponse,
  StudyActivityResponse,
  ProgressAnalysisResponse,
  TedWeekPlan,
  SetTedGoalResponse,
  LegalDocumentsResponse,
  LegalAcceptancesResponse,
  LegalAcceptanceItem,
  LegalAcceptanceMethod,
  CreateLegalAcceptancesResponse,
  RevokeLegalAcceptanceResponse,
  AdminLegalDocumentsIndexResponse,
  AdminLegalDocumentVersionsResponse,
  AdminLegalDocumentResponse,
  AdminLegalDocumentDraftPatch,
  LegalDocumentKind,
  LegalDocumentStatus,
  CheckoutQuoteResponse,
  CheckoutProductResponse,
  CheckoutOrderRequest,
  CheckoutOrderResponse,
  IdempotentRequestOptions,
  InstructorQualityMirrorResponse,
  QualityMirrorDays,
  MentorNpsResponse,
  MentorNpsSubmitPayload,
  MyMentorResponse,
  InstructorAdministrativeResponse,
  MentorDocumentsResponse,
  MentorDocumentResponse,
  MentorDocumentKind,
  AdminMentorCoveragesResponse,
  AdminMentorCoverageResponse,
  AdminMentorCoveragePayload,
  ExamDeadlineQueueResponse,
  ExamDeadlineItemResponse,
  AdminExamDeadlinesResponse,
  AdminExamDeadlineResponse,
  AdminExamDeadlinePayload,
  AdminApiKeysResponse,
  WebhookEventsResponse,
  ExternalRoutesResponse,
  AdminApiKeyResponse,
  AdminApiKeyCreatedResponse,
  AdminApiKeyPayload,
  AdminAssistantSettingsResponse,
  AdminAssistantSettingsPayload,
  AdminAssistantInstructorResponse,
  ApprovalCenterResponse,
  ExamCalendarResponse,
  ExamProcessesResponse,
  ExamProcessSnapshot,
  ExamRadarEntriesResponse,
  ExamRadarEntryResponse,
  ExamRadarOptionsResponse,
  ExamRadarTargetType,
  AdminExamProcessListParams,
  AdminExamProcessesResponse,
  AdminExamProcessResponse,
  AdminExamProcessSavedResponse,
  AdminExamProcessRevisionListParams,
  AdminExamProcessRevisionsResponse,
  AdminExamProcessRevisionResponse,
  AssistantMessage,
  AssistantStreamEvent,
  AssistantStreamOptions,
} from '@/types/api'
import type { User } from '@/types/user'
// Envelopes da jornada: declarados uma unica vez em types/journey.ts. Antes eram
// redeclarados inline aqui, o que criava dois espelhos manuais do mesmo contrato.
import type { JourneyPlanResponse } from '@/types/journey'

import { getToken as getInMemoryToken } from '@/lib/tokenHolder'
import { fetchWithAuth } from '@/lib/authSession'
// Gate de assinatura (spec 10, R4): o 402 do backend vira um sinal global, sem
// passar pelo authStore — que importa este arquivo e criaria ciclo.
import {
  markSubscriptionBlocked,
  SUBSCRIPTION_REQUIRED_CODE,
  SUBSCRIPTION_REQUIRED_STATUS,
} from '@/lib/subscriptionBlock'
import { IDEMPOTENCY_HEADER } from '@/lib/idempotencyKey'
import { createSseParser } from '@/lib/sse'

/**
 * Id de recurso que so e interpolado na URL.
 *
 * Aceita string porque metade das chamadas vem de `useParams()` (sempre string) e a
 * outra metade de dados ja carregados (number). O Rails le os dois do mesmo jeito —
 * converter no cliente so criaria NaN quando o id e um hash, como em `exam_attempts`.
 */
export type ResourceId = number | string

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/v1`

// Get token - first from in-memory holder (most current), then localStorage (for page refreshes)
function getAuthToken(): string | null {
  // First try the in-memory token (immediately updated after login)
  const inMemoryToken = getInMemoryToken()
  if (inMemoryToken) return inMemoryToken

  // Fallback to localStorage (for page refreshes when store is rehydrating)
  try {
    const stored = localStorage.getItem('medhunter-auth')
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed.state?.token || null
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

/**
 * Header `Idempotency-Key` dos creates do Manual (r6-18). Sem chave, nenhum header — o
 * `request` faz merge de `options.headers` por cima dos padroes.
 */
function idempotencyHeaders(
  options?: import('@/types/api').IdempotentRequestOptions,
): Record<string, string> | undefined {
  return options?.idempotencyKey ? { [IDEMPOTENCY_HEADER]: options.idempotencyKey } : undefined
}

/**
 * Exportada no fim do arquivo. E ela que permite a uma tela distinguir 401
 * (sessao expirada) de 500 (falha temporaria): sem o `status` os dois viram a
 * mesma mensagem generica e o botao "tentar de novo" repete uma chamada que
 * nunca vai passar.
 */
class ApiError extends Error {
  status: number
  code?: string
  /**
   * Corpo bruto da resposta de erro. Alguns endpoints mandam detalhe por campo alem do
   * `error` (ex.: `invalid_questions` no 422 de `POST /onboarding/complete`); sem isto o
   * cliente perderia a unica pista de QUAL campo o servidor recusou.
   */
  details?: unknown

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

/**
 * Monta o corpo multipart de um video de acompanhamento.
 *
 * Campos `undefined` sao omitidos (em PATCH, omitir e diferente de mandar
 * vazio); `null` vira string vazia, que e como se limpa uma descricao. O arquivo
 * so entra quando ha um novo — no PATCH sem arquivo, so os metadados mudam.
 */
function buildFollowupVideoForm(
  data: Partial<import('@/types/api').AdminFollowupVideoPayload>,
  file?: File | null,
): FormData {
  const form = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return
    form.append(`followup_video[${key}]`, value === null ? '' : String(value))
  })

  if (file) form.append('file', file)

  return form
}

/**
 * Monta o corpo multipart de um curso de videoaulas (spec r4/12). A thumb vai em
 * `thumbnail`; os campos em `video_course[...]`. `stripe_plan_ids` vai um a um
 * (`video_course[stripe_plan_ids][]`) — e uma lista VAZIA ainda manda a chave, com
 * valor vazio, porque omitir e "nao mexer" e mandar vazio e "todos os alunos".
 */
function buildVideoCourseForm(
  data: Partial<import('@/types/api').AdminVideoCoursePayload>,
  thumbnail?: File | null,
): FormData {
  const form = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return
    if (key === 'stripe_plan_ids') {
      const ids = value as number[]
      if (ids.length === 0) form.append('video_course[stripe_plan_ids][]', '')
      ids.forEach((id) => form.append('video_course[stripe_plan_ids][]', String(id)))
      return
    }
    form.append(`video_course[${key}]`, value === null ? '' : String(value))
  })

  if (thumbnail) form.append('thumbnail', thumbnail)

  return form
}

/** Query string das listagens do Manual: so o que foi informado vai para a URL. */
function manualListQuery(params: import('@/types/api').ManualListParams): string {
  const search = new URLSearchParams()
  if (params.page) search.set('page', String(params.page))
  if (params.per_page) search.set('per_page', String(params.per_page))
  if (params.speciality) search.set('speciality', params.speciality)
  if (params.q?.trim()) search.set('q', params.q.trim())
  if (params.kind) search.set('kind', params.kind)
  const query = search.toString()
  return query ? `?${query}` : ''
}

const ASSISTANT_EVENT_TYPES: ReadonlySet<string> = new Set(['status', 'delta', 'done', 'error'])

/**
 * IA-08: `data` de um evento SSE do assistente -> evento tipado. JSON invalido ou `type`
 * desconhecido vira null e e ignorado, para um evento novo do backend nao quebrar a tela.
 */
function parseAssistantEvent(data: string): AssistantStreamEvent | null {
  try {
    const parsed: unknown = JSON.parse(data)
    if (typeof parsed !== 'object' || parsed === null) return null
    const type = (parsed as { type?: unknown }).type
    if (typeof type !== 'string' || !ASSISTANT_EVENT_TYPES.has(type)) return null
    return parsed as AssistantStreamEvent
  } catch {
    return null
  }
}

// R08-19: `?institution=` das leituras por instituicao do padrinho; vazio = sem parametro.
function institutionQuery(institution?: string): string {
  return institution ? `?${new URLSearchParams({ institution }).toString()}` : ''
}

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`

    // 401 renova o access token e refaz a chamada uma vez (lib/authSession.ts).
    const response = await fetchWithAuth(endpoint, url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
    }, getAuthToken)

    // Check content type before parsing
    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      console.error(`API returned non-JSON response for ${url}:`, text.substring(0, 200))
      throw new ApiError(
        `Servidor retornou resposta invalida (${contentType || 'unknown'})`,
        response.status
      )
    }

    const data = await response.json()

    if (!response.ok || data.success === false) {
      // 402 + code === 'subscription_required': logado, sem plano ativo. NAO e
      // sessao invalida — o token continua valido e nada e limpo aqui. A tela
      // reage pelo sinal global; ver lib/subscriptionBlock.ts.
      if (
        response.status === SUBSCRIPTION_REQUIRED_STATUS &&
        data.code === SUBSCRIPTION_REQUIRED_CODE
      ) {
        markSubscriptionBlocked()
      }

      throw new ApiError(
        data.error || 'Erro ao processar requisicao',
        response.status,
        // O `code` do backend passa a chegar na tela: sem ele o 402 do gate e
        // indistinguivel de qualquer outro erro e vira "Tente novamente".
        typeof data.code === 'string' ? data.code : undefined,
        data
      )
    }

    return data
  }

  /**
   * Variante de `request` para upload de arquivo.
   *
   * Difere em um ponto so, mas que e o ponto: NAO define `Content-Type`. Com
   * FormData quem precisa escrever o header e o proprio navegador, porque ele
   * inclui o `boundary` — defini-lo a mao produz um corpo que o Rack nao
   * consegue separar, e os campos chegam vazios no controller.
   */
  private async requestMultipart<T>(
    endpoint: string,
    body: FormData,
    method: 'POST' | 'PATCH' = 'POST'
  ): Promise<T> {
    // FormData pode ser reenviado: a nova tentativa depois do refresh manda o mesmo corpo.
    const response = await fetchWithAuth(endpoint, `${API_BASE}${endpoint}`, {
      method,
      body,
      headers: { Accept: 'application/json' },
    }, getAuthToken)

    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      console.error(`API returned non-JSON response for ${endpoint}:`, text.substring(0, 200))
      throw new ApiError(
        `Servidor retornou resposta invalida (${contentType || 'unknown'})`,
        response.status
      )
    }

    const data = await response.json()

    if (!response.ok || data.success === false) {
      // Mesmo tratamento de 402 que `request`. Hoje os dois unicos usos deste
      // metodo sao rotas de `api/v1/admin/`, que a allowlist do gate libera por
      // prefixo — mas divergir aqui deixaria armada a proxima rota de upload de
      // aluno: ela cairia num erro generico em vez de levar a tela de plano.
      if (
        response.status === SUBSCRIPTION_REQUIRED_STATUS &&
        data.code === SUBSCRIPTION_REQUIRED_CODE
      ) {
        markSubscriptionBlocked()
      }

      throw new ApiError(
        data.error || 'Erro ao enviar o arquivo',
        response.status,
        typeof data.code === 'string' ? data.code : undefined,
        data
      )
    }

    return data
  }

  /**
   * R08-21: baixa um arquivo autenticado (comprovante de resultado). O arquivo nao tem URL
   * publica: a API exige o Bearer, entao nao da para usar um <a href> direto.
   */
  private async requestBlob(endpoint: string): Promise<Blob> {
    const response = await fetchWithAuth(endpoint, `${API_BASE}${endpoint}`, {
      method: 'GET',
    }, getAuthToken)

    if (!response.ok) {
      let message = 'Não foi possível baixar o arquivo.'
      try {
        const data = await response.json()
        if (typeof data.error === 'string') message = data.error
      } catch {
        // corpo sem JSON: fica a mensagem padrao
      }
      throw new ApiError(message, response.status)
    }

    return response.blob()
  }

  /**
   * IA-08: POST que responde em SSE (assistente do instrutor). Devolve a `Response` crua
   * para o chamador ler o corpo aos poucos. Erro antes do stream (422/429/503) vem em JSON
   * normal e vira `ApiError` com o `code` do backend.
   */
  private async requestStream(endpoint: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    const response = await fetchWithAuth(endpoint, `${API_BASE}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    }, getAuthToken)

    if (!response.ok) {
      let message = 'Não foi possível falar com o assistente.'
      let code: string | undefined
      let data: unknown
      try {
        data = await response.json()
        const payload = data as { error?: unknown; code?: unknown }
        if (typeof payload.error === 'string') message = payload.error
        if (typeof payload.code === 'string') code = payload.code
      } catch {
        // corpo sem JSON: fica a mensagem padrao
      }
      throw new ApiError(message, response.status, code, data)
    }

    return response
  }

  // Auth endpoints
  auth = {
    login: (email: string): Promise<LoginResponse> =>
      this.request<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    register: (userData: {
      first_name: string
      last_name: string
      email: string
      telefone: string
      preparation_course: string
      terms: boolean
    }, planId?: string | null): Promise<{ success: boolean; message: string; email: string; expires_in: number }> =>
      this.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ user: userData, plan_id: planId }),
      }),

    verifyOtp: (email: string, otpCode: string, registrationData?: {
      first_name: string
      last_name: string
      telefone: string
      preparation_course: string
      plan_id?: string | null
    }): Promise<VerifyOtpResponse> =>
      this.request<VerifyOtpResponse>('/auth/verify_otp', {
        method: 'POST',
        body: JSON.stringify({
          email,
          otp_code: otpCode,
          registration_data: registrationData,
        }),
      }),

    me: (): Promise<MeResponse> =>
      this.request<MeResponse>('/auth/me'),

    logout: (): Promise<{ success: boolean }> =>
      this.request('/auth/logout', { method: 'POST' }),

    /** O refresh em si mora em lib/authSession.ts (`refreshAccessToken`), com voo unico. */
    refresh: (): Promise<RefreshResponse> =>
      this.request<RefreshResponse>('/auth/refresh', { method: 'POST' }),
  }

  // Dashboard endpoints
  dashboard = {
    getMetrics: (): Promise<{ success: boolean; data: DashboardMetrics }> =>
      this.request('/dashboard/metrics'),

    getStudyStats: (): Promise<{ success: boolean; data: StudyStats }> =>
      this.request('/dashboard/study_stats'),

    // Os cinco cards do topo do Dashboard (r6-02): totais da temporada e tres
    // indicadores de 30 dias com a variacao contra os 30 anteriores.
    getKpis: (): Promise<{ success: boolean; data: DashboardKpis }> =>
      this.request('/dashboard/kpis'),

    // Pentagono de desempenho em provas do Dashboard (r6-04): nota por grande area nas
    // provas dos ultimos 30 dias, tres origens, com a regra do eixo no payload.
    getExamTrackScores: (): Promise<{ success: boolean; data: ExamTrackScores }> =>
      this.request('/dashboard/exam_track_scores'),

    getAll: (): Promise<{ success: boolean; metrics: DashboardMetrics; recent_activity: RecentActivity[] }> =>
      this.request('/dashboard'),

    getProgressBenchmark: (): Promise<{ success: boolean; analysis: ProgressBenchmark }> =>
      this.request('/dashboard/progress_benchmark'),

    /**
     * LEGADO E SEM CONSUMIDOR. Alimentava a versao antiga de /app/analise-metricas,
     * hoje substituida por getTrackMetrics/getTrackDetail. E o unico dos endpoints
     * de metricas que EXISTE de fato no medhunter-api — mantido de proposito como
     * rede de seguranca enquanto os cinco endpoints novos forem mock. Remover (junto
     * com useMetricsAnalysis e os tipos MetricsAnalysis/SpecialtyPerformance/
     * FsrsOverdueItem/ThemeAnalysisItem) quando o backend entregar os novos.
     */
    getMetricsAnalysis: (): Promise<{ success: boolean; data: MetricsAnalysis }> =>
      this.request('/dashboard/metrics_analysis'),

    getTrackMetrics: (period?: string): Promise<{ success: boolean; data: TrackMetricsResponse }> =>
      this.request(`/dashboard/track_metrics${period ? `?period=${encodeURIComponent(period)}` : ''}`),

    /**
     * Detalhe de uma especialidade. A rota nao mudou com o spec r2/03; o PAYLOAD
     * ganhou `themes` (tabela de temas) e `theme_stage_series` (comparativo por
     * estagio E0, E1, E2...) — ver `TrackDetailResponse`. `exams_by_institution`
     * continua vindo, mas a tela deixou de desenha-lo (R1).
     */
    getTrackDetail: (code: string, period?: string): Promise<{ success: boolean; data: TrackDetailResponse }> =>
      this.request(
        `/dashboard/track_metrics/${encodeURIComponent(code)}${period ? `?period=${encodeURIComponent(period)}` : ''}`
      ),

    getTracks: (): Promise<{ success: boolean; data: TracksResponse }> =>
      this.request('/dashboard/tracks'),

    /**
     * Lista por prova da aba /app/analise-metricas?tab=provas.
     *
     * `period` e OMITIDO de proposito quando a aba esta em "Historico completo":
     * e a ausencia do parametro que o backend le como "sem recorte de data"
     * (decisao 4.3 do spec 03). Mandar string vazia daria o mesmo resultado, mas
     * sujaria a URL e a chave de cache do React Query.
     *
     * `withTracks: false` (r6-16) manda `with_tracks=0` e o backend OMITE `by_track` —
     * e o caminho das chips/picker do comparativo, que so precisam das linhas e nao
     * pagam o catalogo de trilhas. Omitido = payload de sempre.
     */
    getExamMetrics: (
      period?: string,
      { withTracks = true }: { withTracks?: boolean } = {},
    ): Promise<{ success: boolean; data: ExamMetricsResponse }> => {
      const params = new URLSearchParams()
      if (period) params.set('period', period)
      if (!withTracks) params.set('with_tracks', '0')
      const query = params.toString()
      return this.request(`/dashboard/exam_metrics${query ? `?${query}` : ''}`)
    },

    /**
     * Comparativo entre provas de /app/provas/comparar (spec r2/02).
     *
     * `tokens` sao `"<origem>:<id>"`, a mesma forma de `ExamMetricRow.id` — o id
     * numerico colide entre as tres tabelas de origem. Sem periodo de proposito:
     * a selecao e explicita, e recortar por data aqui poderia esconder justamente
     * a prova que o aluno acabou de marcar.
     *
     * Token que nao pertence ao aluno devolve 404 (e nao 403, para a rota nao
     * confirmar a existencia); selecao acima do teto devolve 422 com a razao.
     *
     * A resposta ganhou dois campos para a cascata especialidade -> tema, e NENHUM
     * parametro novo de requisicao — o filtro de tema escolhe quais linhas sao
     * desenhadas, nunca recalcula taxa, indice, total ou acerto, entao a mesma
     * selecao continua devolvendo os mesmos numeros:
     *
     * - `exams[].breakdowns.by_specialty[].specialty` e
     *   `exams[].breakdowns.by_theme[].specialty`: nome da especialidade PAI.
     *   Preenchido nas linhas de tema com pai no catalogo; `null` nas linhas de
     *   especialidade (ja sao o topo) e nas de registro manual.
     * - `meta.theme_limit`: o corte por volume que o backend aplica em `by_theme`
     *   por prova, para a tela separar "a prova nao cobrou o tema" de "o tema
     *   ficou fora do corte desta prova".
     *
     * Os tres pontos do contrato manual (CLAUDE.md) para esta mudanca: o
     * `Dashboard::ExamComparison` na API, este metodo, e `ExamComparisonBreakdown`
     * / `ExamComparisonMeta` em `src/types/api.ts`.
     */
    getExamComparison: (tokens: readonly string[]): Promise<{ success: boolean; data: ExamComparisonResponse }> =>
      this.request(`/dashboard/exam_comparison?attempts=${encodeURIComponent(tokens.join(','))}`),

    getStudyActivity: (
      period: string = 'all',
      weekOffset: number = 0
    ): Promise<{ success: boolean; data: StudyActivityResponse }> =>
      this.request(
        `/dashboard/study_activity?period=${encodeURIComponent(period)}&week_offset=${weekOffset}`
      ),

    // Guia estatistico (/app/guia-estatistico): COBERTURA dos topicos que as bancas
    // prioritarias cobram (o mesmo Dashboard::CoverageSummary de /app/analise-progresso),
    // perfil de cobranca por banca, prontidao como metrica de apoio e onde focar agora.
    //
    // Continua SEM query string: a cobertura nao depende de periodo — numerador (o que o
    // aluno registrou) e denominador (o que as bancas cobram) sao vitalicios.
    getStatisticalGuide: (): Promise<{ success: boolean; data: StatisticalGuide }> =>
      this.request('/dashboard/statistical_guide'),

    // Plano da jornada (/app/jornada): prazo, prontidao por instituicao,
    // capacidade semanal, o que fazer hoje e a grade da semana.
    //
    // `specialty` (spec r2/05) e o codigo de trilha do combo — recorta APENAS
    // `today.study` e `today.review`; `today.exam` e indiferente a ele (R6). E OMITIDO
    // quando o combo esta em "Geral", pelo mesmo motivo que `getExamMetrics` omite o
    // periodo: e a ausencia do parametro que o backend le como "sem recorte", e mandar
    // string vazia so sujaria a URL e a chave de cache. Codigo desconhecido nao e erro —
    // o backend responde 200 com `specialty_filter.selected: null`.
    //
    // `studyLimit` (spec r6-21, R1) e quantos itens de estudo primario pedir (1..20;
    // default 3 no backend). So vai na URL quando informado: omitido, o bundle antigo e a
    // API nova continuam se falando exatamente como antes.
    getJourneyPlan: (specialty?: string | null, studyLimit?: number): Promise<JourneyPlanResponse> => {
      const search = new URLSearchParams()
      if (specialty) search.set('specialty', specialty)
      if (studyLimit !== undefined) search.set('study_limit', String(studyLimit))
      const query = search.toString()
      return this.request(`/dashboard/journey_plan${query ? `?${query}` : ''}`)
    },

    // /app/analise-progresso: PRONTIDAO (combinada e por banca, o mesmo
    // Dashboard::ReadinessSummary do guia estatistico) e COBERTURA — topicos cobrados
    // pelas instituicoes prioritarias x topicos registrados no MedHunter manual, por
    // instituicao e por trilha. Ja e o Rails que responde — o mock MSW que servia esta
    // rota foi removido.
    //
    // `period` recorta simulados, registros fora do catalogo e — desde o
    // spec r2/04 — a TENDENCIA de desempenho por topico (`themes[].trend` e as duas
    // janelas de `meta.trend_window`, que e a atual e a anterior de mesmo tamanho). NAO
    // recorta cobertura nem prontidao: as duas sao vitalicias, e e isso que permite ao
    // guia (que nao tem seletor de periodo) exibir os mesmos numeros.
    //
    // Cuidado ao ler a linha da tabela: `study_count`/`last_studied_at` continuam
    // vitalicios ao lado de `trend`, que e da janela. Sao dois relogios na mesma linha.
    getProgressAnalysis: (
      period: string = 'all'
    ): Promise<{ success: boolean; data: ProgressAnalysisResponse }> =>
      this.request(`/dashboard/progress_analysis?period=${encodeURIComponent(period)}`),
  }

  // Quiz endpoints
  quizzes = {
    list: (): Promise<{ success: boolean; quizzes: Quiz[] }> =>
      this.request('/quizzes'),

    get: (id: ResourceId): Promise<{ success: boolean; quiz: Quiz; questions: QuizQuestion[] }> =>
      this.request(`/quizzes/${id}`),

    getQuestions: (id: ResourceId): Promise<{ success: boolean; questions: QuizQuestion[] }> =>
      this.request(`/quizzes/${id}/questions`),

    answer: (id: number, answers: Record<number, number>): Promise<{ success: boolean; result: QuizResult }> =>
      this.request(`/quizzes/${id}/answer`, {
        method: 'POST',
        body: JSON.stringify({ answers }),
      }),

    getResult: (id: ResourceId): Promise<{ success: boolean; attempt: QuizAttemptResult; results: QuizQuestionResult[] }> =>
      this.request(`/quizzes/${id}/result`),

    getPerformance: (): Promise<{ success: boolean; stats: unknown; recent_attempts: unknown[] }> =>
      this.request('/quizzes/performance'),
  }

  // Integration endpoints
  integrations = {
    // `sync_enabled` reflete a chave INTEGRATIONS_SYNC_ENABLED da API. Com ela em false
    // nenhuma chamada sai para os cursinhos, e os endpoints de sync devolvem 503.
    list: (): Promise<{ success: boolean; platforms: IntegrationPlatform[]; sync_enabled: boolean }> =>
      this.request('/integrations'),

    getMedwayStatus: (): Promise<{ success: boolean; connected: boolean; email: string | null; last_sync_at: string | null; sync_status: string | null; sync_error: string | null }> =>
      this.request('/integrations/medway_status'),

    getMedwayTrails: (): Promise<{ success: boolean; trails: MedwayTrail[] }> =>
      this.request('/integrations/medway_trails'),

    getMedwayEstudos: (): Promise<{ success: boolean; estudos: import('@/types/api').MedwayEstudo[]; stats: import('@/types/api').MedwayEstudosStats }> =>
      this.request('/integrations/medway_estudos'),

    getMedwayRevisoes: (): Promise<{ success: boolean; revisoes: import('@/types/api').MedwayRevisao[]; stats: import('@/types/api').MedwayRevisoesStats }> =>
      this.request('/integrations/medway_revisoes'),

    getMedwayRecommendedRevisions: (limit?: number): Promise<{ success: boolean; recommendations: import('@/types/api').MedwayRecommendation[]; stats: import('@/types/api').MedwayRecommendationsStats }> =>
      this.request(`/integrations/medway_recommended_revisions${limit ? `?limit=${limit}` : ''}`),

    getMedwaySimulados: (): Promise<{ success: boolean; simulados: import('@/types/api').MedwaySimulado[]; stats: import('@/types/api').MedwaySimuladosStats }> =>
      this.request('/integrations/medway_simulados'),

    getQuestionStats: (): Promise<{
      success: boolean
      total_questions: number
      by_speciality: Record<string, number>
      by_study_theme: Record<string, number>
      study_themes_with_specialty: Array<{ theme: string; speciality: string | null; count: number }>
      accuracy_by_speciality: Record<string, { total: number; correct: number; accuracy: number }>
      by_difficulty?: Record<string, number>
      by_institution?: Record<string, number>
      by_year?: Record<string, number>
      accuracy_by_difficulty?: Record<string, { total: number; correct: number; accuracy: number }>
      accuracy_by_year?: Record<string, { total: number; correct: number; accuracy: number }>
      accuracy_by_institution?: Record<string, { total: number; correct: number; accuracy: number }>
    }> =>
      this.request('/integrations/question_stats'),

    syncMedway: (useApi = false): Promise<{ success: boolean; message: string; trails_count?: number; sync_method?: string }> =>
      this.request(`/integrations/sync_medway${useApi ? '?use_api=true' : ''}`, { method: 'POST' }),

    // Fast API-based sync (no browser scraping)
    syncMedwayApi: (): Promise<{ success: boolean; message: string; trails_count?: number; sync_method?: string }> =>
      this.request('/integrations/sync_medway_api', { method: 'POST' }),

    syncAllQuestions: (useApi = false): Promise<{ success: boolean; message: string; questions_count?: number; trails_processed?: number; sync_method?: string }> =>
      this.request(`/integrations/sync_all_questions${useApi ? '?use_api=true' : ''}`, { method: 'POST' }),

    saveCredentials: (platform: string, email: string, password: string): Promise<{ success: boolean; message: string; credential?: unknown }> =>
      this.request('/integrations/credentials', {
        method: 'POST',
        body: JSON.stringify({ platform, email, password }),
      }),

    deleteCredentials: (platform: string): Promise<{ success: boolean; message: string }> =>
      this.request(`/integrations/credentials/${platform}`, { method: 'DELETE' }),

    testConnection: (platform: string, email: string, password: string): Promise<{ success: boolean; message: string }> =>
      this.request('/integrations/test_connection', {
        method: 'POST',
        body: JSON.stringify({ platform, email, password }),
      }),

    // Primary Studies from Medway API
    getPrimaryStudies: (): Promise<{ success: boolean; primary_studies: import('@/types/api').MedwayPrimaryStudy[]; stats: import('@/types/api').PrimaryStudyStats }> =>
      this.request('/integrations/primary_studies'),

    syncPrimaryStudies: (): Promise<{ success: boolean; message: string; created: number; updated: number; skipped: number }> =>
      this.request('/integrations/sync_primary_studies', { method: 'POST' }),

    // Revisions from Medway API
    getRevisions: (): Promise<{ success: boolean; revisions: import('@/types/api').MedwayRevision[]; stats: import('@/types/api').RevisionStats }> =>
      this.request('/integrations/revisions'),

    syncRevisions: (): Promise<{ success: boolean; message: string; created: number; updated: number; skipped: number }> =>
      this.request('/integrations/sync_revisions', { method: 'POST' }),

    // Challenges (Simulados) from Medway API. So a sincronizacao: a leitura
    // (`GET /integrations/challenges`) perdeu o ultimo consumidor junto com o painel
    // legado de /app/analise-progresso.
    syncChallenges: (): Promise<{ success: boolean; message: string; created: number; updated: number; skipped: number }> =>
      this.request('/integrations/sync_challenges', { method: 'POST' }),

    // Recommended Revisions
    getRecommendedRevisions: (): Promise<{ success: boolean; recommendations: import('@/types/api').MedwayRecommendedRevision[]; stats: import('@/types/api').RecommendedRevisionStats }> =>
      this.request('/integrations/recommended_revisions'),

    generateRecommendations: (): Promise<{ success: boolean; message: string; created: number; updated: number }> =>
      this.request('/integrations/generate_recommendations', { method: 'POST' }),

    completeRecommendation: (id: ResourceId): Promise<{ success: boolean; message: string; recommendation: import('@/types/api').MedwayRecommendedRevision }> =>
      this.request(`/integrations/recommended_revisions/${id}/complete`, { method: 'PATCH' }),

    // ==================== MEDCOF ENDPOINTS ====================

    getMedcofStatus: (): Promise<{ success: boolean; connected: boolean; email: string | null; last_sync_at: string | null; sync_status: string | null; sync_error: string | null }> =>
      this.request('/integrations/medcof_status'),

    getMedcofProfile: (): Promise<{ success: boolean; profile: import('@/types/api').MedcofProfile | null; products: import('@/types/api').MedcofProduct[] }> =>
      this.request('/integrations/medcof_profile'),

    getMedcofPreset: (): Promise<{ success: boolean; preset: import('@/types/api').MedcofPreset | null; institutions: import('@/types/api').MedcofInstitution[] }> =>
      this.request('/integrations/medcof_preset'),

    getMedcofReports: (): Promise<{ success: boolean; reports: import('@/types/api').MedcofReport[]; stats: import('@/types/api').MedcofReportStats }> =>
      this.request('/integrations/medcof_reports'),

    getMedcofReport: (id: number): Promise<{ success: boolean; report: import('@/types/api').MedcofReportDetail }> =>
      this.request(`/integrations/medcof_reports/${id}`),

    getMedcofStats: (): Promise<{
      success: boolean
      summary: { total_reports: number; total_questions: number; total_correct: number; avg_accuracy: number; last_sync: string | null }
      area_stats: import('@/types/api').MedcofAreaStatAggregated[]
      tag_stats: import('@/types/api').MedcofTagAggregated[]
      top_institutions: import('@/types/api').MedcofInstitution[]
    }> =>
      this.request('/integrations/medcof_stats'),

    getMedcofDailyProducts: (): Promise<{ success: boolean; products: import('@/types/api').MedcofDailyProduct[]; current_product: string | null; has_daily_cof: boolean }> =>
      this.request('/integrations/medcof_daily_products'),

    syncMedcof: (): Promise<{ success: boolean; message: string; profile?: unknown; preset?: unknown; daily_products?: unknown }> =>
      this.request('/integrations/sync_medcof', { method: 'POST' }),

    syncMedcofReport: (reportId: string): Promise<{ success: boolean; message: string; report_id?: number }> =>
      this.request('/integrations/sync_medcof_report', {
        method: 'POST',
        body: JSON.stringify({ report_id: reportId }),
      }),

    // Medcof Estudos, Revisoes, Recommended Revisions, Simulados
    getMedcofEstudos: (): Promise<{ success: boolean; estudos: MedcofEstudo[]; stats: MedcofEstudosStats }> =>
      this.request('/integrations/medcof_estudos'),

    getMedcofRevisoes: (): Promise<{ success: boolean; revisoes: MedcofRevisao[]; stats: MedcofRevisoesStats }> =>
      this.request('/integrations/medcof_revisoes'),

    getMedcofRecommendedRevisions: (limit?: number): Promise<{ success: boolean; recommendations: MedcofRecommendedRevision[]; stats: MedcofRecommendedStats }> =>
      this.request(`/integrations/medcof_recommended_revisions${limit ? `?limit=${limit}` : ''}`),

    getMedcofSimulados: (): Promise<{ success: boolean; simulados: MedcofSimulado[]; stats: MedcofSimuladosStats }> =>
      this.request('/integrations/medcof_simulados'),

    // ==================== ARISTO ENDPOINTS ====================

    getAristoStatus: (): Promise<{ success: boolean; connected: boolean; email: string | null; last_sync_at: string | null; sync_status: string | null; sync_error: string | null }> =>
      this.request('/integrations/aristo_status'),

    connectAristo: (email: string, password: string): Promise<{ success: boolean; message: string }> =>
      this.request('/integrations/connect_aristo', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    disconnectAristo: (): Promise<{ success: boolean; message: string }> =>
      this.request('/integrations/disconnect_aristo', { method: 'DELETE' }),

    syncAristo: (): Promise<{ success: boolean; message: string; results?: { estudos: { created: number; updated: number }; revisoes: { created: number; updated: number }; simulados: { created: number; updated: number } } }> =>
      this.request('/integrations/sync_aristo', { method: 'POST' }),

    getAristoEstudos: (): Promise<{ success: boolean; estudos: import('@/types/api').AristoEstudo[]; stats: import('@/types/api').AristoEstudosStats }> =>
      this.request('/integrations/aristo_estudos'),

    getAristoRevisoes: (): Promise<{ success: boolean; revisoes: import('@/types/api').AristoRevisao[]; stats: import('@/types/api').AristoRevisoesStats }> =>
      this.request('/integrations/aristo_revisoes'),

    getAristoSimulados: (): Promise<{ success: boolean; simulados: import('@/types/api').AristoSimulado[]; stats: import('@/types/api').AristoSimuladosStats }> =>
      this.request('/integrations/aristo_simulados'),

    getAristoRecommendedRevisions: (): Promise<{ success: boolean; recommendations: import('@/types/api').AristoRecommendation[]; stats: import('@/types/api').AristoRecommendationsStats }> =>
      this.request('/integrations/aristo_recommended_revisions'),

    syncAristoSimuladoDetails: (externalId: string, examType?: string): Promise<{ success: boolean; message: string; simulado?: import('@/types/api').AristoSimulado }> =>
      this.request('/integrations/sync_aristo_simulado_details', {
        method: 'POST',
        body: JSON.stringify({ external_id: externalId, exam_type: examType || 'smart' }),
      }),

    // Unified endpoints (combined data from all platforms)
    getIntegrationStatus: (): Promise<{ success: boolean; integrations: import('@/types/api').IntegrationStatus; has_any: boolean }> =>
      this.request('/integrations/integration_status'),

    getUnifiedEstudos: (): Promise<{ success: boolean; estudos: import('@/types/api').UnifiedEstudo[]; stats: import('@/types/api').UnifiedEstudosStats; integrations: import('@/types/api').IntegrationStatus }> =>
      this.request('/integrations/unified_estudos'),

    getUnifiedRevisoes: (): Promise<{ success: boolean; revisoes: import('@/types/api').UnifiedRevisao[]; stats: import('@/types/api').UnifiedRevisoesStats; integrations: import('@/types/api').IntegrationStatus }> =>
      this.request('/integrations/unified_revisoes'),

    getUnifiedRecommendedRevisions: (limit?: number): Promise<{ success: boolean; recommendations: import('@/types/api').UnifiedRecommendation[]; stats: import('@/types/api').UnifiedRecommendationsStats; integrations: import('@/types/api').IntegrationStatus }> =>
      this.request(`/integrations/unified_recommended_revisions${limit ? `?limit=${limit}` : ''}`),

    getUnifiedSimulados: (): Promise<{ success: boolean; simulados: import('@/types/api').UnifiedSimulado[]; stats: import('@/types/api').UnifiedSimuladosStats; integrations: import('@/types/api').IntegrationStatus }> =>
      this.request('/integrations/unified_simulados'),

    // ==================== PROVAS NA INTEGRA ENDPOINTS ====================

    getProvasInstitutions: (): Promise<{ success: boolean; institutions: import('@/types/api').ProvaInstitution[]; total: number }> =>
      this.request('/integrations/provas_integra/institutions'),

    getProvasExams: (params?: { institution_id?: number; year?: number; exam_type?: string; page?: number; per_page?: number }): Promise<{
      success: boolean
      exams: import('@/types/api').ProvaExam[]
      total: number
      page: number
      per_page: number
      total_pages: number
      filters: import('@/types/api').ProvasFilters
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.institution_id) searchParams.set('institution_id', params.institution_id.toString())
      if (params?.year) searchParams.set('year', params.year.toString())
      if (params?.exam_type) searchParams.set('exam_type', params.exam_type)
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      return this.request(`/integrations/provas_integra/exams?${searchParams.toString()}`)
    },

    getProvaExamDetail: (id: number): Promise<{ success: boolean; exam: import('@/types/api').ProvaExamDetail }> =>
      this.request(`/integrations/provas_integra/exams/${id}`),

    syncProvas: (): Promise<{ success: boolean; message: string; institutions?: { synced: number; total: number }; exams?: { synced: number; total: number } }> =>
      this.request('/integrations/provas_integra/sync', { method: 'POST' }),

    syncProvaExamQuestions: (examId: number): Promise<{ success: boolean; message: string; synced: number; total: number }> =>
      this.request(`/integrations/provas_integra/exams/${examId}/sync_questions`, { method: 'POST' }),

    syncAllProvaQuestions: (params?: { limit?: number; skipSynced?: boolean }): Promise<{
      success: boolean
      message: string
      exams_processed: number
      total_questions: number
      errors: Array<{ exam_id: number; name: string; error: string }>
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.skipSynced === false) searchParams.set('skip_synced', 'false')
      return this.request(`/integrations/provas_integra/sync_all_questions?${searchParams.toString()}`, { method: 'POST' })
    },

    // ==================== MEDCOF PROVAS NA INTEGRA ENDPOINTS ====================

    getMedcofProvasGroups: (): Promise<{ success: boolean; groups: import('@/types/api').MedcofProvaGroup[]; total: number }> =>
      this.request('/integrations/medcof_provas_integra/groups'),

    getMedcofProvasExams: (params?: { group_id?: number; year?: number; exam_type?: string; institution?: string; page?: number; per_page?: number }): Promise<{
      success: boolean
      exams: import('@/types/api').MedcofProvaExam[]
      total: number
      page: number
      per_page: number
      total_pages: number
      filters: import('@/types/api').MedcofProvasFilters
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.group_id) searchParams.set('group_id', params.group_id.toString())
      if (params?.year) searchParams.set('year', params.year.toString())
      if (params?.exam_type) searchParams.set('exam_type', params.exam_type)
      if (params?.institution) searchParams.set('institution', params.institution)
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      return this.request(`/integrations/medcof_provas_integra/exams?${searchParams.toString()}`)
    },

    getMedcofProvaExamDetail: (id: number): Promise<{ success: boolean; exam: import('@/types/api').MedcofProvaExamDetail }> =>
      this.request(`/integrations/medcof_provas_integra/exams/${id}`),

    syncMedcofProvas: (): Promise<{ success: boolean; message: string; groups_synced?: number; exams_synced?: number }> =>
      this.request('/integrations/medcof_provas_integra/sync', { method: 'POST' }),

    syncMedcofProvaExamQuestions: (examId: number): Promise<{ success: boolean; message: string; synced: number; total: number }> =>
      this.request(`/integrations/medcof_provas_integra/exams/${examId}/sync_questions`, { method: 'POST' }),

    syncAllMedcofProvaQuestions: (params?: { limit?: number; skipSynced?: boolean }): Promise<{
      success: boolean
      message: string
      exams_processed: number
      total_questions: number
      errors: Array<{ exam_id: number; name: string; error: string }>
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.skipSynced === false) searchParams.set('skip_synced', 'false')
      return this.request(`/integrations/medcof_provas_integra/sync_all_questions?${searchParams.toString()}`, { method: 'POST' })
    },

    // Get available user tests (tests the user has already started via web)
    getMedcofProvasAvailableUserTests: (): Promise<{
      success: boolean
      tests: import('@/types/api').MedcofProvaAvailableUserTest[]
      stats: { total: number; synced: number; pending: number; unknown: number }
    }> => this.request('/integrations/medcof_provas_integra/available_user_tests'),

    // Sync questions from user's existing test instances (the working approach)
    syncMedcofProvasFromUserTests: (params?: { maxPages?: number }): Promise<{
      success: boolean
      message: string
      exams_updated: number
      total_questions: number
      user_tests_processed: number
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.maxPages) searchParams.set('max_pages', params.maxPages.toString())
      return this.request(`/integrations/medcof_provas_integra/sync_from_user_tests?${searchParams.toString()}`, { method: 'POST' })
    },
  }

  // Video Aulas endpoints (subscription required)
  // Videoaulas de curso, lado do aluno (spec r4/12). A listagem NUNCA traz URL de
  // video; ela so vem de `openLesson`, que e POST porque grava o "assistido".
  videoCourses = {
    list: (): Promise<{ success: boolean; video_courses: import('@/types/api').StudentVideoCourse[] }> =>
      this.request('/video_courses'),

    get: (id: ResourceId): Promise<{ success: boolean; video_course: import('@/types/api').StudentVideoCourse }> =>
      this.request(`/video_courses/${id}`),

    /** O curso marcado como onboarding, ou `null` (R6). */
    onboarding: (): Promise<{ success: boolean; video_course: import('@/types/api').StudentVideoCourse | null }> =>
      this.request('/video_courses/onboarding'),

    /** 403 quando bloqueada — sem URL. */
    openLesson: (id: ResourceId): Promise<{ success: boolean; video_lesson: import('@/types/api').StudentOpenedLesson }> =>
      this.request(`/video_lessons/${id}/open`, { method: 'POST' }),
  }

  // ZOOM: aulas ao vivo, lado do aluno/instrutor (o admin tambem usa `join` para entrar como
  // host). Contrato em .spec/zoom/contrato.md §3.2. `join` so responde dentro da janela.
  liveSessions = {
    list: (scope: import('@/types/api').LiveSessionListScope = 'upcoming'): Promise<import('@/types/api').LiveSessionsResponse> =>
      this.request(`/live_sessions?scope=${scope}`),

    get: (id: ResourceId): Promise<import('@/types/api').LiveSessionResponse> =>
      this.request(`/live_sessions/${id}`),

    join: (id: ResourceId): Promise<import('@/types/api').LiveSessionJoinResponse> =>
      this.request(`/live_sessions/${id}/join`),

    // R10-13: busca da gravacao para quem conduz a aula.
    syncRecording: (id: ResourceId): Promise<import('@/types/api').LiveSessionRecordingSyncResponse> =>
      this.request(`/live_sessions/${id}/sync_recording`, { method: 'POST' }),

    // R10-12: destaque "Ao vivo" do menu e faixa do topo (consultado a cada minuto).
    liveNow: (): Promise<import('@/types/api').LiveNowResponse> => this.request('/live_sessions/live_now'),

    // MH13-39: card "Ultimas reunioes" do dashboard do aluno (vazio com a flag desligada).
    recent: (): Promise<import('@/types/api').RecentLiveSessionsResponse> => this.request('/live_sessions/recent'),
  }

  // HERMES: processos seletivos da lista do aluno logado (instituicoes-alvo e exames nacionais
  // que casam com ela), so a revisao aprovada. Contrato em .spec/hermes/contrato.md §3.4.
  examProcesses = {
    list: (): Promise<ExamProcessesResponse> => this.request('/exam_processes'),

    // D-014: marcos da lista do aluno no calendario. Faixa inclusiva, de no maximo 62 dias.
    calendar: (startDate: string, endDate: string): Promise<ExamCalendarResponse> => {
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate })
      return this.request(`/exam_processes/calendar?${qs.toString()}`)
    },
  }

  // D-013: radar do aluno, processos acompanhados fora da lista dele. Contrato em
  // .time/pacotes/P-004-contrato.md.
  examProcessRadar = {
    list: (): Promise<ExamRadarEntriesResponse> => this.request('/exam_process_radar_entries'),

    options: (): Promise<ExamRadarOptionsResponse> => this.request('/exam_process_radar_entries/options'),

    add: (targetType: ExamRadarTargetType, targetId: number): Promise<ExamRadarEntryResponse> =>
      this.request('/exam_process_radar_entries', {
        method: 'POST',
        body: JSON.stringify({ target_type: targetType, target_id: targetId }),
      }),

    remove: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/exam_process_radar_entries/${id}`, { method: 'DELETE' }),
  }

  // HERMES: processos seletivos no admin. Criar/editar grava uma revisao ja aprovada; a fila
  // aprova ou recusa as revisoes do agente. 422 do snapshot traz `errors` [{path, code,
  // message}] em `ApiError.details.errors`. Contrato em .spec/hermes/contrato.md §3.2.
  adminExamProcesses = {
    list: (params: AdminExamProcessListParams = {}): Promise<AdminExamProcessesResponse> => {
      const qs = new URLSearchParams()
      if (params.review) qs.set('review', params.review)
      if (params.category) qs.set('category', params.category)
      if (params.q) qs.set('q', params.q)
      if (params.page) qs.set('page', String(params.page))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/exam_processes${suffix}`)
    },

    get: (id: ResourceId): Promise<AdminExamProcessResponse> => this.request(`/admin/exam_processes/${id}`),

    create: (snapshot: ExamProcessSnapshot): Promise<AdminExamProcessSavedResponse> =>
      this.request('/admin/exam_processes', { method: 'POST', body: JSON.stringify({ snapshot }) }),

    update: (id: ResourceId, snapshot: ExamProcessSnapshot): Promise<AdminExamProcessSavedResponse> =>
      this.request(`/admin/exam_processes/${id}`, { method: 'PATCH', body: JSON.stringify({ snapshot }) }),

    revisions: (params: AdminExamProcessRevisionListParams = {}): Promise<AdminExamProcessRevisionsResponse> => {
      const qs = new URLSearchParams()
      if (params.status) qs.set('status', params.status)
      if (params.exam_process_id) qs.set('exam_process_id', String(params.exam_process_id))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/exam_process_revisions${suffix}`)
    },

    revision: (id: ResourceId): Promise<AdminExamProcessRevisionResponse> =>
      this.request(`/admin/exam_process_revisions/${id}`),

    approve: (id: ResourceId): Promise<AdminExamProcessRevisionResponse> =>
      this.request(`/admin/exam_process_revisions/${id}/approve`, { method: 'POST' }),

    reject: (id: ResourceId, reason: string | null): Promise<AdminExamProcessRevisionResponse> =>
      this.request(`/admin/exam_process_revisions/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  }

  // ZOOM: aulas ao vivo, admin. Contrato em .spec/zoom/contrato.md §3.1. Para escolher
  // instrutores/alunos use `admin.getInstructors` e `admin.getAvailableStudents`.
  adminLiveSessions = {
    list: (params: import('@/types/api').AdminLiveSessionListParams = {}): Promise<import('@/types/api').AdminLiveSessionsResponse> => {
      const searchParams = new URLSearchParams()
      if (params.scope) searchParams.set('scope', params.scope)
      if (params.status) searchParams.set('status', params.status)
      if (params.page) searchParams.set('page', params.page.toString())
      const query = searchParams.toString()
      return this.request(`/admin/live_sessions${query ? `?${query}` : ''}`)
    },

    get: (id: ResourceId): Promise<import('@/types/api').AdminLiveSessionResponse> =>
      this.request(`/admin/live_sessions/${id}`),

    create: (data: import('@/types/api').AdminLiveSessionCreatePayload): Promise<import('@/types/api').AdminLiveSessionResponse> =>
      this.request('/admin/live_sessions', { method: 'POST', body: JSON.stringify({ live_session: data }) }),

    update: (
      id: ResourceId,
      data: import('@/types/api').AdminLiveSessionUpdatePayload,
    ): Promise<import('@/types/api').AdminLiveSessionResponse> =>
      this.request(`/admin/live_sessions/${id}`, { method: 'PATCH', body: JSON.stringify({ live_session: data }) }),

    /** Cancela a aula (apaga o webinar na Zoom); o registro continua na lista. */
    cancel: (id: ResourceId): Promise<import('@/types/api').AdminLiveSessionResponse> =>
      this.request(`/admin/live_sessions/${id}`, { method: 'DELETE' }),

    addParticipants: (
      id: ResourceId,
      data: import('@/types/api').AdminLiveSessionParticipantsPayload,
    ): Promise<import('@/types/api').AdminLiveSessionResponse> =>
      this.request(`/admin/live_sessions/${id}/participants`, { method: 'POST', body: JSON.stringify(data) }),

    removeParticipant: (id: ResourceId, userId: ResourceId): Promise<import('@/types/api').AdminLiveSessionResponse> =>
      this.request(`/admin/live_sessions/${id}/participants/${userId}`, { method: 'DELETE' }),

    attendance: (id: ResourceId): Promise<import('@/types/api').AdminLiveSessionAttendanceResponse> =>
      this.request(`/admin/live_sessions/${id}/attendance`),

    // R10-13: busca sob demanda a gravacao na nuvem (quando o webhook nao chegou).
    syncRecording: (id: ResourceId): Promise<import('@/types/api').AdminLiveSessionRecordingSyncResponse> =>
      this.request(`/admin/live_sessions/${id}/sync_recording`, { method: 'POST' }),
  }

  // R10-09: pacote Zoom por padrinho; conceder enfileira o cadastro como host na conta da Zoom.
  adminZoomHosts = {
    list: (): Promise<import('@/types/api').AdminZoomHostsResponse> => this.request('/admin/zoom_hosts'),

    update: (userId: ResourceId, zoomEnabled: boolean): Promise<import('@/types/api').AdminZoomHostResponse> =>
      this.request(`/admin/zoom_hosts/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ zoom_enabled: zoomEnabled }),
      }),

    /** Reconsulta a Zoom agora (e cria/licencia o usuario se faltar). */
    check: (userId: ResourceId): Promise<import('@/types/api').AdminZoomHostResponse> =>
      this.request(`/admin/zoom_hosts/${userId}/check`, { method: 'POST' }),
  }

  // R10-14: pacotes de features do instrutor (Basico, Zoom...) e os pacotes de cada padrinho.
  // Conceder/tirar pacote com a feature Zoom provisiona/revoga o host da Zoom no servidor.
  adminInstructorPackages = {
    list: (): Promise<import('@/types/api').InstructorPackagesResponse> => this.request('/admin/instructor_packages'),

    create: (payload: import('@/types/api').InstructorPackagePayload): Promise<import('@/types/api').InstructorPackageResponse> =>
      this.request('/admin/instructor_packages', { method: 'POST', body: JSON.stringify({ instructor_package: payload }) }),

    update: (
      id: ResourceId,
      payload: Partial<import('@/types/api').InstructorPackagePayload>,
    ): Promise<import('@/types/api').InstructorPackageResponse> =>
      this.request(`/admin/instructor_packages/${id}`, { method: 'PATCH', body: JSON.stringify({ instructor_package: payload }) }),

    remove: (id: ResourceId): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/instructor_packages/${id}`, { method: 'DELETE' }),

    forUser: (userId: ResourceId): Promise<import('@/types/api').UserInstructorPackagesResponse> =>
      this.request(`/admin/users/${userId}/instructor_packages`),

    /** Troca o conjunto inteiro de pacotes (nao padrao) do padrinho. */
    setForUser: (userId: ResourceId, packageIds: number[]): Promise<import('@/types/api').UserInstructorPackagesResponse> =>
      this.request(`/admin/users/${userId}/instructor_packages`, {
        method: 'PUT',
        body: JSON.stringify({ instructor_package_ids: packageIds }),
      }),
  }

  // VENDA-02: links de venda do comercial (secao "Links de venda"). O valor e sempre
  // recalculado no servidor; o token so volta no create.
  adminSalesLinks = {
    list: (
      params: import('@/types/api').AdminSalesLinksListParams = {},
    ): Promise<import('@/types/api').AdminSalesLinksListResponse> => {
      const query = new URLSearchParams()
      if (params.status) query.set('status', params.status)
      if (params.created_by_id) query.set('created_by_id', String(params.created_by_id))
      if (params.page) query.set('page', String(params.page))
      if (params.per_page) query.set('per_page', String(params.per_page))
      const suffix = query.toString()
      return this.request(`/admin/sales_links${suffix ? `?${suffix}` : ''}`)
    },

    options: (): Promise<import('@/types/api').AdminSalesLinkOptionsResponse> =>
      this.request('/admin/sales_links/options'),

    /** Cotacao ao vivo: sempre 200; os erros vem em `data.errors`. */
    quote: (
      payload: import('@/types/api').SalesLinkQuotePayload,
    ): Promise<import('@/types/api').SalesLinkQuoteResponse> =>
      this.request('/admin/sales_links/quote', { method: 'POST', body: JSON.stringify(payload) }),

    /** 422 com `errors` (ApiError.details) quando a soma, a validade ou o cliente nao batem. */
    create: (
      payload: import('@/types/api').SalesLinkCreatePayload,
    ): Promise<import('@/types/api').AdminSalesLinkCreateResponse> =>
      this.request('/admin/sales_links', { method: 'POST', body: JSON.stringify(payload) }),

    get: (id: ResourceId): Promise<import('@/types/api').AdminSalesLinkResponse> =>
      this.request(`/admin/sales_links/${id}`),

    /** So link aberto; link usado responde 422 `not_cancellable`. */
    cancel: (id: ResourceId): Promise<import('@/types/api').AdminSalesLinkResponse> =>
      this.request(`/admin/sales_links/${id}/cancel`, { method: 'POST' }),

    /** VENDA-05: gera de novo so a parte que falhou (422 `not_resendable`, 502 se o Asaas falhar). */
    resendPart: (
      id: ResourceId,
      payload: { part_id: number; reason?: string },
    ): Promise<import('@/types/api').AdminSalesLinkResponse> =>
      this.request(`/admin/sales_links/${id}/resend_part`, { method: 'POST', body: JSON.stringify(payload) }),

    /** VENDA-05: libera o acesso com o que ja foi pago; motivo obrigatorio. */
    releaseAccess: (id: ResourceId, reason: string): Promise<import('@/types/api').AdminSalesLinkResponse> =>
      this.request(`/admin/sales_links/${id}/release_access`, { method: 'POST', body: JSON.stringify({ reason }) }),

    /** VENDA-05: cancela no Asaas as cobrancas em aberto e encerra o link; motivo obrigatorio. */
    cancelRemaining: (id: ResourceId, reason: string): Promise<import('@/types/api').AdminSalesLinkResponse> =>
      this.request(`/admin/sales_links/${id}/cancel_remaining`, { method: 'POST', body: JSON.stringify({ reason }) }),

    /**
     * MH14-09: reenvia o e-mail com o link ao comprador. 422 `resend_cooldown`,
     * `link_not_resendable`, `email_unavailable` ou `no_recipient` (ApiError.code).
     */
    resendEmail: (id: ResourceId): Promise<import('@/types/api').AdminSalesLinkResendEmailResponse> =>
      this.request(`/admin/sales_links/${id}/resend_email`, { method: 'POST' }),
  }

  // ZOOM: credenciais da Zoom editadas no admin. Spec .spec/zoom/configuracao-admin.md.
  // Os segredos nunca voltam; no PATCH, segredo omitido = manter o salvo.
  adminZoomSettings = {
    get: (): Promise<import('@/types/api').AdminZoomSettingsResponse> => this.request('/admin/zoom_settings'),

    update: (
      data: import('@/types/api').AdminZoomSettingsUpdatePayload,
    ): Promise<import('@/types/api').AdminZoomSettingsResponse> =>
      this.request('/admin/zoom_settings', { method: 'PATCH', body: JSON.stringify({ zoom_settings: data }) }),

    /** Testa a configuracao SALVA. Aceita o resultado solto ou dentro de `data`. */
    test: async (): Promise<import('@/types/api').AdminZoomSettingsTestResult> => {
      type TestResult = import('@/types/api').AdminZoomSettingsTestResult
      const response = await this.request<TestResult & { data?: TestResult }>('/admin/zoom_settings/test', {
        method: 'POST',
      })
      return response.data ?? response
    },
  }

  // R12-02: contrato do aluno assinado pelo Autentique (medhunter-api/docs/autentique-contrato.md).
  // O aluno assina pelo link devolvido aqui; o Autentique nao envia nada.
  contract = {
    get: (): Promise<import('@/types/api').StudentContractResponse> => this.request('/contract'),

    /** Emite (ou devolve o pendente/assinado). `cpf` so quando o checkout nao tem. */
    issue: (
      data: import('@/types/api').StudentContractIssueData = {},
    ): Promise<import('@/types/api').StudentContractResponse> =>
      this.request('/contract', {
        method: 'POST',
        body: JSON.stringify(Object.fromEntries(Object.entries(data).filter(([, value]) => Boolean(value)))),
      }),

    /** Consulta o Autentique pelo contrato pendente (nao espera o webhook). */
    refresh: (): Promise<import('@/types/api').StudentContractResponse> =>
      this.request('/contract/refresh', { method: 'POST' }),
  }

  adminStudentContract = {
    getSettings: (): Promise<import('@/types/api').AdminStudentContractSettingsResponse> =>
      this.request('/admin/student_contract_settings'),

    updateSettings: (data: { enabled: boolean }): Promise<import('@/types/api').AdminStudentContractSettingsResponse> =>
      this.request('/admin/student_contract_settings', {
        method: 'PATCH',
        body: JSON.stringify({ student_contract_settings: data }),
      }),

    // Modelos de contrato (um por plano: PDF fixo ou HTML com variaveis, R12-08).
    // Sem exclusao do que ja foi usado: arquivar.
    listTemplates: (): Promise<import('@/types/api').AdminContractTemplatesResponse> =>
      this.request('/admin/contract_templates'),

    createTemplate: (
      data: import('@/types/api').AdminContractTemplatePayload & { name: string },
    ): Promise<import('@/types/api').AdminContractTemplateResponse> =>
      this.requestMultipart('/admin/contract_templates', this.contractTemplateForm(data)),

    updateTemplate: (
      id: ResourceId,
      data: import('@/types/api').AdminContractTemplatePayload,
    ): Promise<import('@/types/api').AdminContractTemplateResponse> =>
      this.requestMultipart(`/admin/contract_templates/${id}`, this.contractTemplateForm(data), 'PATCH'),

    archiveTemplate: (id: ResourceId): Promise<import('@/types/api').AdminContractTemplateResponse> =>
      this.request(`/admin/contract_templates/${id}/archive`, { method: 'POST' }),

    unarchiveTemplate: (id: ResourceId): Promise<import('@/types/api').AdminContractTemplateResponse> =>
      this.request(`/admin/contract_templates/${id}/unarchive`, { method: 'POST' }),

    deleteTemplate: (id: ResourceId): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/contract_templates/${id}`, { method: 'DELETE' }),

    downloadTemplate: (id: ResourceId): Promise<Blob> => this.requestBlob(`/admin/contract_templates/${id}/file`),

    /** R12-08: variaveis aceitas no modelo HTML, na ordem da lista fechada. */
    listVariables: (): Promise<import('@/types/api').AdminContractVariablesResponse> =>
      this.request('/admin/contract_templates/variables'),

    /** R12-08: renderiza com os exemplos ficticios; nao valida nem salva. */
    previewTemplate: (bodyHtml: string): Promise<import('@/types/api').AdminContractPreviewResponse> =>
      this.request('/admin/contract_templates/preview', {
        method: 'POST',
        body: JSON.stringify({ body_html: bodyHtml }),
      }),

    getForUser: (userId: ResourceId): Promise<import('@/types/api').AdminUserContractResponse> =>
      this.request(`/admin/users/${userId}/contract`),

    /** Sem `contractId`, o servidor escolhe o pendente mais recente (senao o atual). */
    refreshForUser: (userId: ResourceId, contractId?: number): Promise<import('@/types/api').AdminUserContractResponse> =>
      this.request(`/admin/users/${userId}/contract/refresh`, {
        method: 'POST',
        body: JSON.stringify(contractId ? { contract_id: contractId } : {}),
      }),
  }

  private contractTemplateForm(data: import('@/types/api').AdminContractTemplatePayload): FormData {
    const form = new FormData()
    if (data.name !== undefined) form.append('name', data.name)
    if (data.kind !== undefined) form.append('kind', data.kind)
    if (data.file) form.append('file', data.file)
    if (data.body_html !== undefined) form.append('body_html', data.body_html)
    return form
  }

  // TRANSC-04/16: transcricao das 1:1 no admin. Contrato .spec/transc/contrato.md §4.
  // Chaves nunca voltam (so mascaradas); no PUT, chave em branco/omitida = manter a salva.
  adminTranscription = {
    getSettings: (): Promise<import('@/types/api').AdminTranscriptionSettingsResponse> =>
      this.request('/admin/transcription_settings'),

    updateSettings: (
      data: import('@/types/api').AdminTranscriptionSettingsUpdatePayload,
    ): Promise<import('@/types/api').AdminTranscriptionSettingsResponse> =>
      this.request('/admin/transcription_settings', {
        method: 'PUT',
        body: JSON.stringify({ transcription_settings: data }),
      }),

    /** Sem `data` testa a config gravada; com `provider`/`model`, testa outro par sem gravar. */
    test: async (
      data?: import('@/types/api').AdminTranscriptionTestPayload,
    ): Promise<import('@/types/api').AdminTranscriptionTestResult> => {
      const response = await this.request<{ data: import('@/types/api').AdminTranscriptionTestResult }>(
        '/admin/transcription_settings/test',
        { method: 'POST', body: data ? JSON.stringify(data) : undefined },
      )
      return response.data
    },

    getPilot: (
      range?: import('@/types/api').AdminTranscriptionPilotRange,
    ): Promise<import('@/types/api').AdminTranscriptionPilotResponse> => {
      const params = new URLSearchParams()
      if (range?.from) params.set('from', range.from)
      if (range?.to) params.set('to', range.to)
      const query = params.toString()
      return this.request(`/admin/transcription_pilot${query ? `?${query}` : ''}`)
    },

    retry: (recordingId: number): Promise<import('@/types/api').AdminTranscriptionRetryResponse> =>
      this.request(`/admin/transcription_pilot/recordings/${recordingId}/retry`, { method: 'POST' }),

    getMeetingTranscript: (meetingId: number): Promise<import('@/types/api').AdminMeetingTranscriptResponse> =>
      this.request(`/admin/mentorship_meetings/${meetingId}/transcript`),
  }

  // R10-24: metricas das 1:1 por madrinha (numero, duracao media, frequencia e as 1:1 com transcricao).
  adminMeetingMetrics = {
    get: (
      range?: import('@/types/api').AdminTranscriptionPilotRange,
    ): Promise<import('@/types/api').AdminMeetingMetricsResponse> => {
      const params = new URLSearchParams()
      if (range?.from) params.set('from', range.from)
      if (range?.to) params.set('to', range.to)
      const query = params.toString()
      return this.request(`/admin/mentorship_meeting_metrics${query ? `?${query}` : ''}`)
    },
  }

  // TRANSC-14: versao e link de download do app desktop (rota publica do namespace transcriber).
  transcriberApp = {
    version: (): Promise<import('@/types/api').TranscriberVersionResponse> => this.request('/transcriber/version'),
  }

  // Medway Trails endpoints
  medwayTrails = {
    get: (id: ResourceId): Promise<{ success: boolean; trail: MedwayTrailDetail; questions: MedwayQuestion[]; stats: MedwayTrailStats }> =>
      this.request(`/medway_trails/${id}`),

    updateUrl: (id: ResourceId, manualUrl: string): Promise<{ success: boolean; message: string; trail: MedwayTrailDetail }> =>
      this.request(`/medway_trails/${id}/update_url`, {
        method: 'PATCH',
        body: JSON.stringify({ manual_url: manualUrl }),
      }),

    // Sync questions via API (fast) or scraper (fallback)
    syncQuestions: (id: ResourceId, useScraper = false): Promise<{ success: boolean; message: string; questions_count: number; trail: MedwayTrailDetail; sync_method?: string }> =>
      this.request(`/medway_trails/${id}/sync_questions${useScraper ? '?use_scraper=true' : ''}`, { method: 'POST' }),
  }

  // Manual Entries endpoints (MedHunter without integration)
  manualEntries = {
    // Primary Studies
    // `stats` e `filters` sao sempre do TODO; `meta` e `primary_studies` sao da pagina
    // (spec r4-09). Sem `page` vem tudo, como antes — Dashboard, /app/estudos e as
    // recomendacoes dependem disso.
    getPrimaryStudies: (params: import('@/types/api').ManualListParams = {}): Promise<{
      success: boolean
      primary_studies: import('@/types/api').ManualPrimaryStudy[]
      stats: import('@/types/api').ManualPrimaryStudyStats
      meta: import('@/types/api').PaginationMeta
      filters: { specialities: string[] }
      /** r11-08. Ausente em API antiga. */
      kind_counts?: import('@/types/api').StudyRecordKindCounts
    }> => this.request(`/manual_entries/primary_studies${manualListQuery(params)}`),

    // r11-09: o create legado (POST primary_studies / revisions) devolve 410; o registro
    // novo e `createStudyEntries`.
    updatePrimaryStudy: (id: number, data: Partial<import('@/types/api').CreatePrimaryStudyData>): Promise<{
      success: boolean
      message: string
      primary_study: import('@/types/api').ManualPrimaryStudy
    }> => this.request(`/manual_entries/primary_studies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ primary_study: data }),
    }),

    deletePrimaryStudy: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/manual_entries/primary_studies/${id}`, { method: 'DELETE' }),

    // Revisions
    getRevisions: (): Promise<{
      success: boolean
      revisions: import('@/types/api').ManualRevision[]
      stats: import('@/types/api').ManualRevisionStats
    }> => this.request('/manual_entries/revisions'),

    updateRevision: (id: number, data: Partial<import('@/types/api').CreateRevisionData>): Promise<{
      success: boolean
      message: string
      revision: import('@/types/api').ManualRevision
    }> => this.request(`/manual_entries/revisions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ revision: data }),
    }),

    deleteRevision: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/manual_entries/revisions/${id}`, { method: 'DELETE' }),

    // Especialidades do "Resultado por Especialidade": derivadas das provas na integra
    // das instituicoes prioritarias do aluno, no processo seletivo dele. Nao e lista fixa.
    getTestSpecialties: (): Promise<
      { success: boolean } & import('@/types/api').TestSpecialtyCatalog
    > => this.request('/manual_entries/test_specialties'),

    // Tests and Simulations
    getTestsAndSimulations: (params: import('@/types/api').ManualListParams = {}): Promise<{
      success: boolean
      tests: import('@/types/api').ManualTestAndSimulation[]
      stats: import('@/types/api').ManualTestStats
      meta: import('@/types/api').PaginationMeta
    }> => this.request(`/manual_entries/tests_and_simulations${manualListQuery(params)}`),

    createTestAndSimulation: (
      data: import('@/types/api').CreateTestAndSimulationData,
      options?: import('@/types/api').IdempotentRequestOptions,
    ): Promise<{
      success: boolean
      message: string
      test: import('@/types/api').ManualTestAndSimulation
    }> => this.request('/manual_entries/tests_and_simulations', {
      method: 'POST',
      headers: idempotencyHeaders(options),
      body: JSON.stringify({ test_and_simulation: data }),
    }),

    updateTestAndSimulation: (id: number, data: Partial<import('@/types/api').CreateTestAndSimulationData>): Promise<{
      success: boolean
      message: string
      test: import('@/types/api').ManualTestAndSimulation
    }> => this.request(`/manual_entries/tests_and_simulations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ test_and_simulation: data }),
    }),

    deleteTestAndSimulation: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/manual_entries/tests_and_simulations/${id}`, { method: 'DELETE' }),

    // ---- Primario vs. revisao ----
    // Pre-classificacao: so consulta, nao grava. E o que diz a SPA se precisa perguntar.
    // Desde a spec r2/08 o veredito e por TOPICO: uma aula com dois topicos em estados
    // diferentes devolve DUAS linhas em `results`, com o mesmo `name` e `topic_id`
    // diferentes — indexar a resposta so por `name` perde uma delas.
    //
    // Cada linha traz `classification_state` (spec r3/02): `first` grava estudo primario
    // sem modal, `ask` obriga o modal, `auto_revision` grava revisao sem modal e com
    // aviso na tela. A REGRA E DO SERVIDOR — a SPA le o estado e renderiza, nunca o
    // deduz do historico.
    classifyEntries: (
      items: import('@/types/api').StudyEntryItemInput[],
    ): Promise<import('@/types/api').ClassifyEntriesResponse> =>
      this.request('/manual_entries/classify_entries', {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),

    // Gravacao. O servidor reclassifica; do cliente ele so aceita a ESCOLHA — `choice`
    // para o item inteiro e `topic_choices` por topico (spec r2/08). Quando alguma escolha
    // falta, a resposta volta com `needs_choice` e NADA foi gravado. Uma unica aula pode
    // devolver DOIS itens em `created` (um estudo e uma revisao), com o tempo rateado.
    //
    // `topic_choices` tambem carrega o "foi estudo primario?" de um topico em
    // `auto_revision` (spec r3/02, decisao 4.1): a escolha explicita `primary` grava o
    // registro como primario (`classification: user_primary`) sem tirar o topico do
    // estado — a revisao anterior continua existindo, e o proximo registro volta a ser
    // revisao automatica.
    createStudyEntries: (
      entry: import('@/types/api').StudyEntryAttributes,
      items: import('@/types/api').StudyEntryItemInput[],
      options?: import('@/types/api').IdempotentRequestOptions,
    ): Promise<import('@/types/api').CreateStudyEntriesResponse> =>
      this.request('/manual_entries/study_entries', {
        method: 'POST',
        headers: idempotencyHeaders(options),
        body: JSON.stringify({ entry, items }),
      }),

    // Suggestions for autocomplete.
    //
    // r6-17: sem `speciality`. O filtro e o processo seletivo do aluno, decidido no
    // SERVIDOR (Studies::ThemeSuggestions); a rota ainda aceita o parametro por
    // compatibilidade, mas a SPA nao o manda mais. Minimo de 3 caracteres nos dois lados.
    getSuggestions: (query: string, typeFilter?: 'lesson' | 'topic'): Promise<{
      success: boolean
      themes: import('@/types/api').ThemeSuggestion[]
      specialties: import('@/types/api').SpecialtyOption[]
      /** O mesmo objeto de `getPrepCourse` (spec r4-02). `null` = cursinho nao resolvido. */
      prep_course: import('@/types/api').ResolvedPrepCourse | null
    }> => {
      const params = new URLSearchParams()
      if (query) params.set('query', query)
      if (typeFilter) params.set('type_filter', typeFilter)
      return this.request(`/manual_entries/suggestions?${params.toString()}`)
    },

    /**
     * O cursinho do aluno como a API o enxerga (spec r4-02): resolvido de
     * `preparation_course`, com a contagem de aulas ativas. `null` = nao resolveu.
     * E o que decide se o formulario de estudo oferece a aba "Aula".
     */
    getPrepCourse: (): Promise<{
      success: boolean
      prep_course: import('@/types/api').ResolvedPrepCourse | null
      declared_course: string | null
    }> => this.request('/manual_entries/prep_course'),

    getSpecialties: (): Promise<{
      success: boolean
      specialties: import('@/types/api').SpecialtyOption[]
    }> => this.request('/manual_entries/specialties'),

    // Get study suggestions based on user's target institution prevalence
    getStudySuggestions: (): Promise<{
      success: boolean
      suggestions: StudySuggestion[]
      institutions: string[]
      total_suggestions: number
      message?: string
    }> => this.request('/manual_entries/study_suggestions'),
  }

  // Tempo Extra — estudo PASSIVO (spec r2/06).
  //
  // Grupo proprio, e nao mais um punhado de metodos em `manualEntries`, porque no backend
  // e um recurso proprio (`/api/v1/extra_study_times`, `ExtraStudyTimesController`) e nao
  // uma acao do `manual_entries_controller.rb`. A sub-aba do Manual e a mesma tela; o
  // recurso e outro.
  //
  // UNIDADE: `duration_minutes` viaja em MINUTOS nos dois sentidos. Nada aqui converte —
  // as colunas legadas e que estao em horas.
  extraStudyTimes = {
    list: (): Promise<import('@/types/api').ExtraStudyTimesResponse> =>
      this.request('/extra_study_times'),

    create: (data: import('@/types/api').CreateExtraStudyTimeData): Promise<{
      success: boolean
      message: string
      extra_study_time: import('@/types/api').ExtraStudyTime
    }> => this.request('/extra_study_times', {
      method: 'POST',
      body: JSON.stringify({ extra_study_time: data }),
    }),

    update: (id: number, data: Partial<import('@/types/api').CreateExtraStudyTimeData>): Promise<{
      success: boolean
      message: string
      extra_study_time: import('@/types/api').ExtraStudyTime
    }> => this.request(`/extra_study_times/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ extra_study_time: data }),
    }),

    delete: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/extra_study_times/${id}`, { method: 'DELETE' }),
  }

  // Flashcards — sessao de revisao mista, sem tema (.spec/flashcards.md, caso 2).
  // Recurso proprio (`/api/v1/flashcard_sessions`). `minutes` em MINUTOS.
  flashcardSessions = {
    list: (): Promise<import('@/types/api').FlashcardSessionsResponse> =>
      this.request('/flashcard_sessions'),

    create: (data: import('@/types/api').CreateFlashcardSessionData): Promise<{
      success: boolean
      message: string
      flashcard_session: import('@/types/api').FlashcardSession
    }> => this.request('/flashcard_sessions', {
      method: 'POST',
      body: JSON.stringify({ flashcard_session: data }),
    }),

    update: (id: number, data: Partial<import('@/types/api').CreateFlashcardSessionData>): Promise<{
      success: boolean
      message: string
      flashcard_session: import('@/types/api').FlashcardSession
    }> => this.request(`/flashcard_sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ flashcard_session: data }),
    }),

    delete: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/flashcard_sessions/${id}`, { method: 'DELETE' }),
  }

  // Cronometro de TED — a contagem em andamento (spec r2/07).
  //
  // Grupo proprio pelo mesmo motivo do de cima: no backend e
  // `/api/v1/study_sessions` + `StudySessionsController`, e nao uma acao do
  // `manual_entries_controller.rb`.
  //
  // R4: NENHUM metodo aqui envia duracao. O servidor mede por timestamp; o corpo do
  // `finish` so leva o que o cronometro nao consegue medir (questoes, acertos, conteudo).
  studySessions = {
    // R3: cria a linha ANTES de o relogio comecar. Se algum tema for ambiguo, a resposta
    // volta com `needs_choice` e a contagem NAO abre — perguntar depois de duas horas de
    // estudo e perguntar no pior momento possivel.
    create: (
      session: import('@/types/api').CreateStudySessionData,
      items: import('@/types/api').StudyEntryItemInput[],
    ): Promise<import('@/types/api').StudySessionResponse> =>
      this.request('/study_sessions', {
        method: 'POST',
        body: JSON.stringify({ study_session: session, items }),
      }),

    // R6: a retomada. `study_session: null` quando nao ha contagem aberta — ausencia e
    // resposta valida, nao 404.
    current: (): Promise<import('@/types/api').StudySessionResponse> =>
      this.request('/study_sessions/current'),

    // O clique em "Encerrar": para o relogio NO SERVIDOR. Sem isto, o tempo que o aluno
    // leva respondendo "vai fazer questoes?" e preenchendo o encerramento entrava no TED,
    // e o numero congelado na tela nao era o numero gravado.
    stop: (id: number): Promise<import('@/types/api').StudySessionResponse> =>
      this.request(`/study_sessions/${id}/stop`, { method: 'PATCH' }),

    // O "voltar" do encerramento: volta a contar sem somar o tempo em que ficou parada.
    resume: (id: number): Promise<import('@/types/api').StudySessionResponse> =>
      this.request(`/study_sessions/${id}/resume`, { method: 'PATCH' }),

    // R5: teorico -> questoes na MESMA sessao. Sem corpo: o servidor fecha a fase pelo
    // timestamp dela.
    advance: (id: number): Promise<import('@/types/api').StudySessionResponse> =>
      this.request(`/study_sessions/${id}/advance`, { method: 'PATCH' }),

    // r3-01 R3: estudo -> pausa -> proximo ciclo do Pomodoro. SO Pomodoro; nos outros
    // modos o servidor devolve 422, porque nao ha etapa nenhuma para avancar.
    //
    // Sem corpo, e IDEMPOTENTE de proposito: o servidor deriva a transicao dos timestamps
    // que ja tem. E o que faz o clique duplo do aluno — ou o retry de rede — nao pular
    // dois ciclos. O fim do modo `timer` NAO passa por aqui: la o cliente chama `stop`.
    advanceStage: (id: number): Promise<import('@/types/api').StudySessionResponse> =>
      this.request(`/study_sessions/${id}/advance_stage`, { method: 'PATCH' }),

    finish: (
      id: number,
      finish: import('@/types/api').FinishStudySessionData = {},
      confirmOverCap = false,
    ): Promise<import('@/types/api').FinishStudySessionResponse> =>
      this.request(`/study_sessions/${id}/finish`, {
        method: 'POST',
        body: JSON.stringify({ finish, confirm_over_cap: confirmOverCap }),
      }),

    discard: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/study_sessions/${id}`, { method: 'DELETE' }),
  }

  /**
   * Web Push (spec r3-01, R4) — o aviso de fim de etapa com a aba fechada.
   *
   * Grupo proprio, e nao um apendice de `studySessions`, porque a inscricao pertence ao
   * NAVEGADOR e nao a uma contagem: ela sobrevive a sessao inteira e e reusada pela
   * proxima. Quem orquestra estes tres passos e `lib/push/webPush.ts` — nenhuma tela
   * deveria chama-los solta.
   */
  push = {
    // Consultado ANTES de pedir permissao ao navegador: com `enabled: false` (ambiente
    // sem VAPID) o cliente cai no aviso in-app sem gastar o unico pedido de permissao
    // que o navegador concede por origem.
    publicKey: (): Promise<import('@/types/api').PushPublicKeyResponse> =>
      this.request('/push_subscriptions/public_key'),

    // Upsert por `endpoint` no servidor. Pode ser chamado a cada abertura de contagem: o
    // mesmo navegador reinscrito nao duplica linha, so troca de dono (o mesmo aparelho
    // pode ser usado por outro aluno).
    subscribe: (
      subscription: import('@/types/api').PushSubscriptionPayload,
    ): Promise<{ success: boolean; message: string }> =>
      this.request('/push_subscriptions', {
        method: 'POST',
        body: JSON.stringify({ subscription }),
      }),

    // Idempotente: endpoint inexistente ou de outro aluno responde 200 do mesmo jeito.
    unsubscribe: (endpoint: string): Promise<{ success: boolean; message: string }> =>
      this.request('/push_subscriptions/unsubscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),
  }

  // Catalogo publico (Especialidade > Subespecialidade > Topico).
  //
  // Era `/reference_data/*`, quando os tres niveis moravam numa tabela so. A acao
  // `search` desapareceu junto: ela recebia a `category` como parametro, e sem essa
  // coluna nao havia o que passar — nenhuma tela chamava (o unico consumidor possivel
  // era um hook sem importador).
  catalog = {
    /**
     * Sugestoes de instituicao das PROVAS e das prioridades do aluno. Le `institutions`
     * (bancas: Catalogos > Instituicoes), que e o vocabulario que o onboarding valida
     * (`Onboarding::KnownInstitutions`) e o que a prevalencia casa por string. A faculdade
     * de graduacao e outra lista: `getGraduationInstitutions` (R12-07).
     */
    getInstitutions: (query?: string): Promise<import('@/types/api').CatalogSuggestionsResponse> => {
      const params = new URLSearchParams()
      if (query) params.set('query', query)
      const suffix = params.toString() ? `?${params.toString()}` : ''
      return this.request(`/catalog/institutions${suffix}`)
    },

    /**
     * R12-07 — faculdades de graduacao (`graduation_institutions`), publico como o irmao.
     * Busca sem acento por nome, sigla ou UF; query vazia devolve `results: []`.
     */
    getGraduationInstitutions: (
      query: string,
      limit?: number
    ): Promise<import('@/types/api').CatalogSuggestionsResponse> => {
      const params = new URLSearchParams()
      params.set('query', query)
      if (limit) params.set('limit', String(limit))
      return this.request(`/catalog/graduation_institutions?${params.toString()}`)
    },

    /**
     * Especialidades do CATALOGO — so as que tem subespecialidade abaixo, nao as 99
     * linhas de `specialties`. O `id` continua sendo o hash de catalogo (string), nao
     * o id numerico da tabela: e ele que identifica o nivel 1 na arvore publica.
     */
    getSpecialties: (): Promise<{
      success: boolean
      specialties: Array<{ id: string; name: string; themes_count: number; usage_count: number }>
    }> => this.request('/catalog/specialties'),
  }

  // User endpoints
  users = {
    getSubscription: (): Promise<{ success: boolean; data: unknown }> =>
      this.request('/users/subscription'),

    startTrial: (): Promise<{ success: boolean; message: string }> =>
      this.request('/users/start_trial', { method: 'POST' }),

    updateProfile: (data: Partial<User>): Promise<{ success: boolean; user: User }> =>
      this.request('/users/1', {
        method: 'PATCH',
        body: JSON.stringify({ user: data }),
      }),

    // r7-13: opt-in do MatchHunter. O servidor carimba `discoverable_answered_at`.
    updateDiscoverable: (discoverable: boolean): Promise<{ success: boolean; user: User }> =>
      this.request('/users/1', {
        method: 'PATCH',
        body: JSON.stringify({ user: { discoverable } }),
      }),

    // r7-15: pelo `requestMultipart` — manda o Bearer (o cookie cross-origin nao chega em
    // Safari nem no compose local) e le a mensagem de erro do campo `error` da API.
    uploadAvatar: (file: File): Promise<{ success: boolean; avatar_url: string; user: User; message: string }> => {
      const formData = new FormData()
      formData.append('avatar', file)

      return this.requestMultipart('/users/upload_avatar', formData)
    },

    removeAvatar: (): Promise<{ success: boolean; user: User; message: string }> =>
      this.request('/users/remove_avatar', { method: 'DELETE' }),
  }

  // Documentos legais e aceite (LGPD-02)
  legal = {
    /** Publico: versao publicada de cada tipo, com corpo e hash. */
    documents: (kind?: string): Promise<LegalDocumentsResponse> =>
      this.request(`/legal/documents${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`),

    acceptances: (): Promise<LegalAcceptancesResponse> => this.request('/legal/acceptances'),

    /** Grava o aceite dos documentos informados. 422 se o hash nao bate com o publicado. */
    accept: (
      method: LegalAcceptanceMethod,
      acceptances: LegalAcceptanceItem[],
    ): Promise<CreateLegalAcceptancesResponse> =>
      this.request('/legal/acceptances', {
        method: 'POST',
        body: JSON.stringify({ method, acceptances }),
      }),

    /** So documento opcional; obrigatorio volta 403. */
    revoke: (id: ResourceId): Promise<RevokeLegalAcceptanceResponse> =>
      this.request(`/legal/acceptances/${id}`, { method: 'DELETE' }),
  }

  // Onboarding endpoints (formulario de entrada 2.0)
  onboarding = {
    get: (): Promise<OnboardingResponse> => this.request('/onboarding'),

    /**
     * `keepalive` so deve ser usado no flush de descarga da pagina (pagehide/visibilitychange):
     * sem ele o navegador cancela a requisicao em voo ao descartar o documento. Fora desse
     * caso ele nao vale a pena — o keepalive tem teto de 64 KB por requisicao.
     */
    save: (payload: SaveOnboardingRequest, keepalive = false): Promise<OnboardingResponse> =>
      this.request('/onboarding', {
        method: 'PATCH',
        body: JSON.stringify({ onboarding: payload }),
        keepalive,
      }),

    complete: (): Promise<CompleteOnboardingResponse> =>
      this.request('/onboarding/complete', { method: 'POST' }),
  }

  // Primeira Carga de Dados (convite pos-onboarding + entrada por Configuracoes)
  initialDataLoad = {
    /** So o estado — o convite e o card de Configuracoes nao precisam da arvore. */
    getState: (): Promise<import('@/types/api').InitialDataLoadStateResponse> =>
      this.request('/initial_data_load'),

    getTree: (): Promise<import('@/types/api').InitialDataLoadTreeResponse> =>
      this.request('/initial_data_load/tree'),

    /**
     * `topicIds` sao `hash_id` de linhas de `topics` (o nivel 3 do catalogo) — os
     * unicos ids que a API aceita. Enviar id de especialidade ou de subespecialidade
     * volta 422, e id NUMERICO tambem: o `hash_id` e a identidade publica da arvore e
     * e o que `getTree` devolve em todos os niveis.
     */
    submit: (topicIds: readonly string[]): Promise<import('@/types/api').InitialDataLoadCreateResponse> =>
      this.request('/initial_data_load', {
        method: 'POST',
        body: JSON.stringify({ initial_data_load: { topic_ids: topicIds } }),
      }),

    dismiss: (): Promise<import('@/types/api').InitialDataLoadDismissResponse> =>
      this.request('/initial_data_load/dismiss', { method: 'POST' }),
  }

  // Subscription endpoints
  subscriptions = {
    getAll: (): Promise<{ success: boolean } & import('@/types/api').SubscriptionResponse> =>
      this.request('/subscriptions'),

    getStatus: (): Promise<{ success: boolean } & import('@/types/api').SubscriptionStatusResponse> =>
      this.request('/subscriptions/status'),

    startTrial: (): Promise<{ success: boolean; message: string; trial: import('@/types/api').TrialInfo }> =>
      this.request('/subscriptions/start_trial', { method: 'POST' }),
  }

  // Payment endpoints
  // r7-19: `payments.create` saiu — ele mandava numero de cartao e CVV para o nosso
  // backend. A compra e `checkout.createOrder` + fatura do Asaas.
  payments = {
    // `asaas_plan_id` passa a acompanhar a validacao: com ele o servidor calcula o
    // desconto sobre o preco do BANCO e o `final_amount` exibido nao pode divergir
    // do que sera cobrado. `amount` continua no payload apenas como compatibilidade
    // para chamadas sem plano — ele nunca decide o valor cobrado.
    validateCoupon: (coupon_code: string, amount: number, email: string, asaas_plan_id?: number): Promise<{
      valid: boolean; error?: string
      coupon?: { code: string; name: string; discount_type: string; free_pass: boolean }
      discount_amount?: number; final_amount?: number
    }> =>
      this.request('/payments/validate_coupon', {
        method: 'POST',
        body: JSON.stringify({ coupon_code, amount, email, asaas_plan_id }),
      }),

    getPlans: (): Promise<{ success: boolean; plans: import('@/types/api').PublicPlan[] }> =>
      this.request('/payments/plans'),
  }

  // r7-19 — checkout da plataforma (`/checkout?produto=&cupom=`). Anonimo. O valor sai
  // SEMPRE da cotacao do servidor; `createOrder` so manda identificadores e dados do
  // comprador, e devolve a URL da fatura do Asaas, onde o cartao e digitado.
  checkout = {
    /** r7-20: produto publico (so ativo; 404 `product_not_found`). */
    product: (id: string | number): Promise<CheckoutProductResponse> =>
      this.request(`/checkout/products/${encodeURIComponent(String(id))}`),

    quote: (params: { produto: string; cupom?: string }): Promise<CheckoutQuoteResponse> => {
      const search = new URLSearchParams({ produto: params.produto })
      if (params.cupom) search.set('cupom', params.cupom)
      return this.request(`/checkout/quote?${search.toString()}`)
    },

    createOrder: (
      order: CheckoutOrderRequest,
      options?: IdempotentRequestOptions,
    ): Promise<CheckoutOrderResponse> =>
      this.request('/checkout/orders', {
        method: 'POST',
        body: JSON.stringify({ order }),
        headers: idempotencyHeaders(options),
      }),
  }

  // VENDA-03 — pagina publica do link de venda (`/pagar/:token`). Anonimo; o token e a
  // credencial. `confirm` so manda os dados do cliente: valores saem do link no servidor.
  salesLinks = {
    show: (token: string): Promise<import('@/types/api').PublicSalesLinkResponse> =>
      this.request(`/sales_links/${encodeURIComponent(token)}`),

    confirm: (
      token: string,
      buyer: import('@/types/api').SalesLinkBuyerPayload,
    ): Promise<import('@/types/api').PublicSalesLinkResponse> =>
      this.request(`/sales_links/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: JSON.stringify({ buyer }),
      }),
  }

  // Calendar Events endpoints
  events = {
    list: (startDate?: string, endDate?: string): Promise<{ success: boolean; events: CalendarEvent[] }> => {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      const query = params.toString()
      return this.request(`/events${query ? `?${query}` : ''}`)
    },

    get: (id: ResourceId): Promise<{ success: boolean; event: CalendarEvent }> =>
      this.request(`/events/${id}`),

    create: (event: CreateEventData): Promise<{ success: boolean; event: CalendarEvent; message: string }> =>
      this.request('/events', {
        method: 'POST',
        body: JSON.stringify({ event }),
      }),

    update: (id: ResourceId, event: Partial<CreateEventData>): Promise<{ success: boolean; event: CalendarEvent; message: string }> =>
      this.request(`/events/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ event }),
      }),

    delete: (id: ResourceId): Promise<{ success: boolean; message: string }> =>
      this.request(`/events/${id}`, { method: 'DELETE' }),

    toggleStatus: (id: ResourceId): Promise<{ success: boolean; event: CalendarEvent }> =>
      this.request(`/events/${id}/toggle_status`, { method: 'PATCH' }),

    upcoming: (): Promise<{ success: boolean; events: CalendarEvent[] }> =>
      this.request('/events/upcoming'),

    today: (): Promise<{ success: boolean; events: CalendarEvent[] }> =>
      this.request('/events/today'),

    duplicateWeek: (sourceDate: string): Promise<{ success: boolean; events: CalendarEvent[]; count: number; message: string }> =>
      this.request('/events/duplicate_week', {
        method: 'POST',
        body: JSON.stringify({ source_date: sourceDate }),
      }),
  }

  // Study Goals endpoints
  // Meta TED semanal (r5-06): disponivel / meta diaria / realizado.
  ted = {
    /** `weekOffset` 0 = semana corrente, -1 = anterior... */
    week: (weekOffset = 0): Promise<TedWeekPlan> =>
      this.request(`/ted/week${weekOffset ? `?week_offset=${weekOffset}` : ''}`),
    /** Cria ou atualiza a meta da semana (padrao: corrente). Minutos. */
    setGoal: (tedGoalMin: number, weekStart?: string): Promise<SetTedGoalResponse> =>
      this.request('/ted/goal', {
        method: 'PUT',
        body: JSON.stringify({ ted_goal_min: tedGoalMin, week_start: weekStart }),
      }),
  }

  goals = {
    list: (): Promise<{ success: boolean; goals: Goal[]; current_goal: Goal | null }> =>
      this.request('/goals'),

    getCurrent: (): Promise<{ success: boolean; goal: Goal | null; progress: GoalProgress | null }> =>
      this.request('/goals/current'),

    create: (tedGoal: number): Promise<{ success: boolean; goal: Goal }> =>
      this.request('/goals', {
        method: 'POST',
        body: JSON.stringify({ goal: { ted_goal: tedGoal } }),
      }),

    update: (id: number, tedGoal: number): Promise<{ success: boolean; goal: Goal }> =>
      this.request(`/goals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ goal: { ted_goal: tedGoal } }),
      }),

    delete: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/goals/${id}`, { method: 'DELETE' }),
  }

  // Admin endpoints
  admin = {
    // Documentos legais (LGPD-04). Publicada/arquivada nunca e editada: so rascunho.
    legalDocuments: {
      index: (): Promise<AdminLegalDocumentsIndexResponse> => this.request('/admin/legal_documents'),
      versions: (kind: LegalDocumentKind, status?: LegalDocumentStatus): Promise<AdminLegalDocumentVersionsResponse> =>
        this.request(`/admin/legal_documents?kind=${kind}${status ? `&status=${status}` : ''}`),
      get: (id: ResourceId): Promise<AdminLegalDocumentResponse> => this.request(`/admin/legal_documents/${id}`),
      /** Abre rascunho copiando a versao publicada. 409 se ja existe um. */
      createDraft: (kind: LegalDocumentKind): Promise<AdminLegalDocumentResponse> =>
        this.request('/admin/legal_documents', {
          method: 'POST',
          body: JSON.stringify({ legal_document: { kind } }),
        }),
      updateDraft: (id: ResourceId, patch: AdminLegalDocumentDraftPatch): Promise<AdminLegalDocumentResponse> =>
        this.request(`/admin/legal_documents/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ legal_document: patch }),
        }),
      /** Congela o rascunho como nova versao e cobra re-aceite de todos os usuarios. */
      publish: (id: ResourceId): Promise<AdminLegalDocumentResponse> =>
        this.request(`/admin/legal_documents/${id}/publish`, { method: 'POST' }),
      deleteDraft: (id: ResourceId): Promise<{ success: boolean; message: string }> =>
        this.request(`/admin/legal_documents/${id}`, { method: 'DELETE' }),
    },

    // Dashboard
    getDashboard: (): Promise<{
      success: boolean
      stats: AdminStats
      recent_users: AdminUserBrief[]
      recent_subscriptions: AdminSubscription[]
      charts: { signups_by_day: Record<string, number>; subscriptions_by_day: Record<string, number> }
    }> => this.request('/admin/dashboard'),

    // Users
    getUsers: (params?: { page?: number; per_page?: number; search?: string; role_id?: number; role?: AdminRoleFilter; status?: string }): Promise<{
      success: boolean
      users: AdminUser[]
      pagination: Pagination
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.role_id) searchParams.set('role_id', params.role_id.toString())
      if (params?.role) searchParams.set('role', params.role)
      if (params?.status) searchParams.set('status', params.status)
      return this.request(`/admin/users?${searchParams.toString()}`)
    },

    getUser: (id: number): Promise<{ success: boolean; user: AdminUserDetail }> =>
      this.request(`/admin/users/${id}`),

    // Spec 09 — cadastro manual de usuario pelo admin, com concessao opcional no
    // MESMO ato (o servidor grava os dois numa unica transacao).
    //
    // `role_id` NAO existe neste payload de proposito: o papel vai como NOME e o
    // servidor valida contra a lista permitida (student/instructor). Promover a
    // admin continua sendo `changeRole`, que e outra rota.
    createUser: (payload: {
      user: AdminCreateUserPayload
      grant?: AdminGrantPayload
    }): Promise<{ success: boolean; message: string; user: AdminUserDetail }> =>
      this.request('/admin/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateUser: (id: number, data: Partial<AdminUser>): Promise<{ success: boolean; message: string; user: AdminUser }> =>
      this.request(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ user: data }),
      }),

    deleteUser: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/users/${id}`, { method: 'DELETE' }),

    extendTrial: (id: number, days: number): Promise<{ success: boolean; message: string; trial_ends_at: string }> =>
      this.request(`/admin/users/${id}/extend_trial`, {
        method: 'POST',
        body: JSON.stringify({ days }),
      }),

    changeRole: (id: number, role: string): Promise<{ success: boolean; message: string; user: AdminUser }> =>
      this.request(`/admin/users/${id}/change_role`, {
        method: 'POST',
        body: JSON.stringify({ role }),
      }),

    quickSearchUsers: (query: string): Promise<{
      success: boolean
      results: Array<{ id: number; title: string; subtitle: string; type: string; meta: string }>
    }> => this.request(`/admin/users/quick_search?q=${encodeURIComponent(query)}`),

    quickSearchSubscriptions: (query: string): Promise<{
      success: boolean
      results: Array<{ id: number; title: string; subtitle: string; type: string; meta: string }>
    }> => this.request(`/admin/subscriptions/quick_search?q=${encodeURIComponent(query)}`),

    quickSearchCourses: (query: string): Promise<{
      success: boolean
      results: Array<{ id: number; title: string; subtitle: string; type: string; meta: string }>
    }> => this.request(`/admin/courses/quick_search?q=${encodeURIComponent(query)}`),

    quickSearchCoupons: (query: string): Promise<{
      success: boolean
      results: Array<{ id: number; title: string; subtitle: string; type: string; meta: string }>
    }> => this.request(`/admin/coupons/quick_search?q=${encodeURIComponent(query)}`),

    // Subscriptions
    getSubscriptions: (params?: { page?: number; per_page?: number; search?: string; status?: string; plan_id?: number }): Promise<{
      success: boolean
      subscriptions: AdminSubscription[]
      pagination: Pagination
      // Spec 09: `scheduled` (concessao com inicio futuro) saiu de dentro de `active`,
      // e `manual_grants` conta quantas das assinaturas sao concessao comercial.
      stats: {
        total: number; active: number; scheduled: number; expired: number
        expiring_this_week: number; by_plan: Record<string, number>; manual_grants: number
      }
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.status) searchParams.set('status', params.status)
      if (params?.plan_id) searchParams.set('plan_id', params.plan_id.toString())
      return this.request(`/admin/subscriptions?${searchParams.toString()}`)
    },

    getSubscription: (id: number): Promise<{ success: boolean; subscription: AdminSubscriptionDetail }> =>
      this.request(`/admin/subscriptions/${id}`),

    extendSubscription: (id: number, days: number): Promise<{ success: boolean; message: string; subscription: AdminSubscription }> =>
      this.request(`/admin/subscriptions/${id}/extend`, {
        method: 'POST',
        body: JSON.stringify({ days }),
      }),

    cancelSubscription: (id: number): Promise<{ success: boolean; message: string; subscription: AdminSubscription }> =>
      this.request(`/admin/subscriptions/${id}/cancel`, { method: 'POST' }),

    // Spec 09 — os planos concediveis. Antes desta rota a tela de detalhe mandava
    // `plan_id: 1` fixo, concedendo sempre o plano de id 1. Planos sem
    // `stripe_price_id` nao sao devolvidos: conceder um deles cria assinatura orfa.
    getSubscriptionPlans: (): Promise<{ success: boolean; plans: AdminSubscriptionPlan[] }> =>
      this.request('/admin/subscriptions/plans'),

    // Analytics
    getAnalytics: (): Promise<{
      success: boolean
      overview: { total_users: number; active_users_30d: number; conversion_rate: number; churn_rate: number }
      user_growth: { daily: Record<string, number>; weekly: Record<string, number>; monthly: Record<string, number> }
      subscription_metrics: { total_subscribed: number; trial_to_paid: { rate: number; total_trials: number; converted: number }; avg_subscription_length: number; plan_distribution: Record<string, number> }
      engagement: { daily_active_users: number; weekly_active_users: number; monthly_active_users: number; study_sessions_today: number; quizzes_completed_today: number }
      content: { total_quizzes: number; total_questions: number; quiz_attempts_30d: number; avg_quiz_score: number }
    }> => this.request('/admin/analytics'),

    // Spec 09 decisao 4.2 — `includeManual` (desligado por padrao) soma as concessoes
    // manuais ao total e as injeta em `recent_payments` com `source: 'manual'`.
    // Desligado, a resposta e exatamente a de antes.
    getRevenue: (params?: { includeManual?: boolean }): Promise<{
      success: boolean
      summary: { total_this_month: number; total_last_month: number; total_this_year: number; total_last_year: number; growth_rate: number; yearly_growth_rate: number }
      monthly: Array<{ month: string; revenue: number }>
      by_plan: Record<string, number>
      recent_payments: AdminRevenuePayment[]
      include_manual: boolean
      manual_grants_total_this_month: number
      /** R10-16: vinculos manuais de plano no mes. Nao tem valor: e contado, nunca somado. */
      manual_plan_links_this_month: number
      source: AdminRevenueSource
    }> => this.request(`/admin/analytics/revenue${params?.includeManual ? '?include_manual=true' : ''}`),

    // Mentorships
    getMentorships: (params?: { page?: number; per_page?: number }): Promise<{
      success: boolean
      mentorships: AdminMentorship[]
      pagination: Pagination
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      return this.request(`/admin/mentorships?${searchParams.toString()}`)
    },

    getInstructors: (): Promise<{
      success: boolean
      instructors: Array<{ id: number; name: string; email: string }>
    }> => this.request('/admin/mentorships/instructors'),

    getAvailableStudents: (params?: { search?: string; exclude_instructor_id?: number }): Promise<{
      success: boolean
      students: Array<{ id: number; name: string; email: string }>
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.search) searchParams.set('search', params.search)
      if (params?.exclude_instructor_id) searchParams.set('exclude_instructor_id', params.exclude_instructor_id.toString())
      return this.request(`/admin/mentorships/students?${searchParams.toString()}`)
    },

    getInstructorMentees: (instructorId: number): Promise<{
      success: boolean
      instructor: { id: number; name: string; email: string }
      mentees: Array<{ id: number; name: string; email: string; created_at: string }>
    }> => this.request(`/admin/mentorships/instructor/${instructorId}`),

    createMentorship: (instructorId: number, studentId: number): Promise<{
      success: boolean
      message: string
      mentorship: AdminMentorship
    }> => this.request('/admin/mentorships', {
      method: 'POST',
      body: JSON.stringify({ instructor_id: instructorId, student_id: studentId }),
    }),

    deleteMentorship: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/mentorships/${id}`, { method: 'DELETE' }),

    bulkAssignMentees: (instructorId: number, studentIds: number[]): Promise<{
      success: boolean
      message: string
      created_count: number
    }> => this.request('/admin/mentorships/bulk_assign', {
      method: 'POST',
      body: JSON.stringify({ instructor_id: instructorId, student_ids: studentIds }),
    }),

    // R08-25: cobertura de ferias/plantao (so o admin cria e cancela; 422 com `errors`).
    getMentorCoverages: (mentorId?: number): Promise<AdminMentorCoveragesResponse> =>
      this.request(`/admin/mentor_coverages${mentorId ? `?mentor_id=${mentorId}` : ''}`),

    createMentorCoverage: (payload: AdminMentorCoveragePayload): Promise<AdminMentorCoverageResponse> =>
      this.request('/admin/mentor_coverages', { method: 'POST', body: JSON.stringify(payload) }),

    cancelMentorCoverage: (id: number): Promise<AdminMentorCoverageResponse> =>
      this.request(`/admin/mentor_coverages/${id}/cancel`, { method: 'POST' }),

    // R08-26: prazos do edital por instituicao (so o admin grava; 422 com `errors`).
    // Sem `past`, so os que ainda nao venceram.
    getExamDeadlines: (params: { institutionId?: number; past?: boolean } = {}): Promise<AdminExamDeadlinesResponse> => {
      const qs = new URLSearchParams()
      if (params.institutionId) qs.set('institution_id', String(params.institutionId))
      if (params.past) qs.set('past', '1')
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/exam_deadlines${suffix}`)
    },

    createExamDeadline: (payload: AdminExamDeadlinePayload): Promise<AdminExamDeadlineResponse> =>
      this.request('/admin/exam_deadlines', { method: 'POST', body: JSON.stringify({ exam_deadline: payload }) }),

    updateExamDeadline: (id: number, payload: Partial<AdminExamDeadlinePayload>): Promise<AdminExamDeadlineResponse> =>
      this.request(`/admin/exam_deadlines/${id}`, { method: 'PATCH', body: JSON.stringify({ exam_deadline: payload }) }),

    deleteExamDeadline: (id: number): Promise<{ success: boolean }> =>
      this.request(`/admin/exam_deadlines/${id}`, { method: 'DELETE' }),

    // MH9-01: chaves de API dos agentes externos. O token so vem no `create`.
    getApiKeys: (): Promise<AdminApiKeysResponse> => this.request('/admin/api_keys'),

    createApiKey: (payload: AdminApiKeyPayload): Promise<AdminApiKeyCreatedResponse> =>
      this.request('/admin/api_keys', { method: 'POST', body: JSON.stringify({ api_key: payload }) }),

    updateApiKey: (id: number, payload: Partial<AdminApiKeyPayload>): Promise<AdminApiKeyResponse> =>
      this.request(`/admin/api_keys/${id}`, { method: 'PATCH', body: JSON.stringify({ api_key: payload }) }),

    revokeApiKey: (id: number): Promise<AdminApiKeyResponse> =>
      this.request(`/admin/api_keys/${id}/revoke`, { method: 'POST' }),

    // R11-13/MH9-07: eventos que o webhook de saida dispara, com exemplo do corpo.
    getWebhookEvents: (): Promise<WebhookEventsResponse> => this.request('/admin/api_keys/webhook_events'),

    // R15.1-04: rotas externas que o Hermes chama, com exemplo e schema.
    getExternalRoutes: (): Promise<ExternalRoutesResponse> => this.request('/admin/api_keys/external_routes'),

    // R11-13: gera um segredo novo para o webhook de saida; o antigo para de valer na hora.
    rotateApiKeyWebhookSecret: (id: number): Promise<AdminApiKeyResponse> =>
      this.request(`/admin/api_keys/${id}/rotate_webhook_secret`, { method: 'POST' }),

    // R10-15: perfis de acesso do admin (so admin sem perfil gerencia e atribui).
    getAdminProfiles: (): Promise<import('@/types/api').AdminProfilesResponse> =>
      this.request('/admin/admin_profiles'),

    createAdminProfile: (payload: import('@/types/api').AdminProfilePayload): Promise<import('@/types/api').AdminProfileResponse> =>
      this.request('/admin/admin_profiles', { method: 'POST', body: JSON.stringify({ admin_profile: payload }) }),

    updateAdminProfile: (
      id: number,
      payload: Partial<import('@/types/api').AdminProfilePayload>,
    ): Promise<import('@/types/api').AdminProfileResponse> =>
      this.request(`/admin/admin_profiles/${id}`, { method: 'PATCH', body: JSON.stringify({ admin_profile: payload }) }),

    deleteAdminProfile: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/admin_profiles/${id}`, { method: 'DELETE' }),

    /** `null` tira o perfil: o admin volta a ter acesso total. */
    assignAdminProfile: (
      userId: number,
      adminProfileId: number | null,
    ): Promise<import('@/types/api').AdminProfileAssignmentResponse> =>
      this.request(`/admin/users/${userId}/admin_profile`, {
        method: 'PATCH',
        body: JSON.stringify({ admin_profile_id: adminProfileId }),
      }),

    // MH9-13: assistente de IA — config versionada (restaurar = versao nova) e liberacao por padrinho.
    getAssistantSettings: (): Promise<AdminAssistantSettingsResponse> => this.request('/admin/assistant_settings'),

    updateAssistantSettings: (payload: AdminAssistantSettingsPayload): Promise<AdminAssistantSettingsResponse> =>
      this.request('/admin/assistant_settings', {
        method: 'PATCH',
        body: JSON.stringify({ assistant_settings: payload }),
      }),

    restoreAssistantSettings: (versionId: number): Promise<AdminAssistantSettingsResponse> =>
      this.request(`/admin/assistant_settings/versions/${versionId}/restore`, { method: 'POST' }),

    setInstructorAssistant: (userId: number, enabled: boolean): Promise<AdminAssistantInstructorResponse> =>
      this.request(`/admin/assistant_settings/instructors/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ assistant_enabled: enabled }),
      }),

    // R08-25: contrato e documentos de um padrinho.
    getMentorDocuments: (mentorId: number): Promise<MentorDocumentsResponse> =>
      this.request(`/admin/mentor_documents?mentor_id=${mentorId}`),

    uploadMentorDocument: (
      mentorId: number,
      kind: MentorDocumentKind,
      title: string,
      file: File
    ): Promise<MentorDocumentResponse> => {
      const formData = new FormData()
      formData.append('mentor_id', String(mentorId))
      formData.append('kind', kind)
      formData.append('title', title)
      formData.append('file', file)
      return this.requestMultipart('/admin/mentor_documents', formData)
    },

    deleteMentorDocument: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/mentor_documents/${id}`, { method: 'DELETE' }),

    downloadMentorDocument: (id: number): Promise<Blob> => this.requestBlob(`/admin/mentor_documents/${id}/file`),

    // Quizzes
    getQuizzes: (params?: { page?: number; per_page?: number }): Promise<{
      success: boolean
      quizzes: AdminQuiz[]
      quiz_types: AdminQuizTypeOption[]
      categories: AdminQuizCategoryOption[]
      pagination: Pagination
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      return this.request(`/admin/quizzes?${searchParams.toString()}`)
    },

    getQuiz: (id: number): Promise<{ success: boolean; quiz: AdminQuizDetail }> =>
      this.request(`/admin/quizzes/${id}`),

    createQuiz: (data: { title: string; quiz_type: string; category_id: number }): Promise<{ success: boolean; message: string; quiz: AdminQuiz }> =>
      this.request('/admin/quizzes', {
        method: 'POST',
        body: JSON.stringify({ quiz: data }),
      }),

    updateQuiz: (id: number, data: Partial<AdminQuiz>): Promise<{ success: boolean; message: string; quiz: AdminQuiz }> =>
      this.request(`/admin/quizzes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ quiz: data }),
      }),

    deleteQuiz: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/quizzes/${id}`, { method: 'DELETE' }),

    addQuizQuestion: (quizId: number, question: { title: string; text: string; explanation?: string }, answers: Array<{ text: string; correct: boolean }>): Promise<{ success: boolean; message: string; question: AdminQuestion }> =>
      this.request(`/admin/quizzes/${quizId}/add_question`, {
        method: 'POST',
        body: JSON.stringify({ question, answers }),
      }),

    removeQuizQuestion: (quizId: number, questionId: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/quizzes/${quizId}/questions/${questionId}`, { method: 'DELETE' }),

    // Courses
    getCourses: (params?: { page?: number; per_page?: number; search?: string; status?: string }): Promise<{
      success: boolean
      courses: AdminCourse[]
      pagination: Pagination
      stats: { total: number; active: number; draft: number }
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.status) searchParams.set('status', params.status)
      return this.request(`/admin/courses?${searchParams.toString()}`)
    },

    getCourse: (id: number): Promise<{ success: boolean; course: AdminCourseDetail }> =>
      this.request(`/admin/courses/${id}`),

    createCourse: (data: Partial<AdminCourse>): Promise<{ success: boolean; message: string; course: AdminCourse }> =>
      this.request('/admin/courses', {
        method: 'POST',
        body: JSON.stringify({ course: data }),
      }),

    updateCourse: (id: number, data: Partial<AdminCourse>): Promise<{ success: boolean; message: string; course: AdminCourse }> =>
      this.request(`/admin/courses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ course: data }),
      }),

    deleteCourse: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/courses/${id}`, { method: 'DELETE' }),

    toggleCourseStatus: (id: number): Promise<{ success: boolean; message: string; course: AdminCourse }> =>
      this.request(`/admin/courses/${id}/toggle_status`, { method: 'POST' }),

    // Coupons
    getCoupons: (params?: { page?: number; per_page?: number; search?: string; active?: boolean }): Promise<{
      success: boolean
      coupons: AdminCoupon[]
      pagination: Pagination
      stats: { total: number; active: number; expired: number; exhausted: number; total_used: number }
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.active !== undefined) searchParams.set('active', params.active.toString())
      return this.request(`/admin/coupons?${searchParams.toString()}`)
    },

    getCoupon: (id: number): Promise<{ success: boolean; coupon: AdminCouponDetail }> =>
      this.request(`/admin/coupons/${id}`),

    createCoupon: (data: AdminCouponPayload): Promise<{ success: boolean; message: string; coupon: AdminCoupon }> =>
      this.request('/admin/coupons', {
        method: 'POST',
        body: JSON.stringify({ coupon: data }),
      }),

    updateCoupon: (id: number, data: AdminCouponPayload): Promise<{ success: boolean; message: string; coupon: AdminCoupon }> =>
      this.request(`/admin/coupons/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ coupon: data }),
      }),

    deleteCoupon: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/coupons/${id}`, { method: 'DELETE' }),

    toggleCouponActive: (id: number): Promise<{ success: boolean; message: string; coupon: AdminCoupon }> =>
      this.request(`/admin/coupons/${id}/toggle_active`, { method: 'POST' }),

    // r7-20 — produtos do checkout (sem exclusao: desativar)
    getProducts: (): Promise<{ success: boolean; products: AdminProduct[] }> =>
      this.request('/admin/products'),

    createProduct: (data: AdminProductPayload): Promise<{ success: boolean; message: string; product: AdminProduct }> =>
      this.request('/admin/products', { method: 'POST', body: JSON.stringify({ product: data }) }),

    // MH14-12: `affected_installments` = pedidos recalculados quando a regra de validade mudou.
    updateProduct: (id: number, data: AdminProductPayload): Promise<{ success: boolean; message: string; product: AdminProduct; affected_installments?: number }> =>
      this.request(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify({ product: data }) }),

    toggleProductActive: (id: number): Promise<{ success: boolean; message: string; product: AdminProduct }> =>
      this.request(`/admin/products/${id}/toggle_active`, { method: 'POST' }),

    // r7-25 — na tela, "produto" virou "Plano"; o endpoint continua `/admin/products`.
    getProduct: (id: number): Promise<{ success: boolean; product: AdminProduct }> =>
      this.request(`/admin/products/${id}`),

    /** Catalogo de features que um plano pode liberar (fonte unica no backend). */
    getPlanFeatureCatalog: (): Promise<import('@/types/api').PlanFeatureCatalogResponse> =>
      this.request('/admin/products/features'),

    /** R12-02: contratos ativos para o select "Contrato" do plano (secao de planos). */
    getPlanContractTemplates: (): Promise<import('@/types/api').PlanContractTemplateOptionsResponse> =>
      this.request('/admin/products/contract_templates'),

    /** Vinculos de alunos com planos cadastrados (tela /admin/subscriptions). */
    getPlanLinks: (
      params: import('@/types/api').AdminPlanLinksParams,
    ): Promise<import('@/types/api').AdminPlanLinksResponse> => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') searchParams.set(key, String(value))
      })
      return this.request(`/admin/plan_links?${searchParams.toString()}`)
    },

    // r7-25 — vinculo manual usuario -> plano (sem cobranca, com validade)
    getUserPlans: (userId: number): Promise<import('@/types/api').AdminUserPlansResponse> =>
      this.request(`/admin/users/${userId}/plans`),

    // MH13-40: `send_welcome_email: false` vincula sem o e-mail de boas-vindas do plano.
    grantUserPlan: (
      userId: number,
      data: import('@/types/api').AdminGrantUserPlanPayload,
    ): Promise<import('@/types/api').AdminGrantUserPlanResponse> =>
      this.request(`/admin/users/${userId}/plans`, { method: 'POST', body: JSON.stringify({ plan: data }) }),

    revokeUserPlan: (
      userId: number,
      linkId: number,
    ): Promise<{ success: boolean; message: string; plan: import('@/types/api').AdminUserPlanLink }> =>
      this.request(`/admin/users/${userId}/plans/${linkId}`, { method: 'DELETE' }),

    /** Historico de edicao do usuario (admin, o proprio aluno e o sistema), 20 por pagina. */
    getUserHistory: (userId: number, page = 1): Promise<import('@/types/api').AdminUserHistoryResponse> =>
      this.request(`/admin/users/${userId}/history?page=${page}`),

    // Announcements
    getAnnouncements: (params?: { page?: number; per_page?: number; active?: boolean; audience?: string }): Promise<{
      success: boolean
      announcements: AdminAnnouncement[]
      pagination: Pagination
      stats: { total: number; active: number; expired: number }
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.active !== undefined) searchParams.set('active', params.active.toString())
      if (params?.audience) searchParams.set('audience', params.audience)
      return this.request(`/admin/announcements?${searchParams.toString()}`)
    },

    getAnnouncement: (id: number): Promise<{ success: boolean; announcement: AdminAnnouncement }> =>
      this.request(`/admin/announcements/${id}`),

    createAnnouncement: (data: Partial<AdminAnnouncement>): Promise<{ success: boolean; message: string; announcement: AdminAnnouncement }> =>
      this.request('/admin/announcements', {
        method: 'POST',
        body: JSON.stringify({ announcement: data }),
      }),

    updateAnnouncement: (id: number, data: Partial<AdminAnnouncement>): Promise<{ success: boolean; message: string; announcement: AdminAnnouncement }> =>
      this.request(`/admin/announcements/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ announcement: data }),
      }),

    deleteAnnouncement: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/announcements/${id}`, { method: 'DELETE' }),

    toggleAnnouncementActive: (id: number): Promise<{ success: boolean; message: string; announcement: AdminAnnouncement }> =>
      this.request(`/admin/announcements/${id}/toggle_active`, { method: 'POST' }),

    // ---------- R5: solicitacoes de correcao de catalogo ----------
    // Fila de aulas/topicos que alunos informaram e o catalogo nao tem. So leitura e
    // mudanca de status: o texto informado e evidencia do pedido, nao se edita.

    getCatalogCorrectionRequests: (params?: {
      status?: import('@/types/api').CatalogCorrectionStatus
      request_type?: 'lesson' | 'topic'
      search?: string
    }): Promise<{
      success: boolean
      requests: import('@/types/api').CatalogCorrectionRequest[]
      stats: import('@/types/api').CatalogCorrectionRequestStats
    }> => {
      const qs = new URLSearchParams()
      if (params?.status) qs.set('status', params.status)
      if (params?.request_type) qs.set('request_type', params.request_type)
      if (params?.search) qs.set('search', params.search)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/catalog_correction_requests${suffix}`)
    },

    // r11-09: `resolution: 'student_only'` e o "deixar so para este aluno".
    resolveCatalogCorrectionRequest: (
      id: number,
      adminNotes?: string,
      resolution?: import('@/types/api').CatalogCorrectionResolution,
    ): Promise<{
      success: boolean; message: string; request: import('@/types/api').CatalogCorrectionRequest
    }> =>
      this.request(`/admin/catalog_correction_requests/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes, resolution }),
      }),

    // r11-09: vincula os registros do termo a um topico do catalogo (existente ou novo).
    linkCatalogCorrectionRequest: (
      id: number,
      payload: import('@/types/api').CatalogCorrectionLinkPayload,
    ): Promise<{
      success: boolean
      message: string
      request: import('@/types/api').CatalogCorrectionRequest
      linked_records: number
      resolved_requests: number
    }> =>
      this.request(`/admin/catalog_correction_requests/${id}/link_topic`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    rejectCatalogCorrectionRequest: (id: number, adminNotes?: string): Promise<{
      success: boolean; message: string; request: import('@/types/api').CatalogCorrectionRequest
    }> =>
      this.request(`/admin/catalog_correction_requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes }),
      }),

    // ---------- R10-20: pedidos de prova dos alunos ----------
    // Aceitar/recusar vale para todos os pendentes da mesma instituicao.
    getExamRequests: (params?: {
      status?: import('@/types/api').ExamRequestStatus
      search?: string
    }): Promise<import('@/types/api').AdminExamRequestsResponse> => {
      const query = new URLSearchParams()
      if (params?.status) query.set('status', params.status)
      if (params?.search) query.set('search', params.search)
      const suffix = query.toString() ? `?${query}` : ''
      return this.request(`/admin/exam_requests${suffix}`)
    },

    // R10-05: instituicao x alunos que a priorizam x temos prova?
    getExamDemand: (params?: { selectionProcess?: string }): Promise<import('@/types/api').AdminExamDemandResponse> => {
      const query = new URLSearchParams()
      if (params?.selectionProcess) query.set('selection_process', params.selectionProcess)
      const suffix = query.toString() ? `?${query}` : ''
      return this.request(`/admin/exam_demands${suffix}`)
    },

    decideExamRequest: (
      id: number,
      decision: 'accept' | 'reject',
      adminNotes?: string,
    ): Promise<import('@/types/api').AdminExamRequestDecisionResponse> =>
      this.request(`/admin/exam_requests/${id}/${decision}`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes }),
      }),

    // ---------- Auditoria de topicos ajustados na aula ----------
    // O `:course_lesson_id` e o da AULA: a unidade de trabalho do admin e a aula.

    getLessonTopicAdjustments: (params?: {
      status?: import('@/types/api').LessonTopicAdjustmentStatus
      search?: string
    }): Promise<{
      success: boolean
      lessons: import('@/types/api').LessonTopicAdjustmentLesson[]
      stats: import('@/types/api').LessonTopicAdjustmentStats
    }> => {
      const qs = new URLSearchParams()
      if (params?.status) qs.set('status', params.status)
      if (params?.search) qs.set('search', params.search)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/lesson_topic_adjustments${suffix}`)
    },

    getLessonTopicAdjustmentDetail: (courseLessonId: number): Promise<{
      success: boolean
      course_lesson: { id: number; lesson_title: string }
      adjustments: import('@/types/api').LessonTopicAdjustmentRow[]
    }> => this.request(`/admin/lesson_topic_adjustments/${courseLessonId}`),

    reviewLessonTopicAdjustments: (courseLessonId: number): Promise<{
      success: boolean; message: string; reviewed_count: number
    }> =>
      this.request(`/admin/lesson_topic_adjustments/${courseLessonId}/review`, { method: 'POST' }),

    // ---------- Catalogo de estudo: cursinhos preparatorios ----------

    getPrepCourses: (params?: { search?: string; active?: boolean }): Promise<{
      success: boolean
      prep_courses: import('@/types/api').AdminPrepCourse[]
      stats: { total: number; active: number; lessons: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.search) qs.set('search', params.search)
      if (params?.active !== undefined) qs.set('active', String(params.active))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/prep_courses${suffix}`)
    },

    getPrepCourse: (id: number): Promise<{ success: boolean; prep_course: import('@/types/api').AdminPrepCourse }> =>
      this.request(`/admin/prep_courses/${id}`),

    createPrepCourse: (data: import('@/types/api').AdminPrepCoursePayload): Promise<{
      success: boolean; message: string; prep_course: import('@/types/api').AdminPrepCourse
    }> =>
      this.request('/admin/prep_courses', {
        method: 'POST',
        body: JSON.stringify({ prep_course: data }),
      }),

    updatePrepCourse: (id: number, data: Partial<import('@/types/api').AdminPrepCoursePayload>): Promise<{
      success: boolean; message: string; prep_course: import('@/types/api').AdminPrepCourse
    }> =>
      this.request(`/admin/prep_courses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ prep_course: data }),
      }),

    // `force` confirma a exclusao em cascata das aulas do cursinho; sem ele o
    // backend devolve 409 quando ha aulas vinculadas.
    deletePrepCourse: (id: number, force = false): Promise<{
      success: boolean; message: string; deleted_lessons: number
    }> =>
      this.request(`/admin/prep_courses/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    togglePrepCourseActive: (id: number): Promise<{
      success: boolean; message: string; prep_course: import('@/types/api').AdminPrepCourse
    }> =>
      this.request(`/admin/prep_courses/${id}/toggle_active`, { method: 'POST' }),

    // ---------- Catalogo de estudo: aulas ----------

    getCourseLessons: (params?: {
      page?: number
      per_page?: number
      search?: string
      prep_course_id?: number
      specialty_id?: number
      academic_year?: number
      active?: boolean
    }): Promise<{
      success: boolean
      course_lessons: import('@/types/api').AdminCourseLesson[]
      pagination: import('@/types/api').AdminPagination
      filters: import('@/types/api').AdminCourseLessonFilters
      stats: { total: number; filtered: number; active: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.prep_course_id) qs.set('prep_course_id', String(params.prep_course_id))
      if (params?.specialty_id) qs.set('specialty_id', String(params.specialty_id))
      if (params?.academic_year) qs.set('academic_year', String(params.academic_year))
      if (params?.active !== undefined) qs.set('active', String(params.active))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/course_lessons${suffix}`)
    },

    createCourseLesson: (data: import('@/types/api').AdminCourseLessonPayload): Promise<{
      success: boolean; message: string; course_lesson: import('@/types/api').AdminCourseLesson
    }> =>
      this.request('/admin/course_lessons', {
        method: 'POST',
        body: JSON.stringify({ course_lesson: data }),
      }),

    updateCourseLesson: (id: number, data: Partial<import('@/types/api').AdminCourseLessonPayload>): Promise<{
      success: boolean; message: string; course_lesson: import('@/types/api').AdminCourseLesson
    }> =>
      this.request(`/admin/course_lessons/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ course_lesson: data }),
      }),

    // `force` confirma a exclusao mesmo com estudos/revisoes de alunos vinculados;
    // sem ele o backend devolve 409.
    deleteCourseLesson: (id: number, force = false): Promise<{
      success: boolean; message: string; deleted_links: number
    }> =>
      this.request(`/admin/course_lessons/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // ---------- r6-32: vinculo em lote aula -> subespecialidade/topico ----------
    // Espelho de Api::V1::Admin::CourseLessonBulkLinksController. O lote so ACRESCENTA
    // vinculos; a sugestao por titulo e so proposta (ambiguo nunca vem com `topic`).

    getCourseLessonBulkLinks: (
      params?: import('@/types/api').AdminBulkLinkQuery,
    ): Promise<import('@/types/api').AdminBulkLinkListResponse> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.prep_course_id) qs.set('prep_course_id', String(params.prep_course_id))
      if (params?.specialty_id) qs.set('specialty_id', String(params.specialty_id))
      if (params?.without_topic) qs.set('without_topic', 'true')
      if (params?.suggestion) qs.set('suggestion', params.suggestion)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/course_lessons/bulk_links${suffix}`)
    },

    // `withSuggestions: false` = so as contagens (rapido); com sugestao, o casador roda aula a
    // aula — a tela pede essa parte cursinho a cursinho.
    getCourseLessonBulkLinkReport: (params?: {
      prepCourseId?: number
      withSuggestions?: boolean
    }): Promise<import('@/types/api').AdminBulkLinkReportResponse> => {
      const qs = new URLSearchParams()
      if (params?.prepCourseId) qs.set('prep_course_id', String(params.prepCourseId))
      if (params?.withSuggestions === false) qs.set('suggestions', 'false')
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/course_lessons/bulk_links/report${suffix}`)
    },

    previewCourseLessonBulkLinks: (
      items: import('@/types/api').AdminBulkLinkItem[],
    ): Promise<import('@/types/api').AdminBulkLinkResult> =>
      this.request('/admin/course_lessons/bulk_links/preview', {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),

    applyCourseLessonBulkLinks: (
      items: import('@/types/api').AdminBulkLinkItem[],
    ): Promise<import('@/types/api').AdminBulkLinkApplyResponse> =>
      this.request('/admin/course_lessons/bulk_links', {
        method: 'PATCH',
        body: JSON.stringify({ items }),
      }),

    undoCourseLessonBulkLinks: (): Promise<import('@/types/api').AdminBulkLinkUndoResponse> =>
      this.request('/admin/course_lessons/bulk_links/undo', { method: 'POST' }),

    // ---------- Provas na Integra (Medway + MedCof) ----------
    // Uma tabela so (`exams`), com a plataforma em `source`: o identificador destas
    // rotas voltou a ser o id inteiro do registro.

    getProvas: (params?: {
      page?: number
      per_page?: number
      source?: import('@/types/api').AdminProvaSource
      year?: number
      exam_type?: string
      institution?: string
      active?: boolean
      search?: string
    }): Promise<{
      success: boolean
      provas: import('@/types/api').AdminProva[]
      pagination: import('@/types/api').AdminPagination
      filters: import('@/types/api').AdminProvaFilters
      stats: import('@/types/api').AdminProvaStats
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.source) qs.set('source', params.source)
      if (params?.year) qs.set('year', String(params.year))
      if (params?.exam_type) qs.set('exam_type', params.exam_type)
      if (params?.institution) qs.set('institution', params.institution)
      if (params?.active !== undefined) qs.set('active', String(params.active))
      if (params?.search) qs.set('search', params.search)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/provas_integra${suffix}`)
    },

    getProva: (id: number): Promise<{ success: boolean; prova: import('@/types/api').AdminProva }> =>
      this.request(`/admin/provas_integra/${id}`),

    createProva: (data: import('@/types/api').AdminProvaPayload): Promise<{
      success: boolean; message: string; prova: import('@/types/api').AdminProva
    }> =>
      this.request('/admin/provas_integra', {
        method: 'POST',
        body: JSON.stringify({ prova: data }),
      }),

    updateProva: (id: number, data: import('@/types/api').AdminProvaPayload): Promise<{
      success: boolean; message: string; prova: import('@/types/api').AdminProva
    }> =>
      this.request(`/admin/provas_integra/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ prova: data }),
      }),

    // `force` confirma a exclusao das questoes da prova E das tentativas de alunos.
    // Elas agora sao apagadas junto (antes ficavam orfas, sem FK); sem `force` o
    // backend devolve 409.
    deleteProva: (id: number, force = false): Promise<{
      success: boolean; message: string; deleted_questions: number; deleted_attempts: number
    }> =>
      this.request(`/admin/provas_integra/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    toggleProvaActive: (id: number): Promise<{
      success: boolean; message: string; prova: import('@/types/api').AdminProva
    }> =>
      this.request(`/admin/provas_integra/${id}/toggle_active`, { method: 'POST' }),

    // ---------- Questoes das provas na integra ----------

    getProvaQuestions: (params?: {
      page?: number
      per_page?: number
      exam_id?: number
      source?: import('@/types/api').AdminProvaSource
      difficulty?: string
      status?: 'active' | 'canceled' | 'outdated'
      search?: string
    }): Promise<{
      success: boolean
      questions: import('@/types/api').AdminProvaQuestion[]
      pagination: import('@/types/api').AdminPagination
      exam: import('@/types/api').AdminProva | null
      stats: import('@/types/api').AdminProvaQuestionStats
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.exam_id) qs.set('exam_id', String(params.exam_id))
      if (params?.source) qs.set('source', params.source)
      if (params?.difficulty) qs.set('difficulty', params.difficulty)
      if (params?.status) qs.set('status', params.status)
      if (params?.search) qs.set('search', params.search)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/provas_integra_questions${suffix}`)
    },

    createProvaQuestion: (data: import('@/types/api').AdminProvaQuestionPayload): Promise<{
      success: boolean; message: string; question: import('@/types/api').AdminProvaQuestion
    }> =>
      this.request('/admin/provas_integra_questions', {
        method: 'POST',
        body: JSON.stringify({ question: data }),
      }),

    updateProvaQuestion: (id: number, data: import('@/types/api').AdminProvaQuestionPayload): Promise<{
      success: boolean; message: string; question: import('@/types/api').AdminProvaQuestion
    }> =>
      this.request(`/admin/provas_integra_questions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ question: data }),
      }),

    // `force` confirma a exclusao mesmo com respostas de alunos vinculadas; sem ele
    // o backend devolve 409 e sugere marcar a questao como cancelada.
    deleteProvaQuestion: (id: number, force = false): Promise<{
      success: boolean; message: string; deleted_answers: number
    }> =>
      this.request(`/admin/provas_integra_questions/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // ---------- Catalogos: instituicoes, processos seletivos, especialidades ----------

    getCatalogs: (): Promise<{ success: boolean } & import('@/types/api').AdminCatalogs> =>
      this.request('/admin/catalogs'),

    getInstitutions: (params?: { page?: number; per_page?: number; search?: string; state?: string }): Promise<{
      success: boolean
      institutions: import('@/types/api').AdminInstitution[]
      pagination: import('@/types/api').AdminPagination
      filters: { states: string[] }
      stats: { total: number; filtered: number; with_exams: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.state) qs.set('state', params.state)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/institutions${suffix}`)
    },

    createInstitution: (data: import('@/types/api').AdminInstitutionPayload): Promise<{
      success: boolean; message: string; institution: import('@/types/api').AdminInstitution
    }> =>
      this.request('/admin/institutions', { method: 'POST', body: JSON.stringify({ institution: data }) }),

    updateInstitution: (id: number, data: Partial<import('@/types/api').AdminInstitutionPayload>): Promise<{
      success: boolean; message: string; institution: import('@/types/api').AdminInstitution
    }> =>
      this.request(`/admin/institutions/${id}`, { method: 'PATCH', body: JSON.stringify({ institution: data }) }),

    // `force` confirma a exclusao em cascata das provas da instituicao (e das questoes
    // delas); sem ele o backend devolve 409.
    deleteInstitution: (id: number, force = false): Promise<{
      success: boolean; message: string; deleted_exams: number
    }> =>
      this.request(`/admin/institutions/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // ---------- R12-07: faculdades de graduacao (lista do aluno, nao e banca) ----------

    getGraduationInstitutions: (params?: {
      page?: number; per_page?: number; search?: string; state?: string
    }): Promise<import('@/types/api').AdminGraduationInstitutionsResponse> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.state) qs.set('state', params.state)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/graduation_institutions${suffix}`)
    },

    getGraduationInstitution: (id: number): Promise<import('@/types/api').AdminGraduationInstitutionResponse> =>
      this.request(`/admin/graduation_institutions/${id}`),

    createGraduationInstitution: (
      data: import('@/types/api').AdminGraduationInstitutionPayload
    ): Promise<import('@/types/api').AdminGraduationInstitutionResponse> =>
      this.request('/admin/graduation_institutions', {
        method: 'POST',
        body: JSON.stringify({ graduation_institution: data }),
      }),

    updateGraduationInstitution: (
      id: number,
      data: Partial<import('@/types/api').AdminGraduationInstitutionPayload>
    ): Promise<import('@/types/api').AdminGraduationInstitutionResponse> =>
      this.request(`/admin/graduation_institutions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ graduation_institution: data }),
      }),

    // Sem `force`: nada aponta para a tabela (a resposta do aluno e texto).
    deleteGraduationInstitution: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/graduation_institutions/${id}`, { method: 'DELETE' }),

    // ---------- canonical_topics (dicionario paralelo do FSRS) ----------
    // Nao confundir com o Topico do catalogo, mais abaixo. Estas rotas moravam em
    // `/admin/topics` e sairam de la para o nivel 3 do catalogo ocupar esse caminho,
    // que e o que a SPA sempre chamou de "Topicos". O wrapper do body continua
    // `{ topic: ... }`: o controller so mudou de nome, nao de `params.require`.
    getCanonicalTopics: (params?: {
      page?: number
      per_page?: number
      search?: string
      specialty?: string
      active?: boolean
      parent_topic_id?: number
    }): Promise<{
      success: boolean
      topics: import('@/types/api').AdminCanonicalTopic[]
      pagination: import('@/types/api').AdminPagination
      filters: { specialties: string[]; parents: Array<{ id: number; name: string }> }
      stats: { total: number; filtered: number; active: number; in_questions: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.specialty) qs.set('specialty', params.specialty)
      if (params?.active !== undefined) qs.set('active', String(params.active))
      if (params?.parent_topic_id) qs.set('parent_topic_id', String(params.parent_topic_id))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/canonical_topics${suffix}`)
    },

    createCanonicalTopic: (data: import('@/types/api').AdminCanonicalTopicPayload): Promise<{
      success: boolean; message: string; topic: import('@/types/api').AdminCanonicalTopic
    }> =>
      this.request('/admin/canonical_topics', { method: 'POST', body: JSON.stringify({ topic: data }) }),

    updateCanonicalTopic: (id: number, data: Partial<import('@/types/api').AdminCanonicalTopicPayload>): Promise<{
      success: boolean; message: string; topic: import('@/types/api').AdminCanonicalTopic
    }> =>
      this.request(`/admin/canonical_topics/${id}`, { method: 'PATCH', body: JSON.stringify({ topic: data }) }),

    // `force` confirma apagar prevalencia/progresso e promover os subtemas a raiz.
    deleteCanonicalTopic: (id: number, force = false): Promise<{
      success: boolean
      message: string
      promoted_children: number
      deleted_prevalences: number
      deleted_progresses: number
    }> =>
      this.request(`/admin/canonical_topics/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // Carga inicial de canonical_topics a partir das subespecialidades das questoes.
    // Idempotente.
    importCanonicalTopicsFromQuestions: (): Promise<{
      success: boolean; message: string; total_distinct: number; created: number; skipped: number
    }> =>
      this.request('/admin/canonical_topics/import_from_questions', { method: 'POST' }),

    getSpecialties: (params?: { search?: string; active?: boolean }): Promise<{
      success: boolean
      specialties: import('@/types/api').AdminSpecialty[]
      stats: { total: number; active: number; lessons: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.search) qs.set('search', params.search)
      if (params?.active !== undefined) qs.set('active', String(params.active))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/specialties${suffix}`)
    },

    createSpecialty: (data: import('@/types/api').AdminSpecialtyPayload): Promise<{
      success: boolean; message: string; specialty: import('@/types/api').AdminSpecialty
    }> =>
      this.request('/admin/specialties', { method: 'POST', body: JSON.stringify({ specialty: data }) }),

    // `renamed_from` e `legacy_text_references` avisam quando o nome mudou: estudos e
    // revisoes referenciam a especialidade por texto, sem FK, e nao sao migrados.
    updateSpecialty: (id: number, data: Partial<import('@/types/api').AdminSpecialtyPayload>): Promise<{
      success: boolean
      message: string
      specialty: import('@/types/api').AdminSpecialty
      renamed_from: string | null
      legacy_text_references: number
    }> =>
      this.request(`/admin/specialties/${id}`, { method: 'PATCH', body: JSON.stringify({ specialty: data }) }),

    // `force` confirma a exclusao em cascata das aulas da especialidade.
    deleteSpecialty: (id: number, force = false): Promise<{
      success: boolean; message: string; deleted_lessons: number
    }> =>
      this.request(`/admin/specialties/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // ---------- Processos seletivos ----------
    // Instituicao N-N ProcessoSeletivo N-N Especialidade. Os dois vinculos vao no
    // proprio payload do processo, em `institution_ids` e `specialty_ids`, e sao
    // substituicao: o que nao vier na lista e desvinculado.

    getSelectionProcesses: (params?: {
      page?: number
      per_page?: number
      search?: string
      year?: number
      residency_degree?: import('@/types/api').AdminResidencyDegree
      institution_id?: number
      specialty_id?: number
      career_goal_id?: number
      active?: boolean
    }): Promise<{
      success: boolean
      selection_processes: import('@/types/api').AdminSelectionProcess[]
      pagination: import('@/types/api').AdminPagination
      filters: {
        institutions: import('@/types/api').AdminSelectionProcessInstitution[]
        specialties: import('@/types/api').AdminSelectionProcessSpecialty[]
        career_goals: import('@/types/api').AdminSelectionProcessCareerGoal[]
        years: number[]
        residency_degrees: import('@/types/api').AdminResidencyDegree[]
      }
      stats: { total: number; filtered: number; active: number; without_institutions: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.year) qs.set('year', String(params.year))
      if (params?.residency_degree) qs.set('residency_degree', params.residency_degree)
      if (params?.institution_id) qs.set('institution_id', String(params.institution_id))
      if (params?.specialty_id) qs.set('specialty_id', String(params.specialty_id))
      if (params?.career_goal_id) qs.set('career_goal_id', String(params.career_goal_id))
      if (params?.active !== undefined) qs.set('active', String(params.active))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/selection_processes${suffix}`)
    },

    createSelectionProcess: (data: import('@/types/api').AdminSelectionProcessPayload): Promise<{
      success: boolean; message: string; selection_process: import('@/types/api').AdminSelectionProcess
    }> =>
      this.request('/admin/selection_processes', {
        method: 'POST',
        body: JSON.stringify({ selection_process: data }),
      }),

    updateSelectionProcess: (
      id: number,
      data: Partial<import('@/types/api').AdminSelectionProcessPayload>,
    ): Promise<{
      success: boolean; message: string; selection_process: import('@/types/api').AdminSelectionProcess
    }> =>
      this.request(`/admin/selection_processes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ selection_process: data }),
      }),

    // Sem `force`: a exclusao so derruba as duas tabelas de ligacao — instituicoes e
    // especialidades ficam intactas.
    deleteSelectionProcess: (id: number): Promise<{
      success: boolean
      message: string
      unlinked_institutions: number
      unlinked_specialties: number
      unlinked_career_goals: number
    }> => this.request(`/admin/selection_processes/${id}`, { method: 'DELETE' }),

    // ---------- Objetivos (r6-23) ----------
    // Catalogos > Objetivos: o que o aluno quer ser. Alimenta so "Especialidade desejada".
    // Excluir so sem aluno apontando (409 manda desativar).

    getCareerGoals: (params?: { search?: string; active?: boolean }): Promise<{
      success: boolean
      career_goals: import('@/types/api').AdminCareerGoal[]
      stats: { total: number; active: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.search) qs.set('search', params.search)
      if (params?.active !== undefined) qs.set('active', String(params.active))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/career_goals${suffix}`)
    },

    createCareerGoal: (data: import('@/types/api').AdminCareerGoalPayload): Promise<{
      success: boolean; message: string; career_goal: import('@/types/api').AdminCareerGoal
    }> =>
      this.request('/admin/career_goals', { method: 'POST', body: JSON.stringify({ career_goal: data }) }),

    updateCareerGoal: (id: number, data: Partial<import('@/types/api').AdminCareerGoalPayload>): Promise<{
      success: boolean; message: string; career_goal: import('@/types/api').AdminCareerGoal
    }> =>
      this.request(`/admin/career_goals/${id}`, { method: 'PATCH', body: JSON.stringify({ career_goal: data }) }),

    deleteCareerGoal: (id: number): Promise<{
      success: boolean; message: string; unlinked_processes: number
    }> => this.request(`/admin/career_goals/${id}`, { method: 'DELETE' }),

    // ---------- Catalogo: Subespecialidades (nivel 2) ----------
    // Eram `/admin/reference_data?category=study_theme`. Sem a coluna `category`, o
    // nivel deixou de ser parametro e virou rota: dois recursos concretos no lugar de
    // um CRUD generico. O filtro por pai acompanhou (`specialty_id` aqui,
    // `sub_specialty_id` nos topicos).

    // A colecao vem sob a chave do NIVEL (`sub_specialties`), nao sob um `entries`
    // generico: cada nivel tem controller proprio desde a morte de `reference_data`, e o
    // envelope segue o resto de /api/v1 (o recurso no plural). Quem uniformiza as duas
    // telas e o hook, nao o fio.
    getSubSpecialties: (params?: {
      page?: number
      per_page?: number
      search?: string
      specialty_id?: number
    }): Promise<{
      success: boolean
      sub_specialties: import('@/types/api').AdminSubSpecialty[]
      pagination: import('@/types/api').AdminPagination
      filters: { specialties: Array<{ id: number; name: string }> }
      stats: { total: number; filtered: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.specialty_id) qs.set('specialty_id', String(params.specialty_id))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/sub_specialties${suffix}`)
    },

    // O registro unico volta sob `sub_specialty`, a mesma chave que o POST manda no
    // corpo — nao ha `entry` no fio.
    createSubSpecialty: (data: import('@/types/api').AdminSubSpecialtyPayload): Promise<{
      success: boolean; message: string; sub_specialty: import('@/types/api').AdminSubSpecialty
    }> =>
      this.request('/admin/sub_specialties', {
        method: 'POST',
        body: JSON.stringify({ sub_specialty: data }),
      }),

    // Renomear NAO recalcula o `hash_id`: ele e a identidade publica que a Primeira
    // Carga reenvia e que as sincronizacoes usam para reencontrar o verbete.
    updateSubSpecialty: (
      id: number,
      data: Partial<import('@/types/api').AdminSubSpecialtyPayload>,
    ): Promise<{
      success: boolean; message: string; sub_specialty: import('@/types/api').AdminSubSpecialty
    }> =>
      this.request(`/admin/sub_specialties/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ sub_specialty: data }),
      }),

    // `force` confirma a exclusao mesmo com dependentes. Nada e apagado junto, mas as
    // FKs sao ON DELETE SET NULL: os topicos ficam sem pai e as questoes perdem a
    // classificacao (o backend zera `specialty_id` junto, para nao sobrar especialidade
    // orfa). Sem `force`, a rota responde 409 com o texto que explica o estrago.
    deleteSubSpecialty: (id: number, force = false): Promise<{
      success: boolean; message: string; orphaned_topics?: number; unclassified_questions?: number
    }> =>
      this.request(`/admin/sub_specialties/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // ---------- Catalogo: Topicos (nivel 3, o que o aluno escolhe) ----------

    getTopics: (params?: {
      page?: number
      per_page?: number
      search?: string
      sub_specialty_id?: number
    }): Promise<{
      success: boolean
      topics: import('@/types/api').AdminTopic[]
      pagination: import('@/types/api').AdminPagination
      filters: { sub_specialties: Array<{ id: number; name: string }> }
      stats: { total: number; filtered: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.page) qs.set('page', String(params.page))
      if (params?.per_page) qs.set('per_page', String(params.per_page))
      if (params?.search) qs.set('search', params.search)
      if (params?.sub_specialty_id) qs.set('sub_specialty_id', String(params.sub_specialty_id))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/topics${suffix}`)
    },

    createTopic: (data: import('@/types/api').AdminTopicPayload): Promise<{
      success: boolean; message: string; topic: import('@/types/api').AdminTopic
    }> =>
      this.request('/admin/topics', { method: 'POST', body: JSON.stringify({ topic: data }) }),

    updateTopic: (id: number, data: Partial<import('@/types/api').AdminTopicPayload>): Promise<{
      success: boolean; message: string; topic: import('@/types/api').AdminTopic
    }> =>
      this.request(`/admin/topics/${id}`, { method: 'PATCH', body: JSON.stringify({ topic: data }) }),

    // O topico e folha, entao nao ha filho a soltar — mas `exam_questions.topic_id`
    // tambem e SET NULL, e sao dezenas de milhares de questoes nos topicos grandes.
    // E por elas que o `force` existe aqui.
    deleteTopic: (id: number, force = false): Promise<{
      success: boolean; message: string; untagged_questions?: number
    }> =>
      this.request(`/admin/topics/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

    // ---------- Videoaulas de curso (spec r4/12) ----------
    // Curso: multipart quando ha thumb (`thumbnail`), campos em `video_course[...]`.
    // Aula: JSON puro — o video e um link do Vimeo, `unlock_params` e objeto.

    getVideoCourses: (): Promise<{
      success: boolean
      video_courses: import('@/types/api').AdminVideoCourse[]
      plans: import('@/types/api').AdminVideoCoursePlan[]
      storage_configured: boolean
      stats: { total: number; active: number; lessons: number }
    }> => this.request('/admin/video_courses'),

    getVideoCourse: (id: number): Promise<{
      success: boolean; video_course: import('@/types/api').AdminVideoCourse
    }> => this.request(`/admin/video_courses/${id}`),

    createVideoCourse: (
      data: import('@/types/api').AdminVideoCoursePayload,
      thumbnail?: File | null,
    ): Promise<{ success: boolean; message: string; video_course: import('@/types/api').AdminVideoCourse }> =>
      this.requestMultipart('/admin/video_courses', buildVideoCourseForm(data, thumbnail)),

    updateVideoCourse: (
      id: number,
      data: Partial<import('@/types/api').AdminVideoCoursePayload>,
      thumbnail?: File | null,
    ): Promise<{ success: boolean; message: string; video_course: import('@/types/api').AdminVideoCourse }> =>
      this.requestMultipart(`/admin/video_courses/${id}`, buildVideoCourseForm(data, thumbnail), 'PATCH'),

    deleteVideoCourse: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/video_courses/${id}`, { method: 'DELETE' }),

    toggleVideoCourseActive: (id: number): Promise<{
      success: boolean; message: string; video_course: import('@/types/api').AdminVideoCourse
    }> => this.request(`/admin/video_courses/${id}/toggle_active`, { method: 'POST' }),

    /** A lista INTEIRA de ids das aulas do curso, na ordem nova. */
    reorderVideoLessons: (courseId: number, lessonIds: number[]): Promise<{
      success: boolean; message: string; video_course: import('@/types/api').AdminVideoCourse
    }> =>
      this.request(`/admin/video_courses/${courseId}/reorder_lessons`, {
        method: 'POST',
        body: JSON.stringify({ lesson_ids: lessonIds }),
      }),

    createVideoLesson: (
      data: import('@/types/api').AdminVideoLessonPayload,
    ): Promise<{ success: boolean; message: string; video_lesson: import('@/types/api').AdminVideoLesson }> =>
      this.request('/admin/video_lessons', { method: 'POST', body: JSON.stringify({ video_lesson: data }) }),

    updateVideoLesson: (
      id: number,
      data: Partial<import('@/types/api').AdminVideoLessonPayload>,
    ): Promise<{ success: boolean; message: string; video_lesson: import('@/types/api').AdminVideoLesson }> =>
      this.request(`/admin/video_lessons/${id}`, { method: 'PATCH', body: JSON.stringify({ video_lesson: data }) }),

    deleteVideoLesson: (id: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/admin/video_lessons/${id}`, { method: 'DELETE' }),

    toggleVideoLessonActive: (id: number): Promise<{
      success: boolean; message: string; video_lesson: import('@/types/api').AdminVideoLesson
    }> => this.request(`/admin/video_lessons/${id}/toggle_active`, { method: 'POST' }),

    // ---------- Videos de acompanhamento ----------
    // Criar/atualizar sao multipart: o video vai em `file` e os demais campos em
    // `followup_video[...]`. O POST retorna assim que o arquivo bruto chega ao
    // bucket — o encode roda depois, em job, e o `status` conta em que pe esta.

    getFollowupVideos: (params?: {
      search?: string
      active?: boolean
      status?: import('@/types/api').AdminFollowupVideoStatus
      /** Com quatro condicoes dividindo a tabela, a listagem sem filtro mistura contextos. */
      condition_type?: import('@/types/api').AdminFollowupVideoConditionType
    }): Promise<{
      success: boolean
      followup_videos: import('@/types/api').AdminFollowupVideo[]
      /** false quando faltam bucket/credenciais — a tela avisa em vez de deixar salvar. */
      storage_configured: boolean
      stats: { total: number; active: number; ready: number; pending: number; failed: number }
    }> => {
      const qs = new URLSearchParams()
      if (params?.search) qs.set('search', params.search)
      if (params?.active !== undefined) qs.set('active', String(params.active))
      if (params?.status) qs.set('status', params.status)
      if (params?.condition_type) qs.set('condition_type', params.condition_type)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/followup_videos${suffix}`)
    },

    /**
     * Os relatos mensais de dificuldade. Somente leitura — e a tela onde o TEXTO LIVRE do
     * aluno e lido por um humano, e a unica razao de `notes` existir.
     *
     * A busca alcanca nome e e-mail do aluno, nunca o conteudo de `notes`.
     */
    getDifficultyReports: (params?: {
      reference_month?: string
      search?: string
      limit?: number
      page?: number
    }): Promise<import('@/types/api').AdminDifficultyReportsResponse> => {
      const qs = new URLSearchParams()
      if (params?.reference_month) qs.set('reference_month', params.reference_month)
      if (params?.search) qs.set('search', params.search)
      if (params?.limit !== undefined) qs.set('limit', String(params.limit))
      if (params?.page !== undefined) qs.set('page', String(params.page))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request(`/admin/difficulty_reports${suffix}`)
    },

    getFollowupVideo: (id: number): Promise<{
      success: boolean; followup_video: import('@/types/api').AdminFollowupVideo
    }> => this.request(`/admin/followup_videos/${id}`),

    createFollowupVideo: (
      data: import('@/types/api').AdminFollowupVideoPayload,
      file: File,
    ): Promise<{ success: boolean; message: string; followup_video: import('@/types/api').AdminFollowupVideo }> =>
      this.requestMultipart('/admin/followup_videos', buildFollowupVideoForm(data, file)),

    /** Sem `file` so os metadados mudam; com `file` o video volta para a fila. */
    updateFollowupVideo: (
      id: number,
      data: Partial<import('@/types/api').AdminFollowupVideoPayload>,
      file?: File | null,
    ): Promise<{ success: boolean; message: string; followup_video: import('@/types/api').AdminFollowupVideo }> =>
      this.requestMultipart(`/admin/followup_videos/${id}`, buildFollowupVideoForm(data, file), 'PATCH'),

    deleteFollowupVideo: (id: number): Promise<{
      success: boolean; message: string; files_deleted: number; files_total: number
    }> => this.request(`/admin/followup_videos/${id}`, { method: 'DELETE' }),

    toggleFollowupVideoActive: (id: number): Promise<{
      success: boolean; message: string; followup_video: import('@/types/api').AdminFollowupVideo
    }> => this.request(`/admin/followup_videos/${id}/toggle_active`, { method: 'POST' }),

    /** Devolve um video `failed` para a fila depois de corrigida a causa. */
    reprocessFollowupVideo: (id: number): Promise<{
      success: boolean; message: string; followup_video: import('@/types/api').AdminFollowupVideo
    }> => this.request(`/admin/followup_videos/${id}/reprocess`, { method: 'POST' }),

    /** URLs assinadas de 5 min. `source` pede o arquivo bruto em vez das renditions. */
    getFollowupVideoPreviewUrl: (
      id: number,
      source = false,
    ): Promise<{ success: boolean } & import('@/types/api').AdminFollowupVideoPreview> =>
      this.request(`/admin/followup_videos/${id}/preview_url${source ? '?source=true' : ''}`),
  }

  // MH14-02: previa do TOP 5 das instituicoes prioritarias enquanto o aluno edita.
  priorityInstitutions = {
    top: (
      entries: readonly import('@/types/user').PriorityInstitutionEntry[]
    ): Promise<{ success: boolean; data: { top: import('@/types/user').PriorityTopPick[] } }> =>
      this.request('/priority_institutions/top', {
        method: 'POST',
        body: JSON.stringify({
          priority_institutions: entries.map(({ name, weight, pinned }) => ({ name, weight, pinned: pinned === true })),
        }),
      }),
  }

  // MH13-18/19: primeira entrada (video de boas-vindas e alerta do simulado de entrada)
  firstAccess = {
    // MH14-04: sequencia de videos obrigatorios da primeira entrada.
    videos: (): Promise<{ success: boolean; data: import('@/types/api').FirstAccessVideosState }> =>
      this.request('/first_access/videos'),
    markVideoWatched: (lessonId: number): Promise<{ success: boolean; data: import('@/types/api').FirstAccessVideoWatched }> =>
      this.request(`/first_access/videos/${lessonId}/watched`, { method: 'POST' }),
    entranceTest: (): Promise<{ success: boolean; data: import('@/types/api').EntranceTestState }> =>
      this.request('/first_access/entrance_test'),
  }

  // Videos de acompanhamento (lado do aluno)
  followupVideos = {
    /**
     * URL assinada de 5 minutos, pedida no clique do play. O resultado da prova
     * ja traz o titulo e a duracao — aqui so vem o que expira.
     */
    playback: (id: number): Promise<{ success: boolean } & import('@/types/api').FollowupVideoPlayback> =>
      this.request(`/followup_videos/${id}/playback`),

    /**
     * Os videos que casam com um CONTEXTO (jornada, manual, formulario de entrada,
     * relato de dificuldades). Contexto sem video casado responde `videos: []` — quem
     * decide nao renderizar nada e a tela (decisao 4.4), nunca a API.
     *
     * `onboarding` e `difficulty_report` nao levam parametro: o servidor le as respostas
     * e o relato do PROPRIO aluno.
     */
    recommendations: (
      context: import('@/types/api').FollowupVideoContext,
      params?: { topic_id?: number; topic_ids?: number[] },
    ): Promise<import('@/types/api').FollowupRecommendationsResponse> => {
      const qs = new URLSearchParams()
      qs.set('context', context)
      if (params?.topic_id !== undefined) qs.set('topic_id', String(params.topic_id))
      params?.topic_ids?.forEach((id) => qs.append('topic_ids[]', String(id)))
      return this.request(`/followup_videos/recommendations?${qs.toString()}`)
    },

    /** O alerta (R5): os videos ja oferecidos que continuam `pending`. Teto de 5. */
    pending: (): Promise<import('@/types/api').FollowupPendingResponse> =>
      this.request('/followup_videos/pending'),

    /** Idempotente, e TERMINAL: depois de `seen`, `skip` nao muda mais nada. */
    markSeen: (id: number): Promise<import('@/types/api').FollowupVideoStateResponse> =>
      this.request(`/followup_videos/${id}/seen`, { method: 'POST' }),

    /** "Assistir mais tarde": sai do banner, permanece no contexto na forma discreta. */
    skip: (id: number): Promise<import('@/types/api').FollowupVideoStateResponse> =>
      this.request(`/followup_videos/${id}/skip`, { method: 'POST' }),
  }

  /**
   * Formulario recorrente de dificuldades (spec 05, R4).
   *
   * Nao ha `update` nem `destroy`: o relato do mes e recurso unico por aluno (indice
   * unico em [user_id, reference_month]), entao reenviar dentro da janela e o proprio
   * POST, que atualiza a linha.
   */
  difficultyReports = {
    /**
     * A janela + o relato deste mes, se ja houver. Responde com a MESMA forma dentro e
     * fora da janela: quem decide renderizar o formulario e `available`, nunca `report`.
     */
    current: (): Promise<import('@/types/api').DifficultyReportWindowResponse> =>
      this.request('/difficulty_reports/current'),

    /** 201 no primeiro envio do mes, 200 no reenvio. A resposta ja traz os videos casados. */
    submit: (
      payload: import('@/types/api').DifficultyReportPayload,
    ): Promise<import('@/types/api').CreateDifficultyReportResponse> =>
      this.request('/difficulty_reports', {
        method: 'POST',
        body: JSON.stringify({ difficulty_report: payload }),
      }),
  }

  // R08-14: opt-in do WhatsApp do proprio usuario (aluno ou padrinho). 422 traz `error`
  // (telefone invalido, codigo pedido cedo demais, codigo errado/vencido).
  // Ligar deixa pendente e manda um codigo ao numero; `confirm` com o codigo e que ativa.
  whatsappOptIn = {
    get: (): Promise<{ success: boolean; whatsapp_opt_in: WhatsappOptIn }> => this.request('/whatsapp_opt_in'),

    update: (payload: WhatsappOptInUpdatePayload): Promise<{ success: boolean; whatsapp_opt_in: WhatsappOptIn }> =>
      this.request('/whatsapp_opt_in', {
        method: 'PUT',
        body: JSON.stringify({ whatsapp_opt_in: payload }),
      }),

    confirm: (code: string): Promise<{ success: boolean; whatsapp_opt_in: WhatsappOptIn }> =>
      this.request('/whatsapp_opt_in/confirm', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
  }

  // R08-05: parametros do ciclo do proprio aluno. `month` 'YYYY-MM'; sem ele, o mes
  // corrente. 400 para mes invalido.
  // R08-22 (Modo Essential): a resposta traz tambem `proposal`, `coherence` e `self_setting`.
  // O aluno SEM padrinho grava (PUT, `origin: 'student'`); com padrinho, 403. Combinacao
  // incoerente volta 422 com `errors.coherence` e `coherence`, e nada e gravado.
  cycleParameters = {
    get: (month?: string): Promise<MyCycleParametersResponse> =>
      this.request(`/cycle_parameters${month ? `?month=${encodeURIComponent(month)}` : ''}`),

    update: (payload: CycleParametersUpdatePayload): Promise<MyCycleParametersResponse> =>
      this.request('/cycle_parameters', { method: 'PUT', body: JSON.stringify(payload) }),

    checkCoherence: (payload: CycleCoherenceCheckPayload): Promise<{ success: boolean; coherence: CycleCoherence }> =>
      this.request('/cycle_parameters/coherence', { method: 'POST', body: JSON.stringify(payload) }),
  }

  // R08-08: condutas do proprio aluno — so leitura (vigentes primeiro, depois as revogadas).
  conducts = {
    list: (): Promise<ConductsResponse> => this.request('/conducts'),
  }

  // INST-09: recados do padrinho nas reunioes (ultimo + historico) — so leitura, sem a ata.
  meetingMessages = {
    list: (): Promise<MeetingStudentMessagesResponse> => this.request('/meeting_messages'),
  }

  // R08-24: NPS do proprio aluno sobre o padrinho — pesquisas pendentes e resposta (so nota).
  // 422 com `error` quando nao ha mentoria para avaliar ou ja respondeu no periodo.
  mentorNps = {
    get: (): Promise<MentorNpsResponse> => this.request('/mentor_nps'),

    submit: (payload: MentorNpsSubmitPayload): Promise<MentorNpsResponse> =>
      this.request('/mentor_nps', { method: 'POST', body: JSON.stringify(payload) }),
  }

  // MH13-33: padrinho(s) do proprio aluno — bloco "Seu mentor" do dashboard.
  myMentor = {
    get: (): Promise<MyMentorResponse> => this.request('/my_mentor'),
  }

  // R08-26: fila de prazos do edital da instituicao-alvo do proprio aluno e a marca de "feito".
  // 404 para prazo fora da fila (outra instituicao ou ja vencido).
  examDeadlines = {
    list: (): Promise<ExamDeadlineQueueResponse> => this.request('/exam_deadlines'),

    complete: (id: number): Promise<ExamDeadlineItemResponse> =>
      this.request(`/exam_deadlines/${id}/completion`, { method: 'POST' }),

    uncomplete: (id: number): Promise<ExamDeadlineItemResponse> =>
      this.request(`/exam_deadlines/${id}/completion`, { method: 'DELETE' }),
  }

  // R08-23: material enviado pelo padrinho ao proprio aluno — ler e marcar como visto.
  materialShares = {
    list: (): Promise<MaterialSharesResponse> => this.request('/material_shares'),

    markViewed: (id: number): Promise<MaterialShareResponse> =>
      this.request(`/material_shares/${id}/view`, { method: 'PATCH' }),
  }

  // R08-21: resultados de prova do proprio aluno — registrar, preencher o pedido automatico,
  // confirmar, enviar comprovante e dar/revogar o consentimento (texto provisorio, R08-27).
  // 404 para resultado de outro aluno; 422 com `errors` por campo ou `error`.
  examResults = {
    list: (): Promise<ExamResultsResponse> => this.request('/exam_results'),

    create: (payload: ExamResultCreatePayload): Promise<ExamResultResponse> =>
      this.request('/exam_results', { method: 'POST', body: JSON.stringify({ exam_result: payload }) }),

    update: (id: number, payload: ExamResultFields): Promise<ExamResultResponse> =>
      this.request(`/exam_results/${id}`, { method: 'PATCH', body: JSON.stringify({ exam_result: payload }) }),

    confirm: (id: number): Promise<ExamResultResponse> =>
      this.request(`/exam_results/${id}/confirm`, { method: 'POST' }),

    uploadEvidence: (id: number, file: File): Promise<ExamResultResponse> => {
      const formData = new FormData()
      formData.append('evidence', file)
      return this.requestMultipart(`/exam_results/${id}/evidence`, formData)
    },

    downloadEvidence: (id: number): Promise<Blob> => this.requestBlob(`/exam_results/${id}/evidence`),

    grantConsent: (id: number, termsVersion: string): Promise<ExamResultResponse> =>
      this.request(`/exam_results/${id}/consent`, {
        method: 'POST',
        body: JSON.stringify({ accepted: true, terms_version: termsVersion }),
      }),

    revokeConsent: (id: number): Promise<ExamResultResponse> =>
      this.request(`/exam_results/${id}/revoke_consent`, { method: 'POST' }),
  }

  // R08-09: compromissos do proprio aluno — le, marca cumpri/nao cumpri e comenta enquanto
  // aberto. 404 para compromisso de outro aluno; 422 se ja fechado.
  commitments = {
    list: (): Promise<CommitmentsResponse> => this.request('/commitments'),

    update: (id: number, payload: CommitmentStudentPayload): Promise<CommitmentResponse> =>
      this.request(`/commitments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ commitment: payload }),
      }),
  }

  // Instructor endpoints
  instructor = {
    getDashboard: (): Promise<{
      success: boolean
      stats: { total_students: number; active_students: number; inactive_students: number; total_sessions_this_week?: number }
      recent_students: Array<{ id: number; name: string; email: string; last_sign_in_at?: string; is_active: boolean }>
      mentor_profile?: {
        id: number
        name: string
        specialty?: string
        about?: string
        foto_url?: string
        is_essential_mentor?: boolean
      }
    }> => this.request('/instructor/dashboard'),

    // R08-07: lista de TED de TODOS os afilhados (tela inicial do padrinho), ordenada do pior
    // para o melhor (`rank`). "Nao registrou" vem como `minutes: null`, distinto de 0.
    getTedPortfolio: (): Promise<{ success: boolean; ted_portfolio: TedPortfolio }> =>
      this.request('/instructor/ted_portfolio'),

    // MH9-12: relatorio do ciclo do mes da carteira. Sem `month`: o mes anterior, fechado.
    getCycleReport: (month?: string): Promise<CycleReportResponse> =>
      this.request(`/instructor/cycle_report${month ? `?month=${encodeURIComponent(month)}` : ''}`),

    getStudents: (params?: { page?: number; per_page?: number; search?: string; status?: string }): Promise<{
      success: boolean
      students: InstructorStudent[]
      pagination: Pagination
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.status) searchParams.set('status', params.status)
      return this.request(`/instructor/students?${searchParams.toString()}`)
    },

    getStudent: (id: number): Promise<{ success: boolean; student: InstructorStudentDetail }> =>
      this.request(`/instructor/students/${id}`),

    getStudentAnalytics: (id: number): Promise<{ success: boolean; analytics: StudentAnalytics }> =>
      this.request(`/instructor/students/${id}/analytics`),

    // Guia estatistico do aluno mentorado, na area do padrinho (r03-09): o MESMO guia de
    // `getStatisticalGuide`, sem a projecao que o payload do aluno sofre — aqui vem a
    // prontidao e vem a contagem absoluta de questoes.
    //
    // Envelope identico ao do guia do aluno (`{ success, data }`), porque os dois
    // endpoints respondem `render_success({ data: <guia> })` no backend.
    //
    // 403 para quem nao e padrinho do aluno; sem query string, pela mesma razao do guia
    // do aluno (a cobertura nao depende de periodo).
    //
    // R08-19: `institution` le o guia de UMA instituicao prioritaria do aluno; omitido, o guia
    // combinado. `focus` diz qual foi lida e traz as opcoes. Nome fora das prioritarias: 422.
    getStudentStatisticalGuide: (id: number, institution?: string): Promise<InstructorStatisticalGuideResponse> =>
      this.request(`/instructor/students/${id}/statistical_guide${institutionQuery(institution)}`),

    // MH9-02: Analise de metricas do afilhado — espelho EXATO de getTrackMetrics,
    // getTrackDetail, getStudyActivity e getExamMetrics (mesmos parametros, mesmo payload),
    // lido pelo padrinho. Quem escolhe entre um e outro e `useMetricsScope` (hooks de
    // useDashboard), nunca a tela.
    getMenteeTrackMetrics: (id: number, period?: string): Promise<{ success: boolean; data: TrackMetricsResponse }> =>
      this.request(
        `/instructor/students/${id}/metrics/track_metrics${period ? `?period=${encodeURIComponent(period)}` : ''}`
      ),

    getMenteeTrackDetail: (
      id: number,
      code: string,
      period?: string
    ): Promise<{ success: boolean; data: TrackDetailResponse }> =>
      this.request(
        `/instructor/students/${id}/metrics/track_metrics/${encodeURIComponent(code)}${period ? `?period=${encodeURIComponent(period)}` : ''}`
      ),

    getMenteeStudyActivity: (
      id: number,
      period: string = 'all',
      weekOffset: number = 0
    ): Promise<{ success: boolean; data: StudyActivityResponse }> =>
      this.request(
        `/instructor/students/${id}/metrics/study_activity?period=${encodeURIComponent(period)}&week_offset=${weekOffset}`
      ),

    getMenteeExamMetrics: (
      id: number,
      period?: string,
      { withTracks = true }: { withTracks?: boolean } = {},
    ): Promise<{ success: boolean; data: ExamMetricsResponse }> => {
      const params = new URLSearchParams()
      if (period) params.set('period', period)
      if (!withTracks) params.set('with_tracks', '0')
      const query = params.toString()
      return this.request(`/instructor/students/${id}/metrics/exam_metrics${query ? `?${query}` : ''}`)
    },

    // R08-19: matriz prevalencia x desempenho (instituicao-alvo por padrao).
    getStudentPrevalenceMatrix: (id: number, institution?: string): Promise<InstructorPrevalenceMatrixResponse> =>
      this.request(`/instructor/students/${id}/prevalence_matrix${institutionQuery(institution)}`),

    // R08-19: instituicoes-alvo da carteira lado a lado.
    getInstitutionPortfolio: (): Promise<InstitutionPortfolioResponse> =>
      this.request('/instructor/institution_portfolio'),

    // R08-02: o padrinho ajusta so a DATA-alvo da prova principal do afilhado (a
    // instituicao-alvo e a 1a escolha do aluno). 403 fora da carteira; 422 com
    // `errors.exam_date` para data invalida ou passada.
    updateStudentMainExam: (id: number, payload: MainExamUpdatePayload): Promise<{ success: boolean; main_exam: MainExam }> =>
      this.request(`/instructor/students/${id}/main_exam`, {
        method: 'PATCH',
        body: JSON.stringify({ main_exam: payload }),
      }),

    // R08-05: parametros do ciclo do afilhado. GET le alvo · medido · desvio do mes; PUT
    // grava os alvos (origem 'mentor') e devolve o relatorio atualizado. 403 fora da
    // carteira; 422 com `errors[<parametro>]` para alvo invalido, parametro desconhecido
    // ou mes ja encerrado; 400 para mes invalido.
    // R08-06: as duas respostas trazem tambem `proposal` (proposta do sistema) e `coherence`
    // (dos alvos salvos). O PUT grava mesmo com conflito; valor igual ao proposto sai
    // `origin: 'system'`.
    getStudentCycleParameters: (id: number, month?: string): Promise<InstructorCycleParametersResponse> =>
      this.request(
        `/instructor/students/${id}/cycle_parameters${month ? `?month=${encodeURIComponent(month)}` : ''}`
      ),

    // MH9-14: sugestao do ciclo pela IA. GET le a mais recente do padrinho para o mes (padrao:
    // mes seguinte); POST gera (sincrono, 403 assistant_disabled, 429 daily_limit_reached,
    // 502/504 do provedor); as decisoes aplicam (accept/apply_edit) ou descartam. Nada muda no
    // ciclo sem accept/apply_edit. 422 quando ja decidida ou alvo invalido.
    getStudentCycleSuggestion: (id: number, month?: string): Promise<CycleSuggestionResponse> =>
      this.request(
        `/instructor/students/${id}/cycle_suggestion${month ? `?month=${encodeURIComponent(month)}` : ''}`
      ),

    generateStudentCycleSuggestion: (id: number, month: string): Promise<CycleSuggestionMutationResponse> =>
      this.request(`/instructor/students/${id}/cycle_suggestion`, {
        method: 'POST',
        body: JSON.stringify({ month }),
      }),

    acceptStudentCycleSuggestion: (id: number, suggestionId: number): Promise<CycleSuggestionMutationResponse> =>
      this.request(`/instructor/students/${id}/cycle_suggestions/${suggestionId}/accept`, { method: 'POST' }),

    applyEditedStudentCycleSuggestion: (
      id: number,
      suggestionId: number,
      parameters: CycleParametersUpdatePayload['parameters']
    ): Promise<CycleSuggestionMutationResponse> =>
      this.request(`/instructor/students/${id}/cycle_suggestions/${suggestionId}/apply_edit`, {
        method: 'POST',
        body: JSON.stringify({ parameters }),
      }),

    dismissStudentCycleSuggestion: (id: number, suggestionId: number): Promise<CycleSuggestionMutationResponse> =>
      this.request(`/instructor/students/${id}/cycle_suggestions/${suggestionId}/dismiss`, { method: 'POST' }),

    // MH9-06: meta x realizado do ciclo semana a semana no mes (grafico da Visao geral).
    getStudentCycleWeeks: (id: number, month?: string): Promise<CycleWeeksResponse> =>
      this.request(`/instructor/students/${id}/cycle_weeks${month ? `?month=${encodeURIComponent(month)}` : ''}`),

    updateStudentCycleParameters: (
      id: number,
      payload: CycleParametersUpdatePayload
    ): Promise<InstructorCycleParametersResponse> =>
      this.request(`/instructor/students/${id}/cycle_parameters`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),

    // R08-06: checa a coerencia de um rascunho (alvos salvos + rascunho por cima) sem gravar.
    // R08-16 — projecao de prontidao do afilhado na data-alvo (faixas + as duas saidas).
    getStudentReadinessProjection: (id: number): Promise<InstructorReadinessProjectionResponse> =>
      this.request(`/instructor/students/${id}/readiness_projection`),

    // R08-17: lista de despedida do afilhado e "discordar do abandono" (conduta + compromisso).
    getStudentFarewellTopics: (id: number): Promise<InstructorFarewellResponse> =>
      this.request(`/instructor/students/${id}/farewell_topics`),

    disagreeWithAbandonment: (
      id: number,
      topicId: number,
      payload: FarewellDisagreementPayload
    ): Promise<FarewellDisagreementResponse> =>
      this.request(`/instructor/students/${id}/farewell_topics/${topicId}/disagreement`, {
        method: 'POST',
        body: JSON.stringify({ disagreement: payload }),
      }),

    // R08-26: fila de prazos do edital da instituicao-alvo do afilhado (so leitura).
    getStudentExamDeadlines: (id: number): Promise<ExamDeadlineQueueResponse> =>
      this.request(`/instructor/students/${id}/exam_deadlines`),

    // HERMES: processos seletivos do afilhado (instituicoes-alvo + exames nacionais), so a
    // revisao aprovada. Contrato .spec/hermes/contrato.md §3.3.
    getStudentExamProcesses: (id: number): Promise<ExamProcessesResponse> =>
      this.request(`/instructor/students/${id}/exam_processes`),

    // INST-06/HERMES: Central do Aprovado — processos seletivos da carteira.
    getApprovalCenter: (): Promise<ApprovalCenterResponse> =>
      this.request('/instructor/approval_center'),

    // R08-20: chegada tardia do afilhado e a escolha da conduta (vira conduta, R08-08).
    getStudentLateArrivals: (id: number): Promise<InstructorLateArrivalResponse> =>
      this.request(`/instructor/students/${id}/late_arrivals`),

    chooseLateArrivalConduct: (
      id: number,
      topicId: number,
      payload: LateArrivalChoicePayload
    ): Promise<LateArrivalChoiceResponse> =>
      this.request(`/instructor/students/${id}/late_arrivals/${topicId}/decision`, {
        method: 'POST',
        body: JSON.stringify({ decision: payload }),
      }),

    // R08-18: memoria vs. compreensao por topico (voltar ao primario x revisar).
    getStudentMemoryComprehension: (id: number): Promise<InstructorMemoryComprehensionResponse> =>
      this.request(`/instructor/students/${id}/memory_comprehension`),

    // R08-24: espelho de qualidade do proprio padrinho (regularidade, resposta, briefing, NPS).
    getQualityMirror: (days: QualityMirrorDays = 90): Promise<InstructorQualityMirrorResponse> =>
      this.request(`/instructor/quality_mirror?days=${days}`),

    // R08-25: area administrativa (carteira, remuneracao e bonus pendentes, documentos, cobertura).
    getAdministrative: (): Promise<InstructorAdministrativeResponse> => this.request('/instructor/administrative'),

    // R08-25: documentos do proprio padrinho. Ele envia so 'document' (contrato e do admin);
    // 422 para arquivo fora de PDF/JPEG/PNG/WebP ou acima de 10 MB.
    getMentorDocuments: (): Promise<MentorDocumentsResponse> => this.request('/instructor/mentor_documents'),

    uploadMentorDocument: (title: string, file: File): Promise<MentorDocumentResponse> => {
      const formData = new FormData()
      formData.append('title', title)
      formData.append('file', file)
      return this.requestMultipart('/instructor/mentor_documents', formData)
    },

    downloadMentorDocument: (id: number): Promise<Blob> => this.requestBlob(`/instructor/mentor_documents/${id}/file`),

    // R08-13: cabecalho de confiabilidade (registros, nota individual x bloco, min/questao, IR global).
    getStudentReliabilityHeader: (
      id: number,
      days: ReliabilityHeaderDays = 30
    ): Promise<InstructorReliabilityHeaderResponse> =>
      this.request(`/instructor/students/${id}/reliability_header?days=${days}`),

    checkStudentCycleCoherence: (
      id: number,
      payload: CycleCoherenceCheckPayload
    ): Promise<{ success: boolean; coherence: CycleCoherence }> =>
      this.request(`/instructor/students/${id}/cycle_parameters/coherence`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    // R08-21: linha do tempo da relacao (mais recente no topo) e resultados de prova do
    // afilhado. O padrinho registra e corrige; so o aluno confirma e consente.
    getStudentTimeline: (id: number): Promise<InstructorTimelineResponse> =>
      this.request(`/instructor/students/${id}/timeline`),

    getStudentExamResults: (id: number): Promise<ExamResultsResponse> =>
      this.request(`/instructor/students/${id}/exam_results`),

    createStudentExamResult: (id: number, payload: ExamResultCreatePayload): Promise<ExamResultResponse> =>
      this.request(`/instructor/students/${id}/exam_results`, {
        method: 'POST',
        body: JSON.stringify({ exam_result: payload }),
      }),

    updateStudentExamResult: (id: number, resultId: number, payload: ExamResultFields): Promise<ExamResultResponse> =>
      this.request(`/instructor/students/${id}/exam_results/${resultId}`, {
        method: 'PATCH',
        body: JSON.stringify({ exam_result: payload }),
      }),

    downloadStudentExamEvidence: (id: number, resultId: number): Promise<Blob> =>
      this.requestBlob(`/instructor/students/${id}/exam_results/${resultId}/evidence`),

    // R08-08: condutas do afilhado. Criar (origem 'mentor', vigencia default hoje) e revogar
    // com motivo; nao ha edicao nem exclusao. 403 fora da carteira; 422 com
    // `errors.body` / `errors.effective_from` / `errors.revocation_reason`.
    getStudentConducts: (id: number): Promise<ConductsResponse> =>
      this.request(`/instructor/students/${id}/conducts`),

    createStudentConduct: (id: number, payload: ConductCreatePayload): Promise<ConductResponse> =>
      this.request(`/instructor/students/${id}/conducts`, {
        method: 'POST',
        body: JSON.stringify({ conduct: payload }),
      }),

    revokeStudentConduct: (id: number, conductId: number, reason: string): Promise<ConductResponse> =>
      this.request(`/instructor/students/${id}/conducts/${conductId}/revoke`, {
        method: 'PATCH',
        body: JSON.stringify({ reason }),
      }),

    // R08-09: compromissos do afilhado. Criar (teto de abertos: 422 com `errors.base`),
    // listar com `limits`, fechar como cumprido / nao cumprido / repactuado (motivo
    // obrigatorio: 422 com `errors.close_reason`). 403 fora da carteira.
    getStudentCommitments: (id: number): Promise<InstructorCommitmentsResponse> =>
      this.request(`/instructor/students/${id}/commitments`),

    createStudentCommitment: (id: number, payload: CommitmentCreatePayload): Promise<CommitmentResponse> =>
      this.request(`/instructor/students/${id}/commitments`, {
        method: 'POST',
        body: JSON.stringify({ commitment: payload }),
      }),

    closeStudentCommitment: (
      id: number,
      commitmentId: number,
      payload: CommitmentClosePayload,
    ): Promise<CommitmentResponse> =>
      this.request(`/instructor/students/${id}/commitments/${commitmentId}/close`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    // R08-10: linha do dia do padrinho (1:1 de hoje + pendencias) e agenda das 1:1.
    // Agenda sem periodo = hoje ate hoje + 13; 400 para periodo invalido ou > 62 dias.
    getDayLine: (): Promise<{ success: boolean; day_line: DayLine }> => this.request('/instructor/day_line'),

    /** R08-11: sinais da carteira, ja ordenados por urgencia. */
    getSignals: (): Promise<{ success: boolean; signals: PortfolioSignals }> => this.request('/instructor/signals'),

    /** R08-11: o padrinho leu o comentario do aluno (resolve o sinal "comentario novo"). */
    markStudentCommitmentCommentRead: (id: number, commitmentId: number): Promise<CommitmentResponse> =>
      this.request(`/instructor/students/${id}/commitments/${commitmentId}/read_comment`, { method: 'PATCH' }),

    // Agenda de 1:1 no Google Agenda: o GET cria o feed se nao existir; o rotate troca o
    // token e o link antigo para de funcionar. Nunca cacheado pelo SW (vite.config.ts).
    getCalendarFeed: (): Promise<CalendarFeedResponse> => this.request('/instructor/calendar_feed'),

    rotateCalendarFeed: (): Promise<CalendarFeedResponse> =>
      this.request('/instructor/calendar_feed/rotate', { method: 'POST' }),

    getMeetings: (range?: { from?: string; to?: string }): Promise<AgendaResponse> => {
      const searchParams = new URLSearchParams()
      if (range?.from) searchParams.set('from', range.from)
      if (range?.to) searchParams.set('to', range.to)
      const query = searchParams.toString()
      return this.request(`/instructor/meetings${query ? `?${query}` : ''}`)
    },

    // R08-10: 1:1 do afilhado. 403 fora da carteira; 422 com `errors.scheduled_at`
    // (passado, sobreposicao) ou `errors.base` (cancelada / com presenca). Remarcar, cancelar
    // e registrar presenca so para quem agendou (404 para os outros).
    getStudentMeetings: (id: number): Promise<MeetingsResponse> => this.request(`/instructor/students/${id}/meetings`),

    createStudentMeeting: (id: number, payload: MeetingSchedulePayload): Promise<MeetingResponse> =>
      this.request(`/instructor/students/${id}/meetings`, {
        method: 'POST',
        body: JSON.stringify({ meeting: payload }),
      }),

    // MH9-10: "Iniciar reuniao" -> deep link do Transcritor (codigo de uso unico) + link da reuniao.
    // 422 fora da janela (-30 min / +1 h) ou cancelada; 404 se outro padrinho agendou.
    startStudentMeeting: (id: number, meetingId: number): Promise<import('@/types/api').MeetingStartResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/start`, { method: 'POST' }),

    rescheduleStudentMeeting: (id: number, meetingId: number, payload: MeetingSchedulePayload): Promise<MeetingResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ meeting: payload }),
      }),

    cancelStudentMeeting: (id: number, meetingId: number, reason?: string): Promise<MeetingResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/cancel`, {
        method: 'PATCH',
        body: JSON.stringify({ reason: reason ?? null }),
      }),

    // Chamado pelo modo reuniao (R08-12): o instante e o do servidor, so na janela da 1:1.
    registerMeetingAttendance: (id: number, meetingId: number): Promise<MeetingResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/attendance`, { method: 'POST' }),

    // TRANSC-08: transcricao da 1:1 (so o padrinho que agendou; D9).
    getMeetingTranscript: (id: number, meetingId: number): Promise<MeetingTranscriptResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/transcript`),

    // R08-12 — modo reuniao: ata da 1:1. Abrir registra a presenca (na janela da 1:1).
    getMeetingMinutes: (id: number, meetingId: number): Promise<MeetingMinutesResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes`),

    openMeetingMinutes: (id: number, meetingId: number): Promise<MeetingMinutesResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes`, { method: 'POST' }),

    updateMeetingMinutes: (
      id: number,
      meetingId: number,
      payload: MeetingMinutesUpdatePayload,
    ): Promise<MeetingMinutesResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes`, {
        method: 'PATCH',
        body: JSON.stringify({ minutes: payload }),
      }),

    closeMeetingMinutes: (id: number, meetingId: number): Promise<MeetingMinutesResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes/close`, { method: 'POST' }),

    // INST-09: recado para o aluno (compartilhado quando a ata fecha). Texto vazio apaga.
    updateMeetingStudentMessage: (id: number, meetingId: number, studentMessage: string): Promise<MeetingMinutesResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes/student_message`, {
        method: 'PATCH',
        body: JSON.stringify({ student_message: studentMessage }),
      }),

    createMeetingCommitment: (
      id: number,
      meetingId: number,
      payload: CommitmentCreatePayload,
    ): Promise<CommitmentResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes/commitments`, {
        method: 'POST',
        body: JSON.stringify({ commitment: payload }),
      }),

    createMeetingConduct: (id: number, meetingId: number, payload: ConductCreatePayload): Promise<ConductResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/minutes/conducts`, {
        method: 'POST',
        body: JSON.stringify({ conduct: payload }),
      }),

    // R08-15 — briefing pre-reuniao. A leitura de quem agendou marca o briefing como aberto
    // (linha do dia); a partir de 24 h antes, a leitura tambem gera a foto se ela nao existe.
    getMeetingBriefing: (id: number, meetingId: number): Promise<MeetingBriefingResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/briefing`),

    refreshMeetingBriefing: (id: number, meetingId: number): Promise<MeetingBriefingResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/briefing/refresh`, { method: 'POST' }),

    // R08-23 — Guia (IA) por secao do briefing. So quem agendou (404 para os outros).
    getMeetingGuide: (id: number, meetingId: number): Promise<MentorGuideStatusResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/guide`),

    askMeetingGuide: (id: number, meetingId: number, section: BriefingSectionKey): Promise<MentorGuideAskResponse> =>
      this.request(`/instructor/students/${id}/meetings/${meetingId}/guide`, {
        method: 'POST',
        body: JSON.stringify({ section }),
      }),

    // R08-23 — biblioteca enviavel: catalogo, enviados e envio.
    getMaterialLibrary: (id: number, query = ''): Promise<MaterialLibraryResponse> =>
      this.request(
        `/instructor/students/${id}/material_library${query ? `?q=${encodeURIComponent(query)}` : ''}`,
      ),

    getMaterialShares: (id: number): Promise<MaterialSharesResponse> =>
      this.request(`/instructor/students/${id}/material_shares`),

    shareMaterial: (id: number, payload: MaterialShareCreatePayload): Promise<MaterialShareResponse> =>
      this.request(`/instructor/students/${id}/material_shares`, { method: 'POST', body: JSON.stringify(payload) }),

    // R08-01: sem `addToMentees` — o vinculo padrinho->afilhado so nasce pelo admin
    // (`admin.createMentorship`); o endpoint do instrutor saiu da API.

    quickSearchStudents: (query: string): Promise<{
      success: boolean
      results: Array<{ id: number; title: string; subtitle: string; type: string; meta: string }>
    }> => this.request(`/instructor/students/quick_search?q=${encodeURIComponent(query)}`),

    getProfile: (): Promise<{ success: boolean; profile: InstructorProfile }> =>
      this.request('/instructor/profile'),

    updateProfile: (data: Partial<InstructorProfile>): Promise<{ success: boolean; message: string; profile: InstructorProfile }> =>
      this.request('/instructor/profile', {
        method: 'PATCH',
        body: JSON.stringify({ profile: data }),
      }),

    getPricing: (): Promise<{ success: boolean; pricing: InstructorPricing }> =>
      this.request('/instructor/pricing'),

    updatePricing: (data: UpdateInstructorPricingData): Promise<{ success: boolean; message: string; pricing: InstructorPricing }> =>
      this.request('/instructor/pricing', {
        method: 'PATCH',
        body: JSON.stringify({ pricing: data }),
      }),

    getEarnings: (): Promise<{ success: boolean } & InstructorEarningsResponse> =>
      this.request('/instructor/earnings'),

    /**
     * IA-08: assistente de IA (POC). Le o SSE e chama `onEvent` a cada evento conhecido;
     * resolve quando o stream fecha. O abort (botao Parar) rejeita com `AbortError`.
     */
    streamAssistant: async (messages: AssistantMessage[], options: AssistantStreamOptions): Promise<void> => {
      const response = await this.requestStream('/instructor/assistant', { messages }, options.signal)
      if (!response.body) return

      const parser = createSseParser(({ data }) => {
        const event = parseAssistantEvent(data)
        if (event) options.onEvent(event)
      })
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      try {
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          parser.push(decoder.decode(value, { stream: true }))
        }
        parser.push(decoder.decode())
        parser.flush()
      } finally {
        reader.releaseLock()
      }
    },
  }

  // Social / Friends / Chat endpoints
  social = {
    // Friends
    getFriends: (): Promise<{ success: boolean; friends: Friend[] }> =>
      this.request('/friends'),

    getFriendSuggestions: (limit?: number): Promise<{ success: boolean; suggestions: FriendSuggestion[] }> =>
      this.request(`/friends/suggestions${limit ? `?limit=${limit}` : ''}`),

    searchUsers: (query: string): Promise<{ success: boolean; users: StudentProfile[] }> =>
      this.request(`/friends/search?q=${encodeURIComponent(query)}`),

    // Send friend request
    sendFriendRequest: (friendId: number): Promise<{ success: boolean; message: string; request?: FriendRequest; friend?: Friend }> =>
      this.request('/friends', {
        method: 'POST',
        body: JSON.stringify({ friend_id: friendId }),
      }),

    // Get pending friend requests
    getFriendRequests: (): Promise<{ success: boolean; incoming: FriendRequest[]; outgoing: FriendRequest[] }> =>
      this.request('/friends/requests'),

    // Accept friend request
    acceptFriendRequest: (userId: number): Promise<{ success: boolean; message: string; friend: Friend }> =>
      this.request(`/friends/${userId}/accept`, { method: 'POST' }),

    // Reject friend request
    rejectFriendRequest: (userId: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/friends/${userId}/reject`, { method: 'POST' }),

    // Cancel outgoing friend request
    cancelFriendRequest: (userId: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/friends/${userId}/cancel`, { method: 'DELETE' }),

    removeFriend: (friendId: number): Promise<{ success: boolean; message: string }> =>
      this.request(`/friends/${friendId}`, { method: 'DELETE' }),

    // Conversations
    getConversations: (): Promise<{ success: boolean; conversations: Conversation[] }> =>
      this.request('/conversations'),

    getConversation: (id: number): Promise<{ success: boolean; conversation: Conversation; messages: Message[] }> =>
      this.request(`/conversations/${id}`),

    createConversation: (params: {
      participantId?: number
      participantIds?: number[]
      name?: string
      isGroup?: boolean
    }): Promise<{ success: boolean; conversation: Conversation }> =>
      this.request('/conversations', {
        method: 'POST',
        body: JSON.stringify({
          participant_id: params.participantId,
          participant_ids: params.participantIds,
          name: params.name,
          is_group: params.isGroup,
        }),
      }),

    sendMessage: (conversationId: number, content: string): Promise<{ success: boolean; message: Message }> =>
      this.request(`/conversations/${conversationId}/send_message`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }),

    markRead: (conversationId: number): Promise<{ success: boolean }> =>
      this.request(`/conversations/${conversationId}/read`, { method: 'POST' }),

    toggleFavorite: (conversationId: number): Promise<{ success: boolean; is_favorite: boolean; message: string }> =>
      this.request(`/conversations/${conversationId}/favorite`, { method: 'POST' }),
  }

  // Trilhas de residencia + especialidades de cada uma. Publico: a tela de cadastro
  // precisa da lista antes de haver sessao.
  selectionProcesses = {
    list: (): Promise<{
      success: boolean
      selection_processes: Array<{
        id: number
        name: string
        residency_degree: string
        key: string
        specialties: Array<{ id: number; code: string | null; name: string }>
        // Objetivos (r6-23): so os ativos, por nome. Lista SEPARADA das especialidades.
        career_goals: import('@/types/api').SelectionProcessCareerGoal[]
      }>
    }> => this.request('/selection_processes'),
  }

  // Prevalence Analysis endpoints (Provas na Integra - Combined)
  prevalence = {
    // Get all institutions with question counts
    getInstitutions: (): Promise<{
      success: boolean
      institutions: PrevalenceInstitution[]
      total_institutions: number
      total_questions: number
    }> => this.request('/prevalence/institutions'),

    // Get prevalence data for a specific institution
    getByInstitution: (institutionId: number): Promise<{
      success: boolean
      institution: PrevalenceInstitutionDetail
      total_questions: number
      by_study_theme: PrevalenceThemeCount[]
      by_speciality: PrevalenceSpecialityCount[]
      by_year: PrevalenceYearCount[]
      by_difficulty: PrevalenceDifficultyCount[]
    }> => this.request(`/prevalence/by_institution/${institutionId}`),

    // Get filtered questions
    getQuestions: (params: {
      institution_id: number
      study_theme?: string
      page?: number
      per_page?: number
    }): Promise<{
      success: boolean
      questions: PrevalenceQuestion[]
      total: number
      page: number
      per_page: number
      total_pages: number
    }> => {
      const searchParams = new URLSearchParams()
      searchParams.set('institution_id', String(params.institution_id))
      if (params.study_theme) searchParams.set('study_theme', params.study_theme)
      if (params.page) searchParams.set('page', params.page.toString())
      if (params.per_page) searchParams.set('per_page', params.per_page.toString())
      return this.request(`/prevalence/questions?${searchParams.toString()}`)
    },

    // Get all unique study themes
    // Os temas agora vem do catalogo (`sub_specialties`), com id — nao mais como texto
    // livre agrupado das questoes.
    getStudyThemes: (): Promise<{
      success: boolean
      study_themes: Array<{ id: number; name: string }>
      total: number
    }> => this.request('/prevalence/study_themes'),

    // Get institutions with similar exam content
    getSimilarInstitutions: (institutionId: number): Promise<{
      success: boolean
      similar: SimilarInstitution[]
      target_institution: { id: number; themes_count: number }
    }> => this.request(`/prevalence/similar_institutions?institution_id=${institutionId}`),
  }

  // Topic Revisions (FSRS v2)
  topicRevisions = {
    getAll: (): Promise<{
      success: boolean
      revisions: TopicRevision[]
      total: number
      fq_total: number
      due_count: number
      overdue_count: number
    }> => this.request('/topic_revisions'),
  }

  // R5: o aluno reportando nome errado (ou ausente) no catalogo. A fila e a mesma que o
  // registro de estudo ja alimenta automaticamente — aqui ele pede de proposito.
  catalogCorrections = {
    report: (params: {
      informed_name: string
      request_type?: 'lesson' | 'topic'
      speciality?: string | null
    }): Promise<{ success: boolean; correction: { id: number; status: string } }> =>
      this.request('/catalog_correction_requests', {
        method: 'POST',
        body: JSON.stringify({ request_type: 'lesson', ...params }),
      }),
  }

  // R10-20: o aluno pede uma prova que Provas na Integra nao tem.
  examRequests = {
    list: (): Promise<{ success: boolean; requests: import('@/types/api').ExamRequest[] }> =>
      this.request('/exam_requests'),
    create: (payload: import('@/types/api').CreateExamRequestPayload): Promise<{
      success: boolean; request: import('@/types/api').ExamRequest
    }> =>
      this.request('/exam_requests', { method: 'POST', body: JSON.stringify(payload) }),
  }

  // Aulas do cursinho do aluno que cobrem um topico (cartao de estudo da Jornada).
  courseLessons = {
    byTopic: (topicId: number): Promise<import('@/types/api').TopicCourseLessonsResponse> =>
      this.request(`/course_lessons?topic_id=${topicId}`),
  }

  // Interactive Exams (Provas na Integra)
  examAttempts = {
    getAvailableExams: (params?: {
      page?: number
      per_page?: number
      institution?: string
      year?: number
      q?: string
      selection_process?: string
      recommended?: string
    }): Promise<{
      success: boolean
      exams: import('@/types/api').AvailableExam[]
      total: number
      page: number
      per_page: number
      total_pages: number
      filters: { years: number[]; institutions: string[] }
    }> => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
      if (params?.institution) searchParams.set('institution', params.institution)
      if (params?.year) searchParams.set('year', params.year.toString())
      if (params?.q) searchParams.set('q', params.q)
      if (params?.selection_process) searchParams.set('selection_process', params.selection_process)
      if (params?.recommended) searchParams.set('recommended', params.recommended)
      const qs = searchParams.toString()
      return this.request(`/exam_attempts/available_exams${qs ? '?' + qs : ''}`)
    },

    // `exam_type` saiu do contrato: existe uma tabela de prova so, e a plataforma e
    // um atributo dela (`source`).
    startExam: (examId: number): Promise<{
      success: boolean
      attempt: import('@/types/api').ExamAttemptData
      resumed: boolean
    }> => this.request('/exam_attempts/start', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId })
    }),

    // Cria (ou retoma) a tentativa `offline` que a impressao da prova gera. Mesmo
    // payload de `startExam`; o que muda e o `mode` da tentativa criada.
    //
    // Diferenca que importa: aqui cada questao vem com `canceled`, porque a FOLHA DE
    // GABARITO precisa sinalizar a anulada (o corpo da prova, nao). `startExam` NAO
    // traz o campo, de proposito — no player o aluno nao pode descobrir a anulacao
    // antes da correcao.
    printExam: (examId: number): Promise<{
      success: boolean
      attempt: import('@/types/api').ExamAttemptData
      resumed: boolean
    }> => this.request('/exam_attempts/print', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId })
    }),

    // Tentativa em andamento OU resultado, conforme o status — sao dois serializers
    // atras da mesma rota. So o do RESULTADO traz `canceled` por questao e
    // `annulled_count` na tentativa: a anulacao e informacao pos-correcao.
    getAttempt: (id: string | number): Promise<{
      success: boolean
      attempt: import('@/types/api').ExamAttemptData
    }> => this.request(`/exam_attempts/${id}`),

    answerQuestion: (attemptId: string | number, data: {
      question_index: number
      selected_answer: string | null
      time_spent_seconds?: number
    }): Promise<{
      success: boolean
      answer: { question_index: number; selected_answer: string; is_correct: boolean; correct_answer: string }
      progress: { answered_count: number; total_questions: number; percentage: number }
    }> => this.request(`/exam_attempts/${attemptId}/answer`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

    flagQuestion: (attemptId: string | number, questionIndex: number): Promise<{
      success: boolean
      flagged: boolean
      question_index: number
    }> => this.request(`/exam_attempts/${attemptId}/flag`, {
      method: 'PATCH',
      body: JSON.stringify({ question_index: questionIndex })
    }),

    // Fecha a prova online e devolve ja o RESULTADO: com `canceled` por questao,
    // `annulled_count` na tentativa e `score` calculado sobre as questoes validas
    // (`total_questions - annulled_count`). `total_questions` continua sendo o total
    // impresso e `correct_count` ja exclui as anuladas.
    completeExam: (attemptId: string | number, timeSpent?: number): Promise<{
      success: boolean
      attempt: import('@/types/api').ExamAttemptData
    }> => this.request(`/exam_attempts/${attemptId}/complete`, {
      method: 'PATCH',
      body: JSON.stringify({ time_spent_seconds: timeSpent })
    }),

    abandonExam: (attemptId: string | number): Promise<{
      success: boolean
      message: string
    }> => this.request(`/exam_attempts/${attemptId}/abandon`, {
      method: 'PATCH'
    }),

    // Fecha uma tentativa `offline`: grava o gabarito preenchido a mao e finaliza a
    // prova (mesmo resultado de `completeExam`, com nota — e com a mesma regra de
    // anuladas fora do denominador). `pause_count` (r7-09) so vai quando o cronometro
    // do gabarito foi usado — ausente, o backend grava nulo.
    registerAnswers: (
      attemptId: string | number,
      data: import('@/types/api').RegisterAnswersPayload
    ): Promise<{
      success: boolean
      attempt: import('@/types/api').ExamAttemptData
    }> => this.request(`/exam_attempts/${attemptId}/register_answers`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

    // Ultimo passo da prova impressa, depois de `registerAnswers`: o backend cria um
    // simulado do MedHunter Manual a partir da tentativa. O tempo da conferencia e
    // opcional desde a r7-11 (ausente = nulo); chamado da pagina de respostas. Nao devolve `attempt`, e sim o simulado criado — por isso o
    // chamador precisa invalidar tambem as telas de Simulados.
    registerStudy: (
      attemptId: string | number,
      data: import('@/types/api').RegisterStudyPayload
    ): Promise<{
      success: boolean
      study: import('@/types/api').ExamStudyRegistration
    }> => this.request(`/exam_attempts/${attemptId}/register_study`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),

    // "Cancelar Prova": destroi a tentativa `offline` ainda nao registrada, para o
    // card voltar ao estado inicial (Iniciar/Imprimir) sem residuo de tentativa.
    cancelExam: (attemptId: string | number): Promise<{
      success: boolean
      message: string
    }> => this.request(`/exam_attempts/${attemptId}`, {
      method: 'DELETE'
    }),

    // `ExamAttemptSummary` e uma duplicata manual de `ExamAttemptData`, nao uma
    // extensao: `annulled_count` precisa vir aqui tambem, senao a tabela de historico
    // divide o aproveitamento por um denominador diferente do da tela de resultado —
    // e nada no build acusa.
    getHistory: (): Promise<{
      success: boolean
      attempts: import('@/types/api').ExamAttemptSummary[]
    }> => this.request('/exam_attempts/history'),
  }
}

// Social types
// r7-12 (LGPD): o que um aluno ve de outro — espelho de `serialize_public_profile`
// (friends_controller.rb). Sem e-mail, telefone ou qualquer contato: o contato e pelo chat.
export interface StudentProfile {
  id: number
  name: string
  avatar_initial: string
  is_instructor: boolean
  specialty?: string | null
  preparation_course?: string | null
  institution?: string | null
}

// Amigo aceito: perfil publico + presenca (`serialize_friend`). Busca, sugestao e pedido
// nao trazem presenca.
export interface Friend extends StudentProfile {
  last_seen?: string | null
  is_online: boolean
  // MH13-33: padrinho/afilhado (so em GET /friends). Mentoria so o admin desfaz.
  is_mentorship?: boolean
}

export interface FriendSuggestion {
  user: StudentProfile
  score: number
  reasons: string[]
}

export interface FriendRequest {
  id: number
  user: StudentProfile
  direction: 'incoming' | 'outgoing'
  created_at: string
}

export interface Conversation {
  id: number
  name: string
  is_group: boolean
  participants: Array<{
    id: number
    name: string
    avatar_initial: string
    is_online: boolean
  }>
  last_message?: string
  last_message_at?: string
  unread_count: number
  is_favorite: boolean
  created_at: string
}

export interface Message {
  id: number
  content: string
  user_id: number
  user_name: string
  avatar_initial: string
  is_own: boolean
  created_at: string
  is_filtered: boolean
  conversation_id?: number
}

// Admin types
export interface AdminStats {
  users: { total: number; students: number; instructors: number; admins: number; new_this_month: number; new_this_week: number }
  subscriptions: { active: number; trial: number; expired: number; total_revenue_month: number }
  content: { quizzes: number; questions: number; courses: number; video_aulas: number }
  activity: { active_today: number; active_this_week: number }
  coupons?: { total: number; active: number; total_used: number }
  mentorships?: { total: number; active_instructors: number }
}

export interface AdminUserBrief {
  id: number
  name: string
  email: string
  role: string
  created_at: string
  last_sign_in_at: string | null
  has_subscription: boolean
  in_trial: boolean
}

export type AdminRoleFilter = 'admin' | 'instructor' | 'student'

export interface AdminUser extends AdminUserBrief {
  telefone: string
  role_id: number
  trial_ends_at: string | null
  trial_days_remaining: number
  // Afilhados vinculados (mentoria nao recusada); null para quem nao e instrutor.
  mentees_count: number | null
  // R10-15: perfil de acesso do admin; null = acesso total. Opcional ate o deploy da API.
  admin_profile_id?: number | null
}

export interface AdminUserDetail extends AdminUser {
  graduation_institution: string | null
  graduation_year: string | null
  desired_speciality: string | null
  selection_process: string | null
  sub_desired_speciality: string | null
  preparation_course: string | null
  // plan_a_institution: string | null // hidden: replaced by priority institutions
  // plan_b_instituion: string | null  // hidden
  priority_institution_1: string | null
  priority_institution_2: string | null
  priority_institution_3: string | null
  priority_institution_4: string | null
  priority_institution_5: string | null
  /** MH14-01: lista completa com o grau de importancia (1..5); ausente na API antiga. MH14-02: fixada e TOP 5. */
  priority_institutions?: import('@/types/user').PriorityInstitutionEntry[]
  sign_in_count: number
  current_sign_in_at: string | null
  confirmed_at: string | null
  /**
   * A assinatura vigente. Formato mais enxuto que `AdminSubscription`: o
   * serializer de `Api::V1::Admin::UsersController#show` monta o hash a mao e nao
   * reaproveita o da listagem — ele nao repete os dados do usuario.
   */
  subscription: AdminUserSubscription | null
  /** As 5 assinaturas mais recentes. */
  subscriptions: AdminUserSubscription[]
  /**
   * Spec 09 R6: as duas fontes mescladas. `source: 'asaas'` e dinheiro que passou
   * pelo gateway; `source: 'manual'` e concessao combinada por fora.
   */
  payments: Array<{
    id: number
    source: 'asaas' | 'manual'
    value: number
    status: string
    payment_method: ManualPaymentMethod | null
    granted_by: AdminGrantedBy | null
    notes: string | null
    date: string | null
  }>
  stats: { primary_studies_count: number; revisions_count: number; quiz_attempts_count: number; events_count: number; medway_trails_count: number }
}

/**
 * Spec 09 — formas de pagamento da concessao manual. Espelha
 * `Subscription::MANUAL_PAYMENT_METHODS` no backend; `cortesia` existe porque
 * concessao de valor 0,00 sem forma explicita vira buraco no relatorio.
 */
export type ManualPaymentMethod = 'pix' | 'boleto' | 'cartao' | 'transferencia' | 'cortesia' | 'outro'

/**
 * `scheduled` e a concessao com data de inicio futura: nao vale hoje, mas tambem
 * nao expirou. Mostrar "expirada" ali seria mentira.
 */
export type AdminSubscriptionStatus = 'active' | 'scheduled' | 'expired'

export interface AdminGrantedBy {
  id: number
  name: string
  email: string
}

/** Campos da concessao manual, nulos em toda assinatura vinda do gateway. */
export interface AdminManualGrantFields {
  is_manual: boolean
  amount_paid: number | null
  payment_method: ManualPaymentMethod | null
  notes: string | null
  granted_by: AdminGrantedBy | null
}

/** Assinatura como aparece no detalhe do usuario (sem repetir os dados dele). */
export interface AdminUserSubscription extends AdminManualGrantFields {
  id: number
  plan_name: string | null
  plan_price: number | null
  status: AdminSubscriptionStatus
  starts_at: string | null
  expires_at: string | null
  created_at: string
}

export interface AdminSubscriptionPlan {
  id: number
  name: string
  price: number
  interval: string
  stripe_price_id: string
}

/** Bloco `grant` de `createUser`. */
export interface AdminGrantPayload {
  plan_id: number
  amount_paid?: string | number | null
  payment_method?: ManualPaymentMethod | null
  notes?: string | null
  /** ISO 8601. Ausente = agora. */
  starts_at?: string | null
  /** ISO 8601. Ausente = inicio + `duration_days` (30 por padrao). */
  ends_at?: string | null
  duration_days?: number
}

export interface AdminCreateUserPayload {
  first_name: string
  last_name?: string
  email: string
  telefone?: string
  /** Lista fechada no servidor. `admin` e recusado. */
  role?: 'student' | 'instructor'
  send_welcome_email?: boolean
}

/**
 * R10-16 — de onde sai o numero do painel: parcelas pagas no Asaas, valor bruto
 * (o que o aluno pagou, antes das taxas), regime de caixa. Stripe (legado) nao
 * guarda valor na plataforma: so as assinaturas ativas sao contadas.
 */
export interface AdminRevenueSource {
  gateway: 'asaas'
  basis: 'gross_cash'
  stripe_legacy_active_subscriptions: number
}

export interface AdminRevenuePayment {
  id: number
  source: 'asaas' | 'manual'
  user_name: string
  user_email: string
  plan_name: string | null
  payment_method: ManualPaymentMethod | null
  /** Parcela paga dentro do parcelamento (1 de 12). Nulo na concessao manual. */
  installment_number: number | null
  installments_count: number | null
  /** Valor bruto da parcela. */
  value: number
  /** Data do pagamento no Asaas (`paid_at`). */
  payment_date: string
}

export interface AdminSubscription extends AdminManualGrantFields {
  id: number
  user_id: number
  user_name: string
  user_email: string
  plan_id: number
  plan_name: string
  plan_price: number
  status: AdminSubscriptionStatus
  starts_at: string | null
  expires_at: string
  created_at: string
}

export interface AdminSubscriptionDetail extends AdminSubscription {
  user: { id: number; name: string; email: string; telefone: string; created_at: string }
  plan: { id: number; name: string; price: number; interval: string }
  payment_history: Array<{ id: number; value: number; status: string; payment_date: string; due_date: string }>
}

export interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

export interface AdminMentorship {
  id: number
  instructor: { id: number; name: string; email: string }
  student: { id: number; name: string; email: string }
  created_at: string
}

export interface AdminQuiz {
  id: number
  title: string
  quiz_type?: string
  quiz_type_name?: string | null
  category_id?: number | null
  category_name?: string | null
  questions_count: number
  created_by?: string
  created_at: string
  updated_at: string
}

export interface AdminQuestion {
  id: number
  title?: string
  text: string
  question_type?: string
  points?: number
  explanation?: string
  position?: number
  answers: Array<{ id: number; text: string; correct: boolean }>
}

/** Valores de Quiz::QUIZ_TYPE com o rotulo de Quiz#quiz_type_name (MH13-19b). */
export interface AdminQuizTypeOption {
  value: string
  label: string
}

export interface AdminQuizCategoryOption {
  id: number
  name: string
}

export interface AdminQuizDetail extends AdminQuiz {
  questions: AdminQuestion[]
}

export interface AdminCourse {
  id: number
  title: string
  description?: string
  course_category?: string
  course_level?: string
  status: string
  price?: number
  created_by?: string
  created_at: string
  updated_at: string
}

export interface AdminCourseDetail extends AdminCourse {
  video?: string
  requirements?: string
  reviews_count: number
}

// r7-20 — cupons e produtos do checkout. O ESTADO do cupom (`checkout_status`) e o
// link (`checkout_path`) vem do servidor, da mesma regra que o checkout aplica
// (`Coupon#checkout_rejection`); a tela nao calcula desconto nem validade.
export type AdminCouponType = 'general' | 'unique' | 'free_pass'
export type AdminCouponDiscountType = 'percentage' | 'fixed_amount' | 'free'
/** `null` = aceito pelo checkout (ainda sujeito a produto, e-mail e minimo). */
export type AdminCouponCheckoutStatus = 'coupon_invalid' | 'coupon_expired' | 'coupon_exhausted' | null

export interface AdminCoupon {
  id: number
  code: string
  name?: string
  description?: string
  coupon_type?: AdminCouponType
  discount_type?: AdminCouponDiscountType
  /** Decimal do Rails: chega como string ("10.0"). */
  discount_value?: number | string
  minimum_amount?: number | string
  usage_limit?: number | null
  used_count: number
  starts_at?: string | null
  expires_at?: string | null
  active: boolean
  one_time_use?: boolean
  assigned_to_email?: string | null
  /** `null` = cupom geral (todos os produtos). */
  asaas_plan_id?: number | null
  product?: { id: number; name: string; active: boolean } | null
  is_expired: boolean
  is_exhausted: boolean
  checkout_status?: AdminCouponCheckoutStatus
  /** `/checkout?produto=&cupom=` (sem origem; a SPA prefixa a propria). */
  checkout_path?: string
  created_at: string
}

export interface AdminCouponUsage {
  id: number
  user_id: number
  user_name: string | null
  user_email: string | null
  discount_amount: number
  free_pass: boolean
  order_id: number | null
  order_status: string | null
  created_at: string
}

export interface AdminCouponDetail extends AdminCoupon {
  assigned_to_user: string | null
  usages: AdminCouponUsage[]
}

/** Corpo de criar/editar cupom. `asaas_plan_id: null` volta o cupom a geral. */
export interface AdminCouponPayload {
  code?: string
  name?: string
  description?: string
  coupon_type?: AdminCouponType
  discount_type?: AdminCouponDiscountType
  discount_value?: number
  minimum_amount?: number
  usage_limit?: number | null
  starts_at?: string | null
  expires_at?: string | null
  active?: boolean
  one_time_use?: boolean
  assigned_to_email?: string | null
  asaas_plan_id?: number | null
}

/**
 * Plano do checkout = `AsaasPlan`; o slug e o `/checkout/<slug>`. Na tela se chama "Plano"
 * desde a r7-25; o tipo manteve o nome do endpoint (`/admin/products`).
 */
export interface AdminProduct {
  id: number
  /** r7-25: unico; o checkout e `/checkout/<slug>`. */
  slug: string
  name: string
  description: string | null
  duration_description: string
  price: number
  max_installments: number
  interval: import('@/types/api').PlanInterval
  duration_years: number
  /** MH14-12: 'YYYY-MM-DD' em que o acesso termina com `interval` `fixed_date`; null nos outros. */
  fixed_end_date: string | null
  active: boolean
  /** R10-07: preco zero. */
  free: boolean
  /** R10-08: plano pago oferecido no fim do acesso. */
  upgrade_plan_id: number | null
  /** R12-02: contrato que o comprador assina pelo Autentique (null = sem contrato). */
  contract_template_id: number | null
  contract_template_name: string | null
  coupons_count: number
  paid_orders_count: number
  checkout_path: string
  /** r7-25: features que o plano libera (chaves do catalogo). */
  features: import('@/types/api').PlanFeatureKey[]
  /** O que o menu faz com a feature fora do plano: some ou aparece com cadeado. */
  locked_features_display: import('@/types/api').LockedFeaturesDisplay
  created_at: string
}

export interface AdminProductPayload {
  name?: string
  slug?: string
  description?: string
  price?: number
  interval?: import('@/types/api').PlanInterval
  duration_years?: number
  /** MH14-12: 'YYYY-MM-DD' ou null (obrigatorio com `fixed_date`). */
  fixed_end_date?: string | null
  max_installments?: number
  active?: boolean
  upgrade_plan_id?: number | null
  contract_template_id?: number | null
  features?: import('@/types/api').PlanFeatureKey[]
  locked_features_display?: import('@/types/api').LockedFeaturesDisplay
}

export interface AdminAnnouncement {
  id: number
  title: string
  content: string
  announcement_type: 'info' | 'warning' | 'success' | 'error'
  target_audience: 'all' | 'students' | 'instructors' | 'admins'
  active: boolean
  dismissible: boolean
  starts_at?: string
  expires_at?: string
  is_visible: boolean
  is_expired: boolean
  views_count: number
  dismissals_count: number
  created_by?: string
  created_at: string
}

// Instructor types
export interface InstructorStudent {
  id: number
  name: string
  email: string
  last_sign_in_at: string | null
  is_active: boolean
  is_active_7d: boolean
  created_at: string
  /** R08-02: data-alvo + instituicao-alvo. */
  main_exam: MainExam
}

export interface InstructorStudentDetail extends InstructorStudent {
  telefone?: string
  graduation_institution?: string
  desired_speciality?: string
  selection_process?: string
  sub_desired_speciality?: string
  preparation_course?: string
  /** INST-03: resumo do formulario de entrada; `null` = nao preenchido. */
  onboarding?: import('@/types/api').InstructorStudentOnboarding | null
  stats?: {
    primary_studies_count: number
    revisions_count: number
    test_simulations_count: number
    goals_count: number
  }
  recent_activity?: {
    last_primary_study?: string
    last_revision?: string
    last_test?: string
  }
}

export interface StudentAnalytics {
  primary_study: { performance: Record<string, number>; weekly_volume: Record<string, number> }
  revision: { performance: Record<string, number>; weekly_volume: Record<string, number> }
  test_simulation: { ir: Record<string, number>; performance: Record<string, number> }
  ted_vs_goal: { realized: Record<string, number>; goal: Record<string, number> }
  specialties?: Array<{ specialty: string; total_questions: number; accuracy: number }>
}

export interface InstructorProfile {
  id: number
  name: string
  specialty: string
  specialties: string[]
  about: string
  instagram: string | null
  years_of_experience: number
  residency_hospital: string | null
  medical_school: string | null
  foto_url: string | null
  is_essential_mentor: boolean
  approval_history: string[]
  preparatory_courses_taken: string[]
  available_specialties: string[]
  available_institutions: string[]
  created_at: string
  updated_at: string
}

export interface InstructorPricingPlan {
  id: number
  title: string
  description: string
  price: number
  active: boolean
  created_at: string
}

/**
 * Plano no corpo do PATCH. Difere da leitura em dois pontos que o backend impoe
 * (Api::V1::Instructor::PricingsController#update_plans): `id` ausente significa criar,
 * e `created_at` nunca e aceito na escrita.
 */
export type InstructorPricingPlanInput = Omit<InstructorPricingPlan, 'id' | 'created_at'> & {
  id?: number
}

export interface InstructorPricing {
  base_session_price: number
  base_session_title: string
  plans: InstructorPricingPlan[]
  total_plans: number
  active_plans: number
}

export interface UpdateInstructorPricingData {
  base_session_price?: number
  base_session_title?: string
  plans?: InstructorPricingPlanInput[]
}

export interface InstructorEarnings {
  total_earnings: number
  this_month: number
  last_month: number
  pending: number
  average_per_student: number
  currency: string
}

export interface InstructorEarningsResponse {
  earnings: InstructorEarnings
  monthly_breakdown: Array<{ month: string; month_name: string; amount: number; students_count: number }>
  students_metrics: { total_students: number; active_students: number; new_this_month: number; retention_rate: number }
}

// Prevalence types
export interface PrevalenceInstitution {
  /** Id da instituicao no catalogo. Era um id sintetico "<plataforma>_<slug-do-nome>"
   *  enquanto a prevalencia agrupava pela string `institution_name` das questoes. */
  id: number
  external_id: string | null
  name: string
  abbreviation: string | null
  state: string | null
  city: string | null
  display_name: string
  location: string | null
  thumbnail_url: string | null
  question_count: number
  exam_count: number | null
  /** Uma instituicao pode ter prova das duas origens; virou lista. */
  platforms: Array<'medway' | 'medcof' | 'manual'>
}

export interface PrevalenceInstitutionDetail {
  id: number
  name: string
  display_name: string
  location: string | null
  thumbnail_url: string | null
  platforms: Array<'medway' | 'medcof' | 'manual'>
}

export interface PrevalenceThemeCount {
  study_theme: string
  /** Id em `sub_specialties`: o tema virou FK, entao a contagem sai agrupada por id. */
  id: number
  count: number
}

export interface PrevalenceSpecialityCount {
  /** Nome da especialidade dona da subespecialidade. Nao ha `id` porque a contagem e
   *  agregada por nome — havia dois catalogos de especialidade que divergiam no texto
   *  ("Cirurgia Geral A" x "Cirurgia Geral") e a agregacao por nome ficou. */
  speciality: string
  count: number
}

export interface PrevalenceYearCount {
  year: string | number
  count: number
}

export interface PrevalenceDifficultyCount {
  difficulty: string
  count: number
}

export interface PrevalenceQuestionOption {
  id?: number
  letter?: string
  content?: string
  text?: string
  response_percentage?: number
  is_correct?: boolean
  /** R11-01: figura da alternativa. */
  images?: unknown[]
}

export interface PrevalenceQuestion {
  id: number
  question_id: string | number
  question_number: number | null
  statement: string | null
  options: PrevalenceQuestionOption[] | null
  /** `[{ url, order }]`; string crua em dado antigo. */
  images: unknown[] | null
  study_theme: string | null
  study_topic: string | null
  speciality: string | null
  difficulty: string | null
  difficulty_label: string | null
  year: string | number | null
  institution_name: string | null
  institution_state: string | null
  residency_degree: string | null
  response_stats: Record<string, unknown> | null
  canceled: boolean
  is_outdated: boolean
  correct_answer?: string
  comment?: string
  success_rate?: number
  platform: 'medway' | 'medcof'
}

// Study suggestion from institution prevalence analysis
export interface StudySuggestion {
  institution: string
  theme: string
  speciality: string | null
  prevalence_percentage: number
  prevalence_count: number
  /**
   * RESOLUCAO — questoes resolvidas sobre as que a banca cobra no tema (definicao B).
   * Chamava-se `user_coverage` ate o spec 04, e era a UNICA renderizacao da definicao B
   * com a palavra "Cobertura" na SPA. Nao confundir com a cobertura de
   * /app/analise-progresso, que conta REGISTRO de estudo.
   */
  user_resolution: number
  user_accuracy: number
  user_readiness: number
  is_studied: boolean
  is_revised: boolean
  priority_score: number
  reason: string
}

// Similar institution from prevalence comparison
export interface SimilarInstitution {
  id: number
  name: string
  location: string | null
  question_count: number
  similarity_percentage: number
  shared_themes_count: number
  total_themes: number
  shared_themes: string[]
}

export const api = new ApiClient()
export { ApiError }
